import { normalizeFamilies, normalizeLayers, LocalizationRequestError, LocalizationCapabilityError, labelId } from '@canvas-logic/clp-localization';
export { CUSTOM_KEY_MAX_LENGTH, CUSTOM_PREFIX, CatalogMergeError, EXCEL_CSV_CONTENT_TYPE, LocalizationCapabilityError, LocalizationRequestError, buildCustomTranslationCsvs, buildSlotDisplayContext, cascadeKeysForEntity, cascadeKeysForUi, containsHtml, createCatalogTranslator, createTranslator, customLabelId, defineMessages, encodeExcelCsv, extractPlaceholders, interpolate, labelId, layersFromProjectTranslations, mergeCatalogs, normalizeFamilies, normalizeLayers, placeholdersMatch, resolve, resolveLabel, resolveLayered, validateCatalog, validateCustomKey, validatePlaceholderName } from '@canvas-logic/clp-localization';
import { getAllAcceptedSlotItems, ConfiguratorPV, projectPrice, VariantProperties, getAttributeValue, createAttributeValue, Mat4, centerAABB, transformAABBByMat4, addVec, axisVec, boundaryToAABB, sizeAlong, emptyAABB, unionAABB, isEmpty, resolveSpaceOrigin, resolveBoundary, containsAABB, resolvePartLayout, getSlotSpace, isConditionalAcceptedItems, resolveAcceptedSlotItems, priceKey, zeroPrice, computePartsAmount, toCents, mulPriceByScalar, addPrice, BomList, fromCents, BomCalculator, isSkippingMetaId, hasAttributeValue, CUSTOM_ATTRIBUTE_VALUE } from '@canvas-logic/clp-domain';
import { LogicEngineBuilder, ConfiguratorToDomainProductMapper } from '@canvas-logic/clp-bridges';

class InvalidConfiguration extends Error {
    invalidRules;
    emptyRequiredComponents;
    constructor(invalidRules, emptyRequiredComponents) {
        super('Invalid configuration');
        this.invalidRules = invalidRules;
        this.emptyRequiredComponents = emptyRequiredComponents;
    }
}
/**
 * Thrown by `getPrice({ priceType })` when the requested type matches none of
 * the catalog's price types by ordinal, wire key or stable key.
 *
 * Throwing is deliberate (ADR-0079): returning an empty price map would make a
 * typo indistinguishable from a catalog that genuinely carries no prices.
 */
class PriceTypeNotFoundError extends Error {
    requested;
    available;
    constructor(requested, available) {
        super(`Unknown price type "${requested}". Available: ` +
            available.map((type) => `${type.ordinal} | ${type.wireKey} | ${type.key}`).join(', '));
        this.requested = requested;
        this.available = available;
    }
}

const uiFallBack = {
    localization: 'en',
    layout: {
        controlsHidden: false,
    },
};
const view3dFallBack = {
    views: [],
    hierarchy: {
        root: {
            partId: 'empty',
            children: [],
        },
    },
    camera: {},
    parts: [
        {
            id: 'empty',
            view: {
                selector: 'view',
                match: {
                    '*': '*',
                },
            },
            materials: [],
        },
    ],
    materials: [],
};
const queryParams = ['version', 'catalogId', 'link'];
const initialVariantQueryParams = queryParams.concat(['configurationId']);
class Fetcher {
    url;
    constructor(url) {
        this.url = this.formatUrl(url);
    }
    async getBomDetails(params) {
        const bomParams = { ...params };
        bomParams.catalogId = undefined;
        const response = await fetch(`${this.url}/products/${params.productId}/composite-bom-data/${params.catalogId}?${this.getSearchParams(bomParams)}`);
        return response.json();
    }
    async getShortLink(params, draft = false) {
        const response = await fetch(`${this.url}/short-link?version=${draft ? 'draft' : 'active'}`, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: {
                'content-type': 'application/json',
            },
        });
        return response.text();
    }
    async getProductSchema(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/schema?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getDefaultProductVariant(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/default-product-variant?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getProductVariants(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/product-variants?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeProductSchema(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-schema?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeProductVariants(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-product-variants?${this.getSearchParams(params)}`);
        const data = await response.json();
        // sort by id to keep the same order
        // TODO: Remove when implement explicit order for attribute values
        for (const [, products] of Object.entries(data.components)) {
            for (const value of products) {
                (value?.variants ?? []).sort((a, b) => {
                    return a.variantId - b.variantId;
                });
            }
        }
        return data;
    }
    async getCompositeProductPreset(params, presetId) {
        // Backend route — `products/:productId/composite-presets/:presetId`.
        // `getSearchParams` carries the catalog/version scoping (catalog binding
        // resolves inherited / catalog-level presets server-side via the same
        // presetId).
        const response = await fetch(`${this.url}/products/${params.productId}/composite-presets/${encodeURIComponent(presetId)}?${this.getSearchParams(params)}`);
        if (!response.ok) {
            const errorBody = await response.json().catch(() => null);
            throw new Error(errorBody?.message || `Failed to load composite preset "${presetId}"`);
        }
        return response.json();
    }
    async getInitialCompositeProductVariant(params, initialVariant) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-initial-variant?${this.getInitialVariantSearchParams(params)}${initialVariant ? `&initialVariant=${JSON.stringify(initialVariant)}` : ''}`);
        if (!response.ok) {
            const errorBody = await response.json().catch(() => null);
            throw new Error(errorBody?.message || 'Failed to load composite product variant');
        }
        return response.json();
    }
    formatUrl(url) {
        return url.endsWith('/') ? url + 'configurator' : url + '/configurator';
    }
    async getCompositeData(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-data?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeUiSpecification(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-ui-specification?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeView3dSpecification(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-view3d-specification?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getProductUiSpecification(_params) {
        return uiFallBack;
    }
    async getProductView3dSpecification(_params) {
        return view3dFallBack;
    }
    async getContactUsFeatureSchema(params) {
        const response = await fetch(`${this.url}/contact-us?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getPricingFeatureSchema(params) {
        const response = await fetch(`${this.url}/pricing?${this.getSearchParams(params)}`);
        return response.json();
    }
    async requestQuote(params, formData, routingKey) {
        const search = new URLSearchParams(this.getSearchParams(params));
        if (routingKey) {
            search.set('routing_key', routingKey);
        }
        const response = await fetch(`${this.url}/request-quote?${search.toString()}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        });
        if (!response.ok) {
            const parsed = await response.json();
            throw new Error(parsed.message);
        }
        return response.json();
    }
    async getHqrConfig(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/hqr-config?${this.getSearchParams(params)}`);
        return response.json();
    }
    translationsPath(params, caller) {
        // Scope is carried in the PATH here (projects/:id or catalogs/:id), not via
        // getSearchParams like the other endpoints. Endpoint choice mirrors the
        // basic configurator's useProjectTranslationsQuery:
        //   useLive = draft || no catalog context.
        const catalogId = params.catalogId;
        const useLive = params.version === 'draft' || catalogId == null;
        if (useLive) {
            if (params.projectId == null) {
                throw new Error(`${caller}: projectId is required for live (draft / no-catalog) translations`);
            }
            return `projects/${params.projectId}/products/${params.productId}/translations`;
        }
        return `catalogs/${catalogId}/products/${params.productId}/translations`;
    }
    async getProductTranslations(params, lang) {
        const path = this.translationsPath(params, 'getProductTranslations');
        const search = new URLSearchParams();
        if (lang)
            search.set('lang', lang);
        const query = search.toString();
        const response = await fetch(`${this.url}/${path}${query ? `?${query}` : ''}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch product translations: ${response.status}`);
        }
        return response.json();
    }
    async getProductTranslationsV2(params, options = {}) {
        const path = this.translationsPath(params, 'getProductTranslationsV2');
        // Normalize BEFORE the wire (decision 5): the platform rejects
        // non-canonical token lists explicitly rather than ignoring them.
        const search = new URLSearchParams();
        if (options.lang)
            search.set('lang', options.lang);
        search.set('schema', '2');
        if (options.include)
            search.set('include', normalizeFamilies(options.include).join(','));
        if (options.layers)
            search.set('layers', normalizeLayers(options.layers).join(','));
        const response = await fetch(`${this.url}/${path}?${search.toString()}`);
        if (!response.ok) {
            const body = await response.json().catch(() => null);
            throw new LocalizationRequestError(body?.message || `Failed to fetch layered translations: ${response.status}`, response.status, typeof body?.appErrorCode === 'string' ? body.appErrorCode : undefined);
        }
        const dto = (await response.json());
        // Decision 9: confirmation comes from the response, never from the request
        // having been sent — an older platform ignores unknown query params and
        // answers with the v1 shape.
        if (dto?.schemaVersion !== 2) {
            throw new LocalizationCapabilityError('Platform did not confirm the schema=2 translations capability; use getProductTranslations (v1) instead.');
        }
        return dto;
    }
    getInitialVariantSearchParams(params) {
        return this.getSearchParamsByKeys(initialVariantQueryParams, params);
    }
    getSearchParams(params) {
        return this.getSearchParamsByKeys(queryParams, params);
    }
    getSearchParamsByKeys(keys, params) {
        const searchParams = new URLSearchParams();
        for (const key of keys) {
            const value = params[key];
            if (value) {
                searchParams.set(key, String(value));
            }
        }
        return searchParams.toString();
    }
}

class JsonFetcher {
    url;
    constructor(url) {
        this.url = this.formatUrl(url);
    }
    getBomDetails(_params) {
        throw new Error('Method not implemented.');
    }
    async getShortLink(_params) {
        return '';
    }
    async getContactUsFeatureSchema(_params) {
        throw Error('getContactUsFeatureSchema not implemented for JsonFetcher');
    }
    async getPricingFeatureSchema(_params) {
        throw Error('getContactUsFeatureSchema not implemented for JsonFetcher');
    }
    async requestQuote(_params, _formData) {
        throw Error('requestQuote not implemented for JsonFetcher');
    }
    async getProductSchema(params) {
        return this.fetch(`${this.url}/products/${params.productId}/schema.json`);
    }
    async getDefaultProductVariant(params) {
        return this.fetch(`${this.url}/products/${params.productId}/default_variant.json`);
    }
    async getProductVariants(params) {
        return this.fetch(`${this.url}/products/${params.productId}/variants.json`);
    }
    async getCompositeProductSchema(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/schema.json`);
    }
    async getInitialCompositeProductVariant(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/default_variant.json`);
    }
    async getCompositeProductPreset(params, presetId) {
        // JSON-file fetcher convention: one file per preset, looked up by id.
        return this.fetch(`${this.url}/composites/${params.productId}/presets/${encodeURIComponent(presetId)}.json`);
    }
    async getCompositeProductVariants(params) {
        const compositeVariants = await this.fetch(`${this.url}/composites/${params.productId}/variants.json`);
        const result = {
            rules: compositeVariants.rules ?? [],
            components: {},
        };
        const requests = [];
        for (const [componentKey, componentVariants] of Object.entries(compositeVariants.components)) {
            result.components[componentKey] = [];
            for (const productId of componentVariants) {
                if (productId === null) {
                    result.components[componentKey].push(null);
                }
                else {
                    const promise = this.fetch(`${this.url}/products/${productId}/variants.json`).then((productVariants) => {
                        for (const productVariant of productVariants) {
                            result.components[componentKey].push(productVariant);
                        }
                    });
                    requests.push(promise);
                }
            }
        }
        await Promise.all(requests);
        return result;
    }
    async getProductUiSpecification(params) {
        return this.fetch(`${this.url}/products/${params.productId}/ui.json`);
    }
    async getCompositeUiSpecification(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/ui.json`);
    }
    getProductView3dSpecification(params) {
        return this.fetch(`${this.url}/products/${params.productId}/view3d.json`);
    }
    getCompositeView3dSpecification(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/view3d.json`);
    }
    async getCompositeData(_params) {
        throw new Error('Method not implemented.');
    }
    async getHqrConfig(params) {
        try {
            return await this.fetch(`${this.url}/composites/${params.productId}/hqr-config.json`);
        }
        catch {
            // Return disabled HQR config if file doesn't exist
            return { enabled: false, defaultImage: null, config: [] };
        }
    }
    async getProductTranslations(_params, _lang) {
        // Static-JSON mode ships no translations; return the backend's own
        // "none available" shape so consumers degrade to system values.
        return { language: null, translations: {} };
    }
    async getProductTranslationsV2(_params) {
        // Static-JSON mode ships no layered translations either.
        return {
            schemaVersion: 2,
            language: null,
            defaultLanguage: null,
            families: ['custom', 'entities', 'ui'],
            layers: {},
        };
    }
    async fetch(url) {
        const response = await fetch(url);
        return response.json();
    }
    formatUrl(url) {
        return url.endsWith('/') ? url + 'data' : url + '/data';
    }
}

/**
 * Wrap a {@link FetcherApi} so identical **read** requests resolve once per
 * session. Product-definition reads (schema, variants, view3d, data, preset,
 * ui, bom, feature schemas) are immutable for a given `(productId, version)`,
 * so the in-flight/settled promise is cached by `method + JSON(args)`.
 *
 * This deduplicates the overlapping fetches the SDK otherwise makes for the same
 * product: the composite-api builders, each placed-item api build, and the
 * bounds catalog's `warmFor` all hit the same wrapped fetcher, so e.g. a box's
 * `getCompositeProductVariants` / `getCompositeProductSchema` are fetched once
 * total instead of once per consumer.
 *
 * Mutating / non-idempotent methods (`getShortLink`, `requestQuote`) pass
 * through uncached. Live translation reads (draft version or no catalog context)
 * are exempt from the cache since they change mid-session on CSV import or
 * custom-key edits. Failed reads are evicted so a later call can retry.
 *
 * Scope: the cache lives for the wrapper's lifetime (one per `ClpSdk` instance =
 * one session). Entries are never invalidated — safe because the cached data is
 * versioned and immutable within a session.
 */
function createMemoizingFetcher(fetcher) {
    const cache = new Map();
    const memo = (method, keyArgs, run) => {
        const key = `${method}:${JSON.stringify(keyArgs)}`;
        const hit = cache.get(key);
        if (hit)
            return hit;
        const pending = run();
        cache.set(key, pending);
        pending.catch(() => cache.delete(key)); // evict failures → a retry refetches
        return pending;
    };
    return {
        // --- Mutating / non-idempotent: pass through uncached -----------------
        getShortLink: (params, draft) => fetcher.getShortLink(params, draft),
        requestQuote: (params, formData, routingKey) => fetcher.requestQuote(params, formData, routingKey),
        // --- Read methods: memoized by (method, args) ------------------------
        getProductSchema: (params) => memo('getProductSchema', [params], () => fetcher.getProductSchema(params)),
        getDefaultProductVariant: (params) => memo('getDefaultProductVariant', [params], () => fetcher.getDefaultProductVariant(params)),
        getProductVariants: (params) => memo('getProductVariants', [params], () => fetcher.getProductVariants(params)),
        getCompositeProductSchema: (params) => memo('getCompositeProductSchema', [params], () => fetcher.getCompositeProductSchema(params)),
        getInitialCompositeProductVariant: (params, initialVariant) => memo('getInitialCompositeProductVariant', [params, initialVariant], () => fetcher.getInitialCompositeProductVariant(params, initialVariant)),
        getCompositeProductVariants: (params) => memo('getCompositeProductVariants', [params], () => fetcher.getCompositeProductVariants(params)),
        getCompositeProductPreset: (params, presetId) => memo('getCompositeProductPreset', [params, presetId], () => fetcher.getCompositeProductPreset(params, presetId)),
        getBomDetails: (params) => memo('getBomDetails', [params], () => fetcher.getBomDetails(params)),
        getProductUiSpecification: (params) => memo('getProductUiSpecification', [params], () => fetcher.getProductUiSpecification(params)),
        getCompositeUiSpecification: (params) => memo('getCompositeUiSpecification', [params], () => fetcher.getCompositeUiSpecification(params)),
        getProductView3dSpecification: (params) => memo('getProductView3dSpecification', [params], () => fetcher.getProductView3dSpecification(params)),
        getCompositeView3dSpecification: (params) => memo('getCompositeView3dSpecification', [params], () => fetcher.getCompositeView3dSpecification(params)),
        getCompositeData: (params) => memo('getCompositeData', [params], () => fetcher.getCompositeData(params)),
        getContactUsFeatureSchema: (params) => memo('getContactUsFeatureSchema', [params], () => fetcher.getContactUsFeatureSchema(params)),
        getPricingFeatureSchema: (params) => memo('getPricingFeatureSchema', [params], () => fetcher.getPricingFeatureSchema(params)),
        getHqrConfig: (params) => memo('getHqrConfig', [params], () => fetcher.getHqrConfig(params)),
        // Translations (ADR-0075 §clp-sdk): LIVE reads serve draft data that CSV
        // imports and custom-key edits change mid-session — they must not enter
        // this session-long cache. Only catalog-scoped active reads keep the
        // pre-existing memoization. The same live/active split as
        // Fetcher.translationsPath: useLive = draft || no catalog context.
        getProductTranslations: (params, lang) => {
            const catalogId = params.catalogId;
            const useLive = params.version === 'draft' || catalogId == null;
            if (useLive)
                return fetcher.getProductTranslations(params, lang);
            return memo('getProductTranslations', [params, lang], () => fetcher.getProductTranslations(params, lang));
        },
        // v2 reads pass through: their caching is owned by the localization
        // client, keyed by immutable snapshot identity — a session-long
        // request-keyed cache could serve a stale snapshot after a re-release.
        getProductTranslationsV2: (params, options) => fetcher.getProductTranslationsV2(params, options),
    };
}

const EMPTY_VIEW3D = { views: [], parts: [], camera: {}, materials: [] };
// ---------------------------------------------------------------------------
// Assembly helpers — pure functions used by both the fetcher and any test
// helpers that want the denormalized shape synchronously.
// ---------------------------------------------------------------------------
function basicToComponentProduct(record) {
    return {
        productId: record.schema.productId,
        productKey: record.schema.productKey,
        name: record.schema.productName,
        meta: {},
    };
}
function basicVariantToComponentVariant(variant, model3dId) {
    return {
        attributes: variant.attributes,
        meta: variant.meta,
        variantId: variant.variantId,
        productId: variant.productId,
        productKey: variant.productKey,
        model3dId,
    };
}
function findSchemaComponent(schema, componentKey) {
    const found = schema.productType.components.find((c) => c.key === componentKey);
    if (!found) {
        throw new Error(`createLocalFetcher: composite ${schema.productId} has no component "${componentKey}" ` +
            `in its productType.components.`);
    }
    return found;
}
/**
 * Assemble `ICompositeProductVariantWithRules` from a composite record by
 * resolving each component's basic-product references against `db`. The
 * result matches what the backend's `getCompositeProductVariants` returns.
 */
function assembleCompositeVariants(record, db) {
    const components = {};
    for (const [componentKey, productIds] of Object.entries(record.componentProducts)) {
        const list = productIds.map((productId) => {
            const basic = expectBasic(db, productId, `componentProducts["${componentKey}"]`);
            const componentModel3dMap = record.model3dMap?.[componentKey];
            return {
                product: basicToComponentProduct(basic),
                variants: basic.variants.map((v) => basicVariantToComponentVariant(v, componentModel3dMap?.[v.variantId] ?? null)),
            };
        });
        components[componentKey] = list;
    }
    return { rules: record.schema.rules, components, slots: assembleSlotVariants(record, db) };
}
/**
 * Assemble the `slots` half of `getCompositeProductVariants`: for every slot in
 * the composite's schema, the basic products it can accept, each with its
 * variants (carrying `model3dId`). Mirrors {@link assembleCompositeVariants}'
 * `components`, keyed by slot key instead of component key.
 *
 * `model3dId` is sourced from each accepted basic product's own intrinsic
 * `model3dByVariantId` (the geometry assignment that produced its `model3ds`),
 * not a composite-scoped map — slot-placed basics own their geometry. Accepted
 * items are unioned across any conditional groups via
 * {@link getAllAcceptedSlotItems}; only `basicProduct` entries contribute.
 */
function assembleSlotVariants(record, db) {
    const slots = {};
    for (const slot of record.schema.slots ?? []) {
        const productIds = new Set();
        for (const item of getAllAcceptedSlotItems(slot.acceptedItems ?? [])) {
            if (item.kind === 'basicProduct')
                productIds.add(item.productId);
        }
        slots[slot.key] = [...productIds].map((productId) => {
            const basic = expectBasic(db, productId, `slots["${slot.key}"]`);
            return {
                product: basicToComponentProduct(basic),
                variants: basic.variants.map((v) => basicVariantToComponentVariant(v, basic.model3dByVariantId?.[v.variantId] ?? null)),
            };
        });
    }
    return slots;
}
/**
 * Assemble `ICompositeVariantWithMeta` from a composite record's
 * `initialVariantRefs`. The result matches what the backend's
 * `getInitialCompositeProductVariant` returns.
 */
function assembleInitialVariant(record, db) {
    const components = {};
    for (const [componentKey, ref] of Object.entries(record.initialVariantRefs)) {
        if (ref === null) {
            components[componentKey] = null;
            continue;
        }
        const basic = expectBasic(db, ref.productId, `initialVariantRefs["${componentKey}"]`);
        const variant = basic.variants.find((v) => v.variantId === ref.variantId);
        if (!variant) {
            const known = basic.variants.map((v) => v.variantId).join(', ') || '(none)';
            throw new Error(`createLocalFetcher: basic product ${ref.productId} has no variant ${ref.variantId} ` +
                `for component "${componentKey}". Known variant ids: ${known}.`);
        }
        const model3dId = record.model3dMap?.[componentKey]?.[ref.variantId] ?? null;
        components[componentKey] = {
            product: basicToComponentProduct(basic),
            variant: basicVariantToComponentVariant(variant, model3dId),
        };
    }
    return { components, meta: record.initialMeta ?? {} };
}
/**
 * Assemble `ICompositeProductFullData` from a composite record. Per-component
 * `attributes` are derived by collecting distinct values seen across the
 * variants of every eligible basic product; per-component `products` is the
 * list of those basic products (name from the basic schema, no images by
 * default).
 */
function assembleCompositeData(record, db) {
    const componentDatas = {};
    for (const [componentKey, productIds] of Object.entries(record.componentProducts)) {
        const schemaComponent = findSchemaComponent(record.schema, componentKey);
        const products = productIds.map((productId) => {
            const basic = expectBasic(db, productId, `componentProducts["${componentKey}"]`);
            return {
                productId: basic.schema.productId,
                productKey: basic.schema.productKey,
                texts: { name: basic.schema.productName },
                images: [],
            };
        });
        const attributes = {};
        for (const attr of schemaComponent.type.attributes) {
            const seen = new Map();
            // Stringified value keys per product, so each product exposes only the
            // values its own variants use — mirrors the server's per-product scoping.
            const seenByProduct = new Map();
            for (const productId of productIds) {
                const basic = expectBasic(db, productId, `componentProducts["${componentKey}"]`);
                const productKeys = [];
                for (const variant of basic.variants) {
                    const value = variant.attributes[attr.key];
                    if (value === undefined)
                        continue;
                    // Stringify for keying so 0, false, '' stay distinct.
                    const key = `${typeof value}:${String(value)}`;
                    if (!seen.has(key))
                        seen.set(key, value);
                    if (!productKeys.includes(key))
                        productKeys.push(key);
                }
                seenByProduct.set(basic.schema.productKey, productKeys);
            }
            const valueByKey = new Map();
            const values = Array.from(seen.entries()).map(([key, value], idx) => {
                const textData = { id: idx, image: { url: '' }, value };
                valueByKey.set(key, textData);
                return textData;
            });
            const valuesByProduct = {};
            for (const [productKey, keys] of seenByProduct) {
                valuesByProduct[productKey] = keys.map((key) => valueByKey.get(key));
            }
            attributes[attr.key] = { name: attr.name, values, valuesByProduct };
        }
        componentDatas[componentKey] = {
            productTypeKey: record.componentProductTypeKeys?.[componentKey],
            composite: false,
            texts: record.componentTexts?.[componentKey] ?? { name: schemaComponent.name },
            attributes,
            products,
        };
    }
    return {
        product: {
            productId: record.schema.productId,
            productKey: record.schema.productKey,
            texts: record.productTexts ?? { name: record.schema.productName, description: '' },
        },
        components: componentDatas,
    };
}
function expectBasic(db, productId, context) {
    const record = db[productId];
    if (!record) {
        const knownBasic = Object.entries(db)
            .filter(([, r]) => r.kind === 'basic')
            .map(([id]) => id)
            .join(', ');
        throw new Error(`createLocalFetcher: ${context} references unknown basic product ${productId}. ` +
            `Known basic ids: ${knownBasic || '(none)'}.`);
    }
    if (record.kind !== 'basic') {
        throw new Error(`createLocalFetcher: ${context} references productId ${productId}, ` +
            `but that id is a composite product in the local db. Components must reference basic products.`);
    }
    return record;
}
/**
 * Assemble a composite's 3D view specification from `db`. `views[]` is the
 * union of the `model3ds` of every basic product reachable from the composite:
 * its components (`componentProducts`) plus the basic products its slots accept
 * (`schema.slots[].acceptedItems` of kind `basicProduct`). Nested composite
 * presets are *not* expanded — a placed composite loads its own view3d spec.
 * Deduplicated by `model3dId` so a product shared across components/slots
 * contributes its views once. The render-oriented fields stay empty (the local
 * fixture models bounds, not full scenes).
 */
function assembleView3dSpecification(record, db) {
    const productIds = new Set();
    for (const ids of Object.values(record.componentProducts)) {
        for (const id of ids)
            productIds.add(id);
    }
    for (const slot of record.schema.slots ?? []) {
        // Union across all conditional groups (if any) — every basic product the
        // slot could accept under any condition contributes its 3D models.
        for (const item of getAllAcceptedSlotItems(slot.acceptedItems ?? [])) {
            if (item.kind === 'basicProduct')
                productIds.add(item.productId);
        }
    }
    const views = [];
    const seen = new Set();
    for (const productId of productIds) {
        const basic = db[productId];
        if (basic?.kind !== 'basic' || !basic.model3ds)
            continue;
        for (const model of basic.model3ds) {
            if (seen.has(model.id))
                continue;
            seen.add(model.id);
            views.push(model);
        }
    }
    return { views, parts: assembleParts(record.schema.parts ?? []), camera: {}, materials: [] };
}
/**
 * Map the composite schema's parts to view3d part DTOs so the viewer builds a
 * scene container per part (needed for `space: 'part'` slots, which parent under
 * that container). The local fixture models only **root** parts: each gets an
 * identity `rootPartLayout` and its model is resolved at render time from the
 * component variant's `_model3dId`. Non-root parts need joint data the fixture
 * doesn't carry, so they're skipped with a warning rather than mis-positioned.
 */
function assembleParts(parts) {
    const viewParts = [];
    for (const part of parts) {
        if (part.parentPartKey !== '') {
            console.warn(`createLocalFetcher: skipping non-root part "${part.partKey}" — the local fixture models only root parts (no joint layout).`);
            continue;
        }
        viewParts.push({
            type: 'component',
            partKey: part.partKey,
            partName: part.partName,
            componentKey: part.componentKey,
            parentPartKey: part.parentPartKey,
            layout: { type: 'rootPartLayout', modelsTransforms: {} },
            rules: [],
            materials: [],
        });
    }
    return viewParts;
}
// ---------------------------------------------------------------------------
// Fetcher factory
// ---------------------------------------------------------------------------
/**
 * Build a {@link FetcherApi} backed by an in-memory {@link LocalDb}.
 *
 * Purpose: stand up a working SDK for **composite-product** and
 * **basic-product** development without a backend — local UI / 3D / bounds
 * work until the real API is wired up. Unlike `createFetcherApiMock` (a
 * jest-flavoured test double), this is a plain runtime implementation: no
 * jest, no `fs`, safe in the browser.
 *
 * Storage is *normalized*: every basic product lives once in the db, and
 * composite records reference them by `productId`. Composite-method
 * responses (`getCompositeProductVariants`, `getInitialCompositeProductVariant`,
 * `getCompositeData`) are assembled on the fly from those references — the
 * same pattern the backend's `configurator-product.service.ts` and the
 * `JsonFetcher` already use.
 *
 * Scope (intentionally minimal):
 *   - No catalogs, no draft/active versions. Just products by id.
 *   - Composite and basic data methods are served from `db`. The record's
 *     `kind` discriminator routes the call: composite methods only accept
 *     `kind: 'composite'` records, basic methods only accept
 *     `kind: 'basic'` records.
 *   - Feature methods (pricing, contact-us, HQR, short-link, view3d, …)
 *     still throw a clear "not supported" error so misuse fails loudly
 *     rather than silently returning empty data.
 *
 * Unknown `productId` → descriptive throw listing the known ids per kind so
 * the diagnostic points at the right slice of the db.
 */
function createLocalFetcher(db) {
    const productId = (params) => params.productId;
    const lookupComposite = (id) => {
        if (id === undefined) {
            throw new Error('createLocalFetcher: request is missing productId');
        }
        const record = db[id];
        if (!record) {
            const knownComposite = Object.entries(db)
                .filter(([, r]) => r.kind === 'composite')
                .map(([rid]) => rid)
                .join(', ');
            throw new Error(`createLocalFetcher: no composite product for id ${id}. ` +
                `Known composite ids: ${knownComposite || '(none)'}.`);
        }
        if (record.kind !== 'composite') {
            throw new Error(`createLocalFetcher: productId ${id} is a basic product, ` +
                `but a composite-product method was called for it.`);
        }
        return record;
    };
    const lookupBasic = (id) => {
        if (id === undefined) {
            throw new Error('createLocalFetcher: request is missing productId');
        }
        const record = db[id];
        if (!record) {
            const knownBasic = Object.entries(db)
                .filter(([, r]) => r.kind === 'basic')
                .map(([rid]) => rid)
                .join(', ');
            throw new Error(`createLocalFetcher: no basic product for id ${id}. ` + `Known basic ids: ${knownBasic || '(none)'}.`);
        }
        if (record.kind !== 'basic') {
            throw new Error(`createLocalFetcher: productId ${id} is a composite product, ` +
                `but a basic-product method was called for it.`);
        }
        return record;
    };
    // Async so callers `await fetcher.x()` get a rejected promise (the
    // FetcherApi contract), not a synchronous throw.
    const notSupported = (method) => async () => {
        throw new Error(`createLocalFetcher: "${method}" is not supported by the local fetcher.`);
    };
    const emptyUi = {};
    return {
        // --- Composite product data -----------------------------------------
        getCompositeProductSchema: async (params) => lookupComposite(productId(params)).schema,
        getInitialCompositeProductVariant: async (params) => assembleInitialVariant(lookupComposite(productId(params)), db),
        getCompositeProductVariants: async (params) => assembleCompositeVariants(lookupComposite(productId(params)), db),
        getCompositeProductPreset: async (params, presetId) => {
            const record = lookupComposite(productId(params));
            const preset = record.presets?.[presetId];
            if (!preset) {
                const known = record.presets ? Object.keys(record.presets).join(', ') || '(none)' : '(none)';
                throw new Error(`createLocalFetcher: no preset "${presetId}" on product ${params.productId}. ` +
                    `Known preset ids: ${known}.`);
            }
            return preset;
        },
        getCompositeData: async (params) => assembleCompositeData(lookupComposite(productId(params)), db),
        getCompositeUiSpecification: async (params) => lookupComposite(productId(params)).uiSpecification ?? emptyUi,
        getBomDetails: async (params) => lookupComposite(productId(params)).bomDetails ?? [],
        getProductTranslations: async () => ({ language: null, translations: {} }),
        getProductTranslationsV2: async () => ({
            schemaVersion: 2,
            language: null,
            defaultLanguage: null,
            families: ['custom', 'entities', 'ui'],
            layers: {},
        }),
        // --- Basic product data ---------------------------------------------
        getProductSchema: async (params) => lookupBasic(productId(params)).schema,
        getDefaultProductVariant: async (params) => lookupBasic(productId(params)).initialVariant,
        getProductVariants: async (params) => lookupBasic(productId(params)).variants,
        // --- View3d: served from the db's model3d data -----------------------
        getProductView3dSpecification: async (params) => {
            const record = lookupBasic(productId(params));
            const spec = record.model3ds
                ? { views: record.model3ds, parts: [], camera: {}, materials: [] }
                : EMPTY_VIEW3D;
            return spec;
        },
        getCompositeView3dSpecification: async (params) => {
            const spec = assembleView3dSpecification(lookupComposite(productId(params)), db);
            return spec;
        },
        // --- Everything else: explicit "not supported" ----------------------
        getShortLink: notSupported('getShortLink'),
        getProductUiSpecification: notSupported('getProductUiSpecification'),
        getContactUsFeatureSchema: notSupported('getContactUsFeatureSchema'),
        getPricingFeatureSchema: notSupported('getPricingFeatureSchema'),
        requestQuote: notSupported('requestQuote'),
        getHqrConfig: notSupported('getHqrConfig'),
    };
}

/**
 * Creates a "pure" copy of an object by removing system/internal properties.
 *
 * This function recursively processes an object and its nested objects, filtering out
 * properties that are considered system(those starting with '_' like _variantId, _meta, etc.), while
 * preserving the original structure and non-system properties.
 *
 * @param object - The source object to make pure
 * @param skipProps - Array of property names that should NOT be filtered out even if they start with '_'
 * @returns A new object containing only non-system properties
 *

 */
function pureObject(object, skipProps = []) {
    const entries = [];
    for (const [prop, value] of Object.entries(object)) {
        if (prop.startsWith('_') && !skipProps.includes(prop)) {
            continue;
        }
        if (typeof object[prop] === 'object' && object[prop] !== null) {
            entries.push([prop, pureObject(value, skipProps)]);
        }
        else {
            // Preserve primitive values and other non-object properties as-is
            entries.push([prop, value]);
        }
    }
    // Convert entries back to an object and return
    return Object.fromEntries(entries);
}
function createVariantId(prefix, id) {
    return prefix + '_' + id.toString();
}
function createAttributeId(productComponentKey, attributeKey) {
    return `${productComponentKey}.${attributeKey}`;
}

// Moved over from CompositeProductApi so the dependency between the two modules
// stays one-way: CompositeProductApi imports the price-type helpers below, and
// nothing flows back.
function createNullVariantId(typeName) {
    return typeName + '_null';
}
/**
 * Ordinal of the default price type, mirroring the domain key grammar
 * (`priceKey`/`parsePriceKey`): ordinal 1 is emitted bare, so `getPrice()`
 * without options projects a pre-price-types payload onto itself unchanged.
 */
const DEFAULT_PRICE_TYPE_ORDINAL = 1;
/**
 * Every catalog owns a default price-type row keyed `default`, but payloads
 * produced before price types — and the catalog-less read paths — carry no
 * `priceTypes` at all. Synthesizing it keeps `getPriceTypes()` non-empty
 * everywhere, so a host can always render a selector and
 * `getPrice({ priceType: 'default' })` resolves instead of throwing.
 */
const IMPLICIT_DEFAULT_PRICE_TYPE = {
    ordinal: DEFAULT_PRICE_TYPE_ORDINAL,
    key: 'default',
    name: null,
    isDefault: true,
};
/**
 * Turns the catalog's price-type records into the SDK's public view. Shared by
 * every api that owns a price so the ordinal/key/wire-key triple is derived in
 * exactly one place.
 *
 * The price-type label ID is catalog-scoped (`cat:<catalogKey>.price:…`) and
 * `catalogMeta.catalogKey` is optional by design: the catalog-less
 * product-version read paths have no catalog to scope to. When it is absent
 * `nameLabelId` stays empty and hosts fall back to the raw `name`, which is the
 * documented last resort of the name resolution order.
 */
function buildPriceTypeViews(catalogMeta) {
    const priceTypes = catalogMeta?.priceTypes?.length ? catalogMeta.priceTypes : [IMPLICIT_DEFAULT_PRICE_TYPE];
    const catalogKey = catalogMeta?.catalogKey;
    return priceTypes.map((priceType) => ({
        ordinal: priceType.ordinal,
        // Mirrors the domain grammar's prefix; `priceKey` itself always wants a currency.
        wireKey: `PT${priceType.ordinal}`,
        key: priceType.key,
        name: priceType.name,
        nameLabelId: catalogKey ? labelId.priceTypeName(catalogKey, priceType.key) : '',
        isDefault: priceType.isDefault,
    }));
}
/**
 * Resolves the `priceType` argument — ordinal, wire key or stable key — against
 * the catalog's types. The only place the dual identity of a price type is
 * interpreted; everything else works with ordinals.
 */
function resolvePriceTypeOrdinal(views, priceType) {
    if (priceType === undefined) {
        return DEFAULT_PRICE_TYPE_ORDINAL;
    }
    const match = views.find((view) => view.ordinal === priceType || view.wireKey === priceType || view.key === priceType);
    if (!match) {
        throw new PriceTypeNotFoundError(priceType, views);
    }
    return match.ordinal;
}
/**
 * Currently use internally by CompositeProductComponentApi.
 *
 */
class BasicProductApi {
    id;
    componentKey;
    productTypeName;
    attributes;
    _modelRef;
    productName;
    defaultValidationOptions;
    _awaitLogicEngineReady = null;
    defaultVariantId;
    nullVariantId;
    _onModelChange = null;
    _catalogMeta;
    constructor(id, componentKey, productTypeName, attributes = [], 
    /**
     * Reference to the CompositeProduct model stored in the parent
     */
    _modelRef, productName, defaultValidationOptions) {
        this.id = id;
        this.componentKey = componentKey;
        this.productTypeName = productTypeName;
        this.attributes = attributes;
        this._modelRef = _modelRef;
        this.productName = productName;
        this.defaultValidationOptions = defaultValidationOptions;
        this.nullVariantId = createNullVariantId(productTypeName);
        this.defaultVariantId = id === undefined ? this.nullVariantId : this.toCompoundVariantId(id);
    }
    setModelChangeNotifier(notifier) {
        this._onModelChange = notifier;
    }
    /**
     * Wiring seam for the catalog's price types: this api is built from a variant
     * alone and never sees the product schema, so the owner injects `catalogMeta`.
     * `CompositeProductApi` does it for its component apis. Without it,
     * `getPriceTypes()` reports the implicit default type only.
     */
    setCatalogMeta(catalogMeta) {
        this._catalogMeta = catalogMeta;
    }
    injectLogicEngine(logicEngine) {
        this._awaitLogicEngineReady = logicEngine;
    }
    get awaitLogicEngine() {
        if (!this._awaitLogicEngineReady) {
            throw new Error('Logic engine should be injected before using');
        }
        return this._awaitLogicEngineReady;
    }
    get variant() {
        return new ConfiguratorPV(this._modelRef.model[this.componentKey]);
    }
    get parentCompositeProduct() {
        return this._modelRef.model;
    }
    getKey() {
        return this.componentKey;
    }
    /**
     * Current variantId reflected by the api's model. Differs from the
     * constructor-time `id` (initial placement variant) once `setAttributeValue`
     * navigates to a different variant. Returns `undefined` for a nullish
     * variant (no value selected).
     *
     * Stored in `ConfiguratorPV` as a string (via `_variantId` system property);
     * coerced back to `ProductVariantId` (number) here so callers — notably
     * `SlotApi.serialize` — get the canonical domain type without re-parsing.
     */
    getCurrentVariantId() {
        if (this.variant.isNullish)
            return undefined;
        const raw = this.variant.variantId;
        const num = Number(raw);
        return Number.isFinite(num) ? num : undefined;
    }
    /**
     * Get the current value of the attribute.
     */
    getAttributeValue(attributeId) {
        const attribute = this.attributes.find((attribute) => attribute.id === attributeId);
        if (!attribute) {
            return undefined;
        }
        return this.variant.raw[attribute.key];
    }
    /**
     * Get current product variant (all current attribute values for product)
     */
    getAllAttributesValue() {
        return pureObject(this.variant.raw);
    }
    getAttributes() {
        return this.attributes;
    }
    getName() {
        return this.productName;
    }
    getPriceTypes() {
        return buildPriceTypeViews(this._catalogMeta);
    }
    async getPrice(options) {
        const ordinal = resolvePriceTypeOrdinal(this.getPriceTypes(), options?.priceType);
        return projectPrice(await this.getPriceAllTypes(), ordinal);
    }
    async getPriceAllTypes() {
        if (this.variant.isNullish) {
            return {};
        }
        const compoundId = this.variant.compoundId;
        await this.awaitLogicEngine;
        const variantPrice = this.variant.meta.price;
        if (!variantPrice) {
            console.error(`Cannot find price for current variant "${compoundId}"`);
            return {};
        }
        return variantPrice;
    }
    async getMetaId() {
        if (this.variant.isNullish) {
            return '';
        }
        await this.awaitLogicEngine;
        const metaId = this.variant.meta.id;
        if (!metaId) {
            console.error(`Cannot find metaId for current variant "${this.variant.compoundId}"`);
            return '';
        }
        return metaId;
    }
    getVariant() {
        return this.getAllAttributesValue();
    }
    getVariantRaw() {
        return this.variant.raw;
    }
    /**
     * Returns values filtered by the current configurator state (respecting the active model snapshot).
     */
    async loadCompatibleAttributeValues(attributeId) {
        const attribute = this.findAttributeById(attributeId);
        return this.loadAttributeValues(attribute.key);
    }
    /**
     * Returns every value the logic engine knows about for the attribute, ignoring the current configurator state.
     * Useful when you need the full domain (all variant combinations), not just context-aware availability.
     */
    async loadAllAttributeValues(attributeId) {
        const logicEngine = await this.awaitLogicEngine;
        const attribute = this.findAttributeById(attributeId);
        return logicEngine.getAllValues(this.componentKey, attribute.key);
    }
    async loadAttributeValues(attributeKey) {
        const logicEngine = await this.awaitLogicEngine;
        const values = logicEngine.getCompatibleValues(this.parentCompositeProduct, this.componentKey, attributeKey, this.defaultValidationOptions);
        if (!values) {
            throw new Error(`Error in component ${this.componentKey}. Unknown attribute: ${attributeKey}`);
        }
        return values;
    }
    async loadAllCompatibleAttributeValues() {
        const attributeValues = {};
        for (const attribute of this.attributes) {
            attributeValues[attribute.key] = await this.loadAttributeValues(attribute.key);
        }
        return attributeValues;
    }
    async loadAllAttributesValues() {
        const attributeValues = {};
        for (const attribute of this.attributes) {
            attributeValues[attribute.key] = await this.loadAllAttributeValues(attribute.id);
        }
        return attributeValues;
    }
    async setAttributeValue(attributeId, attributeValue) {
        const attribute = this.findAttributeById(attributeId);
        return this.setAttributeValueByAttributeKey(attribute.key, attributeValue);
    }
    isAvailable() {
        return this.variant.isAvailable();
    }
    async setAttributeValueByAttributeKey(attributeKey, attributeValue) {
        const logicEngine = await this.awaitLogicEngine;
        const previousModel = this._modelRef.model;
        const model = logicEngine.setValue(this.parentCompositeProduct, this.componentKey, attributeKey, attributeValue, this.defaultValidationOptions);
        if (!model) {
            throw new Error(`Cannot set "${attributeValue}" for attribute "${attributeKey}" in component "${this.componentKey}"`);
        }
        this._modelRef.model = model;
        // Notify subscribers about all changes
        if (this._onModelChange) {
            this._onModelChange(previousModel, model);
        }
    }
    findAttributeById(attributeId) {
        const attribute = this.attributes.find((attribute) => attribute.id === attributeId);
        if (!attribute) {
            throw new Error(`Invalid attributeId "${attributeId}"`);
        }
        return attribute;
    }
    toCompoundVariantId(id) {
        return createVariantId(this.productTypeName, id);
    }
}

function validateProductLogicOrder(logicOrder, type) {
    if (!logicOrder.length) {
        throw new Error('Empty logic order');
    }
    const typeAttributes = new Set(type.attributes.map((attribute) => attribute.key));
    for (let i = 0; i < logicOrder.length; i++) {
        const { attributeKey } = logicOrder[i];
        if (attributeKey === '_productId') {
            continue;
        }
        if (attributeKey === undefined) {
            throw new Error(`Invalid item #${i + 1}. Missing "attributeKey"`);
        }
        if (typeof attributeKey !== 'string') {
            throw new Error(`Invalid item #${i + 1}. "attributeKey" must be a string`);
        }
        if (!typeAttributes.has(attributeKey)) {
            throw new Error(`Invalid attribute "${attributeKey}"`);
        }
    }
    const usedAttributes = new Set();
    for (const { attributeKey } of logicOrder) {
        if (usedAttributes.has(attributeKey)) {
            throw new Error(`Duplicated attribute "${attributeKey}" found`);
        }
        else {
            usedAttributes.add(attributeKey);
        }
    }
}
function getDefaultProductLogicOrder(type) {
    return type.attributes.map((attribute) => ({ attributeKey: attribute.key }));
}
function validateCompositeLogicOrder(logicOrder, type) {
    if (!logicOrder.length) {
        throw new Error('Empty logic order');
    }
    const typeComponents = new Set(type.components.map((item) => item.key));
    const components = new Set();
    const attributes = new Map();
    for (let i = 0; i < logicOrder.length; i++) {
        const item = logicOrder[i];
        if (item.componentKey === undefined) {
            throw new Error(`Invalid item #${i + 1}. Missing "componentKey"`);
        }
        if (typeof item.componentKey !== 'string') {
            throw new Error(`Invalid item #${i + 1}. "componentKey" must be a string`);
        }
        if (!typeComponents.has(item.componentKey)) {
            throw new Error(`Invalid component "${item.componentKey}"`);
        }
        switch (item.type) {
            case 'availability':
                if (components.has(item.componentKey)) {
                    throw new Error(`Duplicated component "${item.componentKey}" found`);
                }
                else {
                    components.add(item.componentKey);
                }
                break;
            case 'attribute':
                if (!components.has(item.componentKey)) {
                    throw new Error(`Invalid item #${i + 1}. Component "${item.componentKey}" must go before any of its attribute`);
                }
                if (!attributes.has(item.componentKey)) {
                    attributes.set(item.componentKey, []);
                }
                attributes.get(item.componentKey).push({ attributeKey: item.attributeKey });
                break;
        }
    }
    for (const [componentKey, logicOrder] of attributes) {
        const componentType = type.components.find((component) => component.key === componentKey);
        if (!componentType) {
            throw new Error(`Invalid componentKey "${componentKey}"`);
        }
        try {
            validateProductLogicOrder(logicOrder, componentType?.type);
        }
        catch (e) {
            if (e instanceof Error) {
                throw new Error(`Invalid component "${componentKey}". ${e.message}`);
            }
            else {
                throw e;
            }
        }
    }
}
function getDefaultCompositeLogicOrder(type) {
    const result = [];
    for (const component of type.components) {
        const attributes = getDefaultProductLogicOrder(component.type);
        result.push({
            componentKey: component.key,
            type: 'availability',
        });
        result.push({
            componentKey: component.key,
            attributeKey: '_productId',
            type: 'attribute',
        });
        for (const attribute of attributes) {
            result.push({
                componentKey: component.key,
                attributeKey: attribute.attributeKey,
                type: 'attribute',
            });
        }
    }
    return result;
}

const ComponentKey = 'product';
class BasicProductApiBuilder {
    options;
    fetcher;
    defaultValidationOptions;
    logicEngineBuilder = new LogicEngineBuilder();
    product;
    attributes = [];
    initialProductVariant;
    // Cached on the first fetch (either by `load` when `initialVariantId` is
    // requested, or by `prepareEngine`). Avoids hitting `getProductVariants`
    // twice when both paths need the full variants list.
    variantsPromise = null;
    logicOrder = [];
    // Per-variant 3D-model ids for this slot-placed basic, supplied via
    // `load({ model3dByVariantId })`. Threaded into every mapped variant so the
    // api's `_model3dId` is correct (and survives attribute navigation), letting
    // `buildSlotRenderSpecs` emit a `model3d` ref. Empty for standalone basics.
    model3dByVariantId = {};
    isInCatalog;
    constructor(options, fetcher, defaultValidationOptions) {
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
        this.isInCatalog = 'catalogId' in options;
    }
    async load(loadOptions = {}) {
        this.model3dByVariantId = loadOptions.model3dByVariantId ?? {};
        if (loadOptions.initialVariantId !== undefined) {
            // Initial variant is selected from the full variants list — fetch
            // schema + variants in parallel and pick the matching one.
            this.variantsPromise = this.fetcher.getProductVariants(this.options);
            const [product, variants] = await Promise.all([
                this.fetcher.getProductSchema(this.options),
                this.variantsPromise,
            ]);
            this.product = product;
            const match = variants.find((v) => v.variantId === loadOptions.initialVariantId);
            if (!match) {
                throw new Error(`BasicProductApiBuilder: variantId ${loadOptions.initialVariantId} not found ` +
                    `for productId ${this.product.productId} (productKey ${this.product.productKey}).`);
            }
            this.initialProductVariant = match;
        }
        else {
            [this.product, this.initialProductVariant] = await Promise.all([
                this.fetcher.getProductSchema(this.options),
                this.fetcher.getDefaultProductVariant(this.options),
            ]);
        }
        this.parseAttributes();
        this.createSchema();
        const model = {
            [ComponentKey]: ConfiguratorToDomainProductMapper.toCfgProductVariant({
                variant: this.initialProductVariant,
                componentKey: ComponentKey,
                product: this.product,
                model3dId: this.model3dByVariantId[this.initialProductVariant.variantId] ?? null,
            }).mapped,
        };
        const api = new BasicProductApi(this.initialProductVariant.variantId, ComponentKey, this.product.productType.name, this.attributes, { model }, this.product.productName, this.defaultValidationOptions);
        // A standalone basic owns its schema, so its catalog's price types arrive on
        // `product.catalogMeta`. Mirrors what `CompositeProductApi` does for its
        // component apis; without it `getPriceTypes()` would report the implicit
        // default only and `getPrice({ priceType })` would throw for every real type.
        api.setCatalogMeta(this.product.catalogMeta);
        api.injectLogicEngine(this.prepareEngine());
        return api;
    }
    parseAttributes() {
        if (this.product.logicOrder) {
            this.parseLogicOrder(this.product.logicOrder);
        }
        else {
            this.createImplicitLogicOrder();
        }
        this.attributes = this.logicOrder.map((item) => ({
            id: item.attributeKey,
            key: item.attributeKey,
        }));
    }
    createSchema() {
        this.logicEngineBuilder.setSchema({
            attributes: this.attributes.map((attribute) => ({
                attributeKey: attribute.key,
                componentKey: ComponentKey,
            })),
        });
    }
    async prepareEngine() {
        // Reuse the cached promise if `load` already fired the variants fetch
        // for the `initialVariantId` path; otherwise kick it off now.
        if (!this.variantsPromise) {
            this.variantsPromise = this.fetcher.getProductVariants(this.options);
        }
        const variants = await this.variantsPromise;
        this.createProductVariants(variants);
        return this.logicEngineBuilder.build();
    }
    createProductVariants(variants) {
        this.logicEngineBuilder.addProductVariants(ComponentKey, variants.map((variant) => ConfiguratorToDomainProductMapper.toCfgProductVariant({
            variant,
            componentKey: ComponentKey,
            product: this.product,
            model3dId: this.model3dByVariantId[variant.variantId] ?? null,
        }).mapped));
    }
    createImplicitLogicOrder() {
        this.logicOrder = getDefaultProductLogicOrder(this.product.productType);
    }
    parseLogicOrder(logicOrder) {
        this.logicOrder = logicOrder;
        this.validateLogicOrder();
    }
    validateLogicOrder() {
        validateProductLogicOrder(this.logicOrder, this.product.productType);
    }
}

/**
 * Provide access to a single component, its products and attributes(using BasicProductApi)
 */
class CompositeProductComponentApi {
    api;
    componentData;
    productComponent;
    awaitInjection = null;
    constructor(api, componentData, productComponent) {
        this.api = api;
        this.componentData = componentData;
        this.productComponent = productComponent;
    }
    get variant() {
        return this.api.variant;
    }
    getProductData() {
        if (this.variant.isNullish) {
            return null;
        }
        const productId = this.variant.productId;
        const result = this.componentData.products.find((it) => String(it.productId) === productId);
        if (!result) {
            throw new Error('Component data not found');
        }
        return result;
    }
    getKey() {
        return this.api.getKey();
    }
    injectAwait(awaitInjection) {
        this.awaitInjection = awaitInjection;
        this.api.injectLogicEngine(awaitInjection.then((value) => value.logicEngine));
    }
    getAttributeValue(attributeKey) {
        return this.api.getAttributeValue(attributeKey);
    }
    getCurrentVariantId() {
        return this.api.getCurrentVariantId();
    }
    getAttributes(skipSystemAttributes = false) {
        return this.api
            .getAttributes()
            .filter((attribute) => attribute.key !== VariantProperties.available && (!skipSystemAttributes || !attribute.key.startsWith('_')));
    }
    getName() {
        return this.api.getName();
    }
    /** See `BasicProductApi.setCatalogMeta` — called by the owning composite. */
    setCatalogMeta(catalogMeta) {
        this.api.setCatalogMeta(catalogMeta);
    }
    getPriceTypes() {
        return this.api.getPriceTypes();
    }
    getPrice(options) {
        return this.api.getPrice(options);
    }
    getPriceAllTypes() {
        return this.api.getPriceAllTypes();
    }
    getMetaId() {
        return this.api.getMetaId();
    }
    getVariant() {
        return this.api.getVariant();
    }
    getVariantRaw() {
        return this.api.getVariantRaw();
    }
    isAvailable() {
        return this.api.isAvailable();
    }
    isEnabled() {
        return this.productComponent.enabled;
    }
    isIncludedInPdf() {
        return this.productComponent.includeInPdf;
    }
    async loadAvailability() {
        const values = await this.api.loadAttributeValues(VariantProperties.available);
        return values.map((item) => getAttributeValue(item));
    }
    loadCompatibleAttributeValues(attributeId) {
        return this.api.loadCompatibleAttributeValues(attributeId);
    }
    loadAllCompatibleAttributeValues() {
        return this.api.loadAllCompatibleAttributeValues();
    }
    loadAllAttributeValues(attributeId) {
        return this.api.loadAllAttributeValues(attributeId);
    }
    loadAllAttributesValues() {
        return this.api.loadAllAttributesValues();
    }
    setAttributeValue(attributeId, attributeValue) {
        return this.api.setAttributeValue(attributeId, attributeValue);
    }
    setAvailability(available) {
        return this.api.setAttributeValueByAttributeKey(VariantProperties.available, createAttributeValue(available));
    }
}

const ZERO = { x: 0, y: 0, z: 0 };
/** True for an absent or identity `(0,0,0)` Euler rotation. */
function isZeroRotation(v) {
    return !v || (v.x === 0 && v.y === 0 && v.z === 0);
}
/**
 * The item's effective (rotated) bbox and the rotation matrix that produced it.
 * Rotation (ADR-0060 / ADR-0063) is applied about the local bbox centre, so the
 * bbox centre is invariant and only the extents change. Identity rotation
 * returns the local bbox unchanged. Shared by the stack and list strategies —
 * downstream layout consumes the rotated bbox and composes `T · rCenter`.
 */
function rotatedAboutCenter(local, rotation) {
    if (isZeroRotation(rotation))
        return { bbox: local, rCenter: Mat4.identity() };
    const c = centerAABB(local);
    const r = Mat4.fromTransform({ translation: ZERO, rotation: rotation });
    // T(c) · R · T(-c): rotate about the bbox centre.
    const rCenter = Mat4.makeTranslation(c)
        .multiply(r)
        .multiply(Mat4.makeTranslation({ x: -c.x, y: -c.y, z: -c.z }));
    return { bbox: transformAABBByMat4(local, rCenter), rCenter };
}

const AXES$1 = ['x', 'y', 'z'];
/**
 * List strategy.
 *
 * Semantics:
 *   - A list slot is a 1-D row of fixed cells `0..capacity-1` (capacity =
 *     slot's `cardinality.max`). Items are placed at an **explicit** cell
 *     index via `SlotApi.addItemAt` — there is no append/auto-fill.
 *   - Cells may have **gaps**: removing a cell does NOT re-index the others
 *     (`onRemove` is a no-op), so the remaining items keep their positions.
 *   - A cell holds at most one item; `canAdd` rejects an already-occupied
 *     index, a negative index, or an index `>= capacity`.
 *
 * Positioning (`computeItemTransform`, ADR-0063): each cell `N` sits at
 * `anchor + N·cellSize` along `direction`. The item is rotated about its bbox
 * centre (`rotation`, ADR-0060 geometry) and the **rotated** bbox is anchored
 * to the cell point by the per-axis `align` feature (min/center/max, default
 * `center`) on EVERY axis — the main axis included — then shifted by the
 * signed per-axis `offset`. Per-item `align`/`offset` override the slot-level
 * defaults per axis. Gaps just leave their cell point unoccupied — neighbours
 * keep their absolute positions because the offset is index-driven, not packed
 * from sizes (unlike `stack`). The final transform is `translation · rotation`.
 */
function checkTarget(currentItems, capacity, target) {
    if (!target) {
        return { ok: false, reason: 'list placement requires an explicit cell index (use addItemAt).' };
    }
    const { index } = target;
    if (!Number.isInteger(index) || index < 0) {
        return { ok: false, reason: `list cell index must be a non-negative integer, got ${index}.` };
    }
    if (capacity !== 'unbounded' && index >= capacity) {
        return { ok: false, reason: `list cell index ${index} is out of bounds (capacity ${capacity}).` };
    }
    const occupied = currentItems.some((it) => it.placement?.kind === 'list' && it.placement.index === index);
    if (occupied) {
        return { ok: false, reason: `list cell index ${index} is already occupied.` };
    }
    return { ok: true };
}
const listStrategy = {
    kind: 'list',
    canAdd({ currentItems, capacity, target }) {
        return checkTarget(currentItems, capacity, target);
    },
    onAdd({ config, currentItems, capacity, target }) {
        const check = checkTarget(currentItems, capacity, target);
        if (!check.ok) {
            throw new Error(check.reason);
        }
        // checkTarget guarantees `target` is defined when ok. Carry the preferred
        // rotation onto the placed item (omit identity to keep serialized placement
        // info clean) — ADR-0063, mirroring stack's onAdd.
        const base = { kind: 'list', index: target.index };
        return isZeroRotation(config.defaultRotation) ? base : { ...base, rotation: config.defaultRotation };
    },
    // Holes are preserved — remaining items keep their cell index.
    onRemove() {
        return new Map();
    },
    /**
     * Anchor the item's **rotated** bbox at its cell point
     * `anchor + index·cellSize` along `direction`: the `align` feature (default
     * `center`) on every axis — main included — then `+ offset` per axis
     * (ADR-0063). Offsetting is purely index-driven, so gaps leave their cell
     * empty without shifting neighbours.
     *
     * Items without a `kind: 'list'` placement, or whose bbox is missing from
     * `itemBounds`, fall back to translating the model origin to the cell point
     * (no centre anchor possible) — defensive, mirrors `stack`.
     */
    computeItemTransform({ item, config, itemBounds }) {
        const info = item.placement?.kind === 'list' ? item.placement : undefined;
        const cellPoint = addVec(config.anchor, axisVec(config.direction, (info?.index ?? 0) * config.cellSize));
        const local = itemBounds.get(item.id);
        if (!local) {
            return Mat4.makeTranslation(cellPoint);
        }
        const { bbox, rCenter } = rotatedAboutCenter(local, info?.rotation);
        // Per-element overrides take precedence over slot-level defaults, per axis.
        const align = { ...config.align, ...info?.align };
        const offset = { ...config.offset, ...info?.offset };
        const t = { x: 0, y: 0, z: 0 };
        for (const c of AXES$1) {
            // Every axis — the main one included — anchors the chosen bbox feature
            // to the cell point; `center` is the default (the fixed-pitch behaviour
            // that used to be hardcoded on the main axis).
            const mode = align[c] ?? 'center';
            const feature = mode === 'min' ? bbox.min[c] : mode === 'max' ? bbox.max[c] : (bbox.min[c] + bbox.max[c]) / 2;
            t[c] = cellPoint[c] - feature + (offset[c] ?? 0);
        }
        // Final transform = translation · rotation-about-centre.
        return Mat4.makeTranslation(t).multiply(rCenter);
    },
};

const AXES = ['x', 'y', 'z'];
function rotationOf(item) {
    return item.placement?.kind === 'stack' ? item.placement.rotation : undefined;
}
/**
 * Sum of preceding stack siblings' **rotated** sizes along `direction`.
 * Siblings whose bbox is missing contribute zero but still count toward the
 * gap multiplier.
 */
function accumulatePreceding(myIndex, config, allItems, itemBounds) {
    let acc = 0;
    let count = 0;
    for (const sib of allItems) {
        if (sib.placement?.kind !== 'stack')
            continue;
        if (sib.placement.index >= myIndex)
            continue;
        count++;
        const sb = itemBounds.get(sib.id);
        if (sb)
            acc += sizeAlong(rotatedAboutCenter(sb, rotationOf(sib)).bbox, config.direction);
    }
    return { acc, count };
}
/**
 * Uniform main-axis shift that anchors the BLOCK by the first item's bbox
 * feature: with `align[direction-axis]` set, the min/center/max of the FIRST
 * stack item lands at `anchor[m]` and the whole run shifts by the same delta —
 * packing (gap between neighbouring faces) is untouched. Undefined `align[m]`
 * keeps the original leading-edge anchoring. The first item is the
 * lowest-index stack item that has bounds (same tolerance as packing).
 */
function mainAxisAlignDelta(config, allItems, itemBounds) {
    const m = config.direction[1];
    const s = config.direction[0] === '-' ? -1 : 1;
    let first = null;
    let firstIndex = Infinity;
    for (const item of allItems) {
        if (item.placement?.kind !== 'stack' || item.placement.index >= firstIndex)
            continue;
        const local = itemBounds.get(item.id);
        if (!local)
            continue;
        firstIndex = item.placement.index;
        first = { bbox: rotatedAboutCenter(local, item.placement.rotation).bbox, align: item.placement.align };
    }
    if (!first)
        return 0;
    const mode = { ...config.align, ...first.align }[m];
    if (!mode)
        return 0;
    const { bbox } = first;
    const lead = s > 0 ? bbox.min[m] : bbox.max[m];
    const feature = mode === 'min' ? bbox.min[m] : mode === 'max' ? bbox.max[m] : (bbox.min[m] + bbox.max[m]) / 2;
    return lead - feature;
}
/**
 * The item's pre-clamp slot-local translation (ADR-0059) plus its effective
 * (rotated, ADR-0060) bbox and the rotation matrix. Returns `null` when the
 * item is not a stack placement or has no bounds. All downstream layout
 * (edge-anchor, align, accumulation, clamp) operates on the rotated bbox.
 *
 *   - main (`direction`) axis: edge-anchored packing — leading face lands at
 *     `anchor + Σ siblingSizes + gap·N`, then `+ offset[main]`. With
 *     `align[main]` set, the whole block additionally shifts so the FIRST
 *     item's chosen bbox feature lands at `anchor[main]`
 *     ({@link mainAxisAlignDelta}); inter-item gaps are unchanged.
 *   - cross axes: `align` pins the bbox min/center/max to `anchor[axis]`;
 *     when `align` is omitted the model origin lands at `anchor[axis]`
 *     (legacy behaviour). Then `+ offset[axis]`.
 */
function preClampTranslation(item, config, allItems, itemBounds) {
    if (item.placement?.kind !== 'stack')
        return null;
    const local = itemBounds.get(item.id);
    if (!local)
        return null;
    const { bbox, rCenter } = rotatedAboutCenter(local, item.placement.rotation);
    const m = config.direction[1];
    const s = config.direction[0] === '-' ? -1 : 1;
    const { acc, count } = accumulatePreceding(item.placement.index, config, allItems, itemBounds);
    const gap = config.gap ?? 0;
    // Per-element overrides take precedence over slot-level defaults, per axis.
    const align = { ...config.align, ...item.placement.align };
    const offset = { ...config.offset, ...item.placement.offset };
    const t = { x: 0, y: 0, z: 0 };
    for (const c of AXES) {
        const off = offset[c] ?? 0;
        if (c === m) {
            const basePos = config.anchor[m] + s * (acc + gap * count);
            const lead = s > 0 ? bbox.min[m] : bbox.max[m];
            t[m] = basePos - lead + mainAxisAlignDelta(config, allItems, itemBounds) + off;
        }
        else {
            const mode = align[c];
            if (mode === undefined) {
                t[c] = config.anchor[c] + off;
            }
            else {
                const feature = mode === 'min' ? bbox.min[c] : mode === 'max' ? bbox.max[c] : (bbox.min[c] + bbox.max[c]) / 2;
                t[c] = config.anchor[c] - feature + off;
            }
        }
    }
    return { t, bbox, rCenter };
}
/** Minimal shift to bring the span `[lo, hi]` inside `[min, max]` (0 if already inside). */
function clampShift(lo, hi, min, max) {
    if (hi > max)
        return max - hi;
    if (lo < min)
        return min - lo;
    return 0;
}
/**
 * The single uniform shift (ADR-0059 whole-block fit) that pulls the stack's
 * main-axis extent inside the boundary. Pure function of the inputs, so each
 * per-item `computeItemTransform` call derives the same delta without shared
 * state. Unbounded boundary axes (±Infinity) yield zero.
 */
function mainAxisBlockDelta(config, allItems, itemBounds, boundary) {
    const m = config.direction[1];
    let lo = Infinity;
    let hi = -Infinity;
    for (const it of allItems) {
        const p = preClampTranslation(it, config, allItems, itemBounds);
        if (!p)
            continue;
        lo = Math.min(lo, p.t[m] + p.bbox.min[m]);
        hi = Math.max(hi, p.t[m] + p.bbox.max[m]);
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi))
        return 0;
    return clampShift(lo, hi, boundary.min[m], boundary.max[m]);
}
/**
 * Stack strategy.
 *
 * Semantics:
 *   - addItem appends at the tail: new item's `index = currentItems.length`,
 *     `rotation = defaultRotation` (ADR-0060; the auto-rotation search across
 *     alternatives happens at the SlotApi layer, which has bounds access).
 *   - removeItem shifts trailing items left (compacts indices), preserving each
 *     remaining item's per-element overrides and rotation.
 *
 * Layout (`computeItemTransform`) implements ADR-0059 + ADR-0060: the item is
 * rotated about its bbox centre, then the rotated bbox drives edge-anchored
 * packing along `direction`, per-axis cross-alignment (`align`), signed
 * `offset`, inter-item `gap`, and — when `fit` is enabled — a minimal soft
 * clamp into `boundary`. The final transform is `translation · rotation`.
 */
const stackStrategy = {
    kind: 'stack',
    onAdd({ config, currentItems }) {
        const base = { kind: 'stack', index: currentItems.length };
        // Carry the preferred rotation onto the placed item (omit identity to keep
        // serialized placement info clean). Alternatives are searched at add-time
        // by the SlotApi when a boundary is present.
        return isZeroRotation(config.defaultRotation) ? base : { ...base, rotation: config.defaultRotation };
    },
    // Stack is append-only: it has no notion of a target cell. A supplied target
    // is a caller mistake (e.g. addItemAt on a stack slot), so reject it.
    canAdd({ target }) {
        if (target) {
            return { ok: false, reason: 'stack placement appends to the tail and does not accept a target index.' };
        }
        return { ok: true };
    },
    onRemove({ remaining }) {
        const updates = new Map();
        remaining.forEach((item, idx) => {
            // Preserve per-element overrides and rotation; only the index compacts.
            const prev = item.placement?.kind === 'stack' ? item.placement : undefined;
            updates.set(item.id, {
                kind: 'stack',
                index: idx,
                ...(prev?.align && { align: prev.align }),
                ...(prev?.offset && { offset: prev.offset }),
                ...(prev?.rotation && { rotation: prev.rotation }),
            });
        });
        return updates;
    },
    computeItemTransform({ item, config, allItems, itemBounds }) {
        if (item.placement?.kind !== 'stack') {
            return Mat4.makeTranslation(config.anchor);
        }
        const placement = preClampTranslation(item, config, allItems, itemBounds);
        if (!placement) {
            // No bbox for this item — fall back to the accumulated base (no edge
            // anchor possible), mirroring the previous behaviour.
            const { acc } = accumulatePreceding(item.placement.index, config, allItems, itemBounds);
            return Mat4.makeTranslation(addVec(config.anchor, axisVec(config.direction, acc)));
        }
        const { t, bbox, rCenter } = placement;
        if (config.fit && config.boundary) {
            const B = boundaryToAABB(config.boundary);
            const m = config.direction[1];
            // Cross axes: clamp each item independently.
            for (const c of AXES) {
                if (c === m)
                    continue;
                t[c] += clampShift(t[c] + bbox.min[c], t[c] + bbox.max[c], B.min[c], B.max[c]);
            }
            // Main axis: shift the whole block by one uniform delta.
            t[m] += mainAxisBlockDelta(config, allItems, itemBounds, B);
        }
        // Final transform = translation · rotation-about-centre.
        return Mat4.makeTranslation(t).multiply(rCenter);
    },
};

/**
 * Placeholder strategy for `free`.
 *
 * v1 implements `stack` and `list`. `free` round-trips through ISlot.placement
 * (so fixtures and existing tests don't break) but contributes no per-item
 * placement info — `addItem` succeeds and the placed item just doesn't carry a
 * `placement` field. When a real implementation lands, the stub is swapped out
 * via `registerPlacementStrategy`.
 */
function makeStub(kind) {
    return {
        kind,
        onAdd() {
            return undefined;
        },
        canAdd({ target }) {
            // Stubs append without positioning; they have no addressable target.
            if (target) {
                return { ok: false, reason: `placement kind "${kind}" does not support targeted placement.` };
            }
            return { ok: true };
        },
        onRemove() {
            return new Map();
        },
        computeItemTransform() {
            // Stub strategies don't know how to position items. The bounds service
            // surfaces null as a `missingBounds` entry, signalling to the caller
            // that this strategy needs a real implementation (or the slot's items
            // should be measured by other means).
            return null;
        },
    };
}
const freeStub = makeStub('free');
/**
 * Strategy for `bag` — a non-visual collection. A bag never positions items (no
 * geometry, no scene presence), so it carries no per-item placement info and
 * contributes nothing to bounds. `BagSlotApi` drives quantity/cardinality
 * directly and does not route adds through this strategy; the registration
 * exists so shared `SlotApi` paths (`removeItem`, etc.) that look the strategy up
 * by `placement.kind` resolve for bag slots too.
 */
const bagStub = makeStub('bag');

const registry$1 = new Map();
function registerPlacementStrategy(impl) {
    registry$1.set(impl.kind, impl);
}
function getPlacementStrategy(kind) {
    const impl = registry$1.get(kind);
    if (!impl) {
        throw new Error(`No placement strategy registered for kind "${kind}". ` +
            `Known kinds: ${[...registry$1.keys()].join(', ') || '(none)'}.`);
    }
    return impl;
}

// Register the SDK's built-in placement strategies. Importing this module
// causes the registration as a side-effect; the public entry (`src/index.ts`)
// re-exports from here so consumers don't have to think about it.
registerPlacementStrategy(stackStrategy);
registerPlacementStrategy(listStrategy);
registerPlacementStrategy(freeStub);
registerPlacementStrategy(bagStub);

/**
 * Node-kind discriminator passed to providers. Phase 1 only ships the
 * built-in `'static-model'` kind; future kinds (morphed, custom procedural)
 * register their own providers.
 */
const STATIC_MODEL_NODE_KIND = 'static-model';

/**
 * Built-in provider for the common case: a basic-variant pointing at a
 * static Model3d in the catalog. Reads bounds metadata via the catalog and
 * returns either the whole-model bbox, a specific submesh's bbox, or the
 * union of visible submeshes when hide-mesh rules are active.
 *
 * Scope:
 *   - whole-model lookup is the fast path (no submesh walk)
 *   - submesh lookup (`submeshName`) returns one named submesh, or null if
 *     absent (caller surfaces as missingBounds)
 *   - hide-mesh rules (`hiddenMeshes`, Phase 4): bbox = union of visible
 *     submeshes; "hidden = excluded from bbox"
 *
 * Morphing is deferred — see ADR-0058. Joint composition is applied by the
 * caller (composite traversal), not here.
 */
const staticModelBoundsProvider = {
    kind: STATIC_MODEL_NODE_KIND,
    getBounds({ nodeData, catalog }) {
        const meta = catalog.getBasicVariantBounds(nodeData.productId, nodeData.variantId);
        if (!meta)
            return null;
        // Explicit single-submesh selection takes precedence.
        if (nodeData.submeshName !== undefined) {
            return meta.submeshes?.[nodeData.submeshName] ?? null;
        }
        // Hide-mesh rules: union of *visible* submeshes.
        const hidden = nodeData.hiddenMeshes;
        if (hidden && hidden.size > 0) {
            if (!meta.submeshes) {
                // Hide rules exist but no submesh-level metadata to filter against.
                // Fall back to the whole bbox — a conservative overestimate (better a
                // false "doesn't fit" than a false "fits"). The extraction pipeline
                // should emit submeshes whenever a model has hide rules.
                return meta.whole;
            }
            let bbox = emptyAABB();
            for (const [name, sub] of Object.entries(meta.submeshes)) {
                if (hidden.has(name))
                    continue;
                bbox = unionAABB(bbox, sub);
            }
            // bbox may be empty (every submesh hidden) — that's a legitimate result.
            return bbox;
        }
        return meta.whole;
    },
};

const registry = new Map();
function registerBoundsProvider(provider) {
    registry.set(provider.kind, provider);
}
function getBoundsProvider(kind) {
    return registry.get(kind) ?? null;
}

const IDENTITY_MAT4 = Mat4.identity();
const ZERO_ORIGIN = { x: 0, y: 0, z: 0 };
/**
 * Compute the bounding box for a basic-variant or composite-preset input.
 *
 *  - basic-variant: dispatches to the `'static-model'` bounds provider, which
 *    looks up the variant's Model3d bounds via `catalog.getBasicVariantBounds`.
 *
 *  - composite-preset (Phase 3): walks `catalog.getCompositeComponents`,
 *    recursively computes each component's bbox via this same function, and
 *    composes them in the composite's local frame via per-component transforms
 *    from `catalog.resolveComponentTransforms` (joint composition). Slot
 *    children are intentionally deferred to Phase 5 and surfaced as
 *    `missingBounds` so callers can detect "not yet implemented" without an
 *    exception.
 *
 * Never throws on missing data. Provider exceptions are caught and surfaced
 * as `missingBounds[].reason === 'provider-error'`.
 */
function computeBounds(input, catalog) {
    if (input.kind === 'basicVariant') {
        return computeBasicVariantBounds(input.productId, input.variantId, catalog, input.hiddenMeshes);
    }
    return computeCompositeBounds(input.productId, input.state, catalog);
}
/**
 * Build a {@link IBoundsDebugInfo} **tree** mirroring the composite structure
 * for a developer debug overlay: the root product, its components, and its slots
 * with placed items — recursing into nested composites (their components +
 * slots). Every node's `bbox` is expressed in the **root** product's local frame
 * (transforms / slot lifts composed in), and carries the keys/ids needed to
 * resolve its scene node (`componentKey` for components, `itemId` for slot
 * items). Missing-data reasons are flattened into `notes`. Purely diagnostic —
 * never throws.
 */
function computeBoundsDebug(input, catalog) {
    const missing = [];
    let root;
    {
        root = buildCompositeDebugNode('root', String(input.productId), 'root', input.productId, input.state, catalog, IDENTITY_MAT4, missing);
    }
    const notes = missing.map((m) => `${m.kind} "${m.id}": ${m.reason}${m.detail ? ` — ${m.detail}` : ''}`);
    return { root, notes };
}
/**
 * Recursive worker for {@link computeBoundsDebug}. Walks one composite into a
 * debug node: a `basic` child per component, a child per placed slot item
 * (recursing for `compositePreset` items). `frame` maps **this** composite's
 * local frame into the root product's frame, so every emitted `bbox` ends up in
 * root coordinates regardless of nesting depth. Mirrors the geometry of
 * {@link computeCompositeBounds} (same component transforms + slot lifts), so the
 * debug boxes line up with the composed bbox the renderer uses.
 */
function buildCompositeDebugNode(nodeId, label, role, productId, state, catalog, frame, missing) {
    const node = {
        id: nodeId,
        label,
        role,
        productKind: 'composite',
        productId,
        bbox: null,
        children: [],
    };
    const components = catalog.getCompositeComponents(productId);
    if (!components) {
        missing.push({
            kind: 'root',
            id: String(productId),
            reason: 'no-metadata',
            detail: 'composite schema not in catalog',
        });
        return node;
    }
    const transforms = catalog.resolveComponentTransforms(productId, state) ?? new Map();
    let bbox = emptyAABB();
    // Components — each is a basic variant in this composite's local frame.
    for (const component of components) {
        const componentBbox = computeComponentBounds(productId, component.key, component.productId, state, catalog, missing);
        const childId = `${nodeId}/${component.key}`;
        if (componentBbox === null) {
            node.children.push({
                id: childId,
                label: component.key,
                role: 'component',
                productKind: 'basic',
                productId: component.productId,
                componentKey: component.key,
                bbox: null,
                children: [],
            });
            continue;
        }
        const transform = transforms.get(component.key) ?? IDENTITY_MAT4;
        const local = transformAABBByMat4(componentBbox, transform);
        bbox = unionAABB(bbox, local);
        node.children.push({
            id: childId,
            label: component.key,
            role: 'component',
            productKind: 'basic',
            productId: component.productId,
            componentKey: component.key,
            bbox: transformAABBByMat4(local, frame),
            children: [],
        });
    }
    // Slots — each placed item lifted into this composite's frame; composite items
    // recurse so their own components/sub-items appear too.
    const slotsSchema = catalog.getCompositeSlots(productId) ?? [];
    const slotsByKey = new Map(slotsSchema.map((s) => [s.key, s]));
    const bindingCtx = buildSlotBindingContext(state, components, catalog);
    const partToComponent = buildPartToComponent(productId, catalog);
    for (const [slotKey, items] of Object.entries(state.slots ?? {})) {
        const slotConfig = slotsByKey.get(slotKey);
        if (!slotConfig)
            continue;
        const lift = resolveSlotLift(slotConfig.space, bindingCtx, transforms, partToComponent);
        const { layouts, missingBounds } = layoutSlotItems(items, withResolvedBoundary(slotConfig.placement, bindingCtx), catalog);
        for (const m of missingBounds)
            missing.push(m);
        const itemById = new Map(items.map((it) => [it.id, it]));
        for (const l of layouts) {
            const liftedLocal = transformAABBByMat4(l.worldBbox, lift);
            bbox = unionAABB(bbox, liftedLocal);
            const item = itemById.get(l.id);
            const childId = `${nodeId}#${slotKey}:${l.id}`;
            if (item?.product.kind === 'compositePreset' && item.product.configState) {
                // item-local → root frame = frame · slot lift · placement transform.
                const itemFrame = frame.multiply(lift).multiply(l.transform);
                const childNode = buildCompositeDebugNode(childId, l.id, 'slotItem', item.product.productId, item.product.configState, catalog, itemFrame, missing);
                childNode.slotKey = slotKey;
                childNode.itemId = l.id;
                node.children.push(childNode);
            }
            else {
                node.children.push({
                    id: childId,
                    label: l.id,
                    role: 'slotItem',
                    productKind: 'basic',
                    productId: item?.product.productId ?? 0,
                    itemId: l.id,
                    slotKey,
                    bbox: transformAABBByMat4(liftedLocal, frame),
                    children: [],
                });
            }
        }
    }
    node.bbox = isEmpty(bbox) ? null : transformAABBByMat4(bbox, frame);
    return node;
}
function computeBasicVariantBounds(productId, variantId, catalog, hiddenMeshes) {
    const provider = getBoundsProvider(STATIC_MODEL_NODE_KIND);
    if (!provider) {
        throw new Error(`BoundsService: no provider registered for "${STATIC_MODEL_NODE_KIND}". ` +
            `Ensure the bounds module is imported on SDK initialization.`);
    }
    const nodeData = { productId, variantId, hiddenMeshes };
    const missing = [];
    try {
        const result = provider.getBounds({ nodeData, state: null, catalog });
        if (result === null) {
            missing.push({
                kind: 'root',
                id: `${productId}/${variantId}`,
                reason: 'no-metadata',
                detail: 'no bounds metadata on Model3d for this variant',
            });
            return { bbox: emptyAABB(), missingBounds: missing };
        }
        return { bbox: result, missingBounds: missing };
    }
    catch (err) {
        missing.push({
            kind: 'root',
            id: `${productId}/${variantId}`,
            reason: 'provider-error',
            detail: err instanceof Error ? err.message : String(err),
        });
        return { bbox: emptyAABB(), missingBounds: missing };
    }
}
function computeCompositeBounds(productId, state, catalog) {
    const components = catalog.getCompositeComponents(productId);
    if (!components) {
        return {
            bbox: emptyAABB(),
            missingBounds: [
                {
                    kind: 'root',
                    id: String(productId),
                    reason: 'no-metadata',
                    detail: 'composite schema not in catalog',
                },
            ],
        };
    }
    const transforms = catalog.resolveComponentTransforms(productId, state) ?? new Map();
    let bbox = emptyAABB();
    const missing = [];
    for (const component of components) {
        const componentBbox = computeComponentBounds(productId, component.key, component.productId, state, catalog, missing);
        if (componentBbox === null)
            continue;
        const transform = transforms.get(component.key) ?? IDENTITY_MAT4;
        bbox = unionAABB(bbox, transformAABBByMat4(componentBbox, transform));
    }
    // Slot children — recurse for each placed item, then position via the
    // slot's placement strategy. Each slot is additionally lifted into this
    // composite's local frame by its `space` (ADR-0061 / ADR-0062): the resolved
    // origin, and — for `kind:'part'` — the part's transform. Origin expressions
    // resolve against this composite's component bboxes + state (the same binding
    // context the render path uses).
    const slotsSchema = catalog.getCompositeSlots(productId) ?? [];
    const slotsByKey = new Map(slotsSchema.map((s) => [s.key, s]));
    const bindingCtx = buildSlotBindingContext(state, components, catalog);
    const partToComponent = buildPartToComponent(productId, catalog);
    for (const [slotKey, items] of Object.entries(state.slots ?? {})) {
        const slotConfig = slotsByKey.get(slotKey);
        if (!slotConfig) {
            // Slot present in state but absent from the catalog schema — silently
            // skip. This usually means the catalog implementation is incomplete;
            // not a per-item miss.
            continue;
        }
        const lift = resolveSlotLift(slotConfig.space, bindingCtx, transforms, partToComponent);
        const slotBbox = computeSlotBounds(items, withResolvedBoundary(slotConfig.placement, bindingCtx), lift, catalog, missing);
        bbox = unionAABB(bbox, slotBbox);
    }
    return { bbox, missingBounds: missing };
}
/** Build `partKey → componentKey` for a composite (empty if the catalog has no
 *  part support or the product declares none). */
function buildPartToComponent(productId, catalog) {
    const map = new Map();
    for (const part of catalog.getCompositeParts?.(productId) ?? []) {
        map.set(part.partKey, part.componentKey);
    }
    return map;
}
/**
 * Build the binding context for resolving slot `origin` expressions during
 * bounds composition: each component's bbox (keyed by component key) plus the
 * composite state. Mirrors `SlotApi.buildBindingContext` so a slot origin bound
 * to `ctx.bbox["base"]...` resolves to the same value on the bounds path as on
 * the render path. Component bboxes come straight from the catalog's
 * whole-model bounds (untransformed), matching the render-path source.
 */
function buildSlotBindingContext(state, components, catalog) {
    const bbox = {};
    // `product` carries each component's attribute values in the
    // `product[componentKey][attrKey].value` shape the compiled binding fns read.
    // The serialized state only holds variant *refs* (productId + variantId), not
    // attribute values, so we resolve the values from the catalog — otherwise a
    // slot `space.origin` offset authored as a binding expression over component
    // attributes (e.g. the box's horizontal-divider inset
    // `0.5 * (base.external-depth - base.internal-depth) / 1000`) would throw and
    // collapse the whole slot origin to (0,0,0), mispositioning the slot's
    // children far outside the part (ADR-0061 / ADR-0062).
    const product = {};
    for (const component of components) {
        const ref = state.components[component.key];
        if (!ref || ref.id === undefined || ref.id === null)
            continue;
        const meta = catalog.getBasicVariantBounds(component.productId, ref.id);
        if (meta?.whole) {
            const { min, max } = meta.whole;
            bbox[component.key] = {
                min,
                max,
                center: { x: (min.x + max.x) / 2, y: (min.y + max.y) / 2, z: (min.z + max.z) / 2 },
                size: { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z },
            };
        }
        const attributes = catalog.getBasicVariantAttributes?.(component.productId, ref.id);
        if (attributes) {
            const wrapped = {};
            for (const [key, value] of Object.entries(attributes))
                wrapped[key] = { value };
            product[component.key] = wrapped;
        }
    }
    return { product: product, bbox };
}
/**
 * The matrix that lifts a slot's items from the slot-local frame into this
 * composite's frame, per the slot's {@link ISlotSpace}:
 *   - `global` — a pure translation by the resolved origin.
 *   - `part` — the part's transform composed with the origin offset inside the
 *     part frame: `partTransform · T(origin)`, so items move with the part.
 *
 * Origin resolution degrades to the zero vector if an expression can't be
 * evaluated (e.g. a component bbox not yet warmed) and the part transform
 * degrades to identity if the part is unknown — bounds composition never throws.
 */
function resolveSlotLift(space, ctx, transforms, partToComponent) {
    let origin;
    try {
        origin = resolveSpaceOrigin(space, ctx, (partKey) => partToComponent.get(partKey));
    }
    catch {
        origin = ZERO_ORIGIN;
    }
    const originMat = Mat4.makeTranslation(origin);
    if (space?.kind !== 'part')
        return originMat;
    const componentKey = partToComponent.get(space.partKey);
    const partTransform = componentKey ? transforms.get(componentKey) : undefined;
    return partTransform ? partTransform.multiply(originMat) : originMat;
}
/**
 * Resolve every placed item in a slot to its world position. Two-phase walk:
 *   1. Recurse into each placed item to obtain its local bbox.
 *   2. Ask the slot's placement strategy where each item sits in the slot's
 *      local frame, then derive its world bbox.
 *
 * Single source of placement coordinates — shared by bounds composition
 * (`computeSlotBounds`), boundary validation (`validateSlotBoundaries`), and the
 * public `SlotApi.getItemLayout`. Items the strategy can't position (stubs
 * returning `null`) or that have no measurable bbox are excluded from `layouts`
 * and reported via `missingBounds`.
 */
/**
 * Substitute a placement's `boundary` with resolved (numeric) bounds before
 * layout: the strategies' `fit` clamp reads `config.boundary` directly, and
 * `boundaryToAABB` treats an unresolved binding expression as an OPEN side —
 * so laying out with the raw config silently disables fit for expression
 * bounds. Unresolvable bindings degrade to the raw boundary (same policy as
 * validation).
 */
function withResolvedBoundary(placement, ctx) {
    if (!('boundary' in placement))
        return placement;
    const boundary = placement.boundary;
    if (!boundary)
        return placement;
    try {
        return { ...placement, boundary: resolveBoundary(boundary, ctx) };
    }
    catch {
        return placement;
    }
}
function layoutSlotItems(items, placement, catalog) {
    const layouts = [];
    const missing = [];
    if (items.length === 0)
        return { layouts, missingBounds: missing };
    let strategy;
    try {
        strategy = getPlacementStrategy(placement.kind);
    }
    catch (err) {
        for (const item of items) {
            missing.push({
                kind: 'item',
                id: item.id,
                reason: 'provider-error',
                detail: err instanceof Error ? err.message : String(err),
            });
        }
        return { layouts, missingBounds: missing };
    }
    // Phase 1: compute each item's local bbox via recursion.
    const itemBounds = new Map();
    for (const item of items) {
        const itemResult = computeSlotItemBounds(item, catalog);
        // Hoist nested misses, tagging anonymous root misses with the placed
        // item's id so caller diagnostics point at the right place.
        for (const m of itemResult.missingBounds) {
            missing.push(m.kind === 'root' ? { kind: 'item', id: item.id, reason: m.reason, detail: m.detail } : m);
        }
        if (!isEmpty(itemResult.bbox)) {
            itemBounds.set(item.id, itemResult.bbox);
        }
    }
    // Phase 2: position each item via the strategy.
    const placedItems = items.map(serialToRuntime);
    for (const item of placedItems) {
        const local = itemBounds.get(item.id);
        if (!local)
            continue;
        let transform;
        try {
            transform = strategy.computeItemTransform({
                item,
                config: placement, // narrow per kind handled inside strategy
                allItems: placedItems,
                itemBounds,
            });
        }
        catch (err) {
            missing.push({
                kind: 'item',
                id: item.id,
                reason: 'provider-error',
                detail: err instanceof Error ? err.message : String(err),
            });
            continue;
        }
        if (transform === null) {
            missing.push({
                kind: 'item',
                id: item.id,
                reason: 'no-metadata',
                detail: `placement strategy "${placement.kind}" doesn't position items yet`,
            });
            continue;
        }
        const worldBbox = transformAABBByMat4(local, transform);
        if (isEmpty(worldBbox))
            continue;
        layouts.push({ id: item.id, localBbox: local, transform, worldBbox });
    }
    return { layouts, missingBounds: missing };
}
/**
 * Compute the contribution of one slot to the parent composite's bbox: union of
 * every placed item's world bbox (via {@link layoutSlotItems}), each lifted from
 * the slot-local frame into the composite frame by `lift` (the slot's resolved
 * `space` transform — origin, plus part transform for `kind:'part'`).
 *
 * The lift is applied **here** rather than inside {@link layoutSlotItems}
 * because that layout is slot-local by contract — it is shared with boundary
 * validation (origin-independent) and `SlotApi.getItemLayout` (the viewer adds
 * the slot frame separately). Lifting in those paths would double-apply it /
 * break validation; only bounds composition needs the slot frame placed within
 * the composite. See ADR-0061 / ADR-0062.
 */
function computeSlotBounds(items, placement, lift, catalog, missing) {
    const { layouts, missingBounds } = layoutSlotItems(items, placement, catalog);
    for (const m of missingBounds)
        missing.push(m);
    let slotBbox = emptyAABB();
    for (const l of layouts) {
        slotBbox = unionAABB(slotBbox, transformAABBByMat4(l.worldBbox, lift));
    }
    return slotBbox;
}
function computeSlotItemBounds(item, catalog) {
    if (item.product.kind === 'basicVariant') {
        // The serialized placedRef carries `initialVariantId` (current variant at
        // serialize time when api was wired; otherwise placement-time identity).
        // The bounds API has its own internal `variantId` key — wire them up.
        return computeBounds({ kind: 'basicVariant', productId: item.product.productId, variantId: item.product.initialVariantId }, catalog);
    }
    // compositePreset — needs configState to recurse. If absent, surface as a
    // miss (we have no way to materialise default state from outside the SDK).
    if (!item.product.configState) {
        return {
            bbox: emptyAABB(),
            missingBounds: [
                {
                    kind: 'root',
                    id: String(item.product.productId),
                    reason: 'no-metadata',
                    detail: 'placed compositePreset item carries no configState — bounds unmeasurable from snapshot alone',
                },
            ],
        };
    }
    return computeBounds({ kind: 'compositePreset', productId: item.product.productId, state: item.product.configState }, catalog);
}
function serialToRuntime(item) {
    const product = item.product.kind === 'basicVariant'
        ? {
            kind: 'basicVariant',
            productId: item.product.productId,
            initialVariantId: item.product.initialVariantId,
        }
        : { kind: 'compositePreset', productId: item.product.productId };
    return {
        id: item.id,
        slot: item.slot,
        product,
        ...(item.placement ? { placement: item.placement } : {}),
    };
}
/**
 * Resolves bounds for a single component of a composite. Looks up the selected
 * variantId from `state.components[key].id`, recurses through `computeBounds`
 * for the basic-variant lookup, and hoists any nested missing entries up to
 * the caller (re-tagged under this component).
 *
 * Returns the bbox on success or `null` on data gaps (which are recorded in
 * the shared `missing` accumulator). Returning `null` is intentional rather
 * than `BoundsResult`: the caller is already aggregating into a shared bbox
 * and missing array; a separate `BoundsResult` per component would just be
 * unpacked again.
 */
function computeComponentBounds(compositeProductId, componentKey, componentProductId, state, catalog, missing) {
    const variantRef = state.components[componentKey];
    if (!variantRef || variantRef.id === undefined || variantRef.id === null) {
        missing.push({
            kind: 'component',
            id: componentKey,
            reason: 'no-metadata',
            detail: 'no variant selected for this component in state',
        });
        return null;
    }
    // Hide-mesh rules (Phase 4): which of this component's submeshes are hidden
    // given the composite state. Optional catalog method — absent → all visible.
    const hiddenMeshes = catalog.resolveHiddenMeshes?.(compositeProductId, state, componentKey) ?? undefined;
    const result = computeBounds({
        kind: 'basicVariant',
        productId: componentProductId,
        variantId: variantRef.id,
        ...(hiddenMeshes && hiddenMeshes.size > 0 ? { hiddenMeshes } : {}),
    }, catalog);
    // Hoist nested misses, re-tagging the root-level entry as a component miss
    // so the caller's diagnostics point at the offending component, not at the
    // anonymous "root" of a sub-call.
    for (const m of result.missingBounds) {
        if (m.kind === 'root') {
            missing.push({
                kind: 'component',
                id: componentKey,
                reason: m.reason,
                detail: m.detail,
            });
        }
        else {
            missing.push(m);
        }
    }
    return isEmpty(result.bbox) ? null : result.bbox;
}

function validateSlotBoundaries(input, catalog, resolveBoundary) {
    const violations = [];
    const missing = [];
    if (input.kind === 'basicVariant') {
        // Basic variants don't carry slots — nothing to validate.
        return { violations, missingBounds: missing };
    }
    validateCompositeSlots(input.productId, input.state, catalog, violations, missing, resolveBoundary);
    return { violations, missingBounds: missing };
}
function validateCompositeSlots(productId, state, catalog, violations, missing, resolveBoundary) {
    const slotsSchema = catalog.getCompositeSlots(productId);
    if (!slotsSchema)
        return; // not composite or no slots — done
    const slotsByKey = new Map(slotsSchema.map((s) => [s.key, s]));
    const slotEntries = Object.entries(state.slots ?? {});
    for (const [slotKey, items] of slotEntries) {
        const slotConfig = slotsByKey.get(slotKey);
        if (!slotConfig)
            continue;
        const raw = readBoundary(slotConfig.placement);
        const boundary = raw && resolveBoundary ? resolveBoundary(raw, productId) : raw;
        if (boundary) {
            validateSlotItems(productId, slotKey, items, slotConfig.placement, boundary, catalog, violations, missing);
        }
        // Recurse into composite-as-slot-item even if the outer slot has no
        // boundary — inner composites may have their own boundaries.
        for (const item of items) {
            if (item.product.kind === 'compositePreset' && item.product.configState) {
                validateCompositeSlots(item.product.productId, item.product.configState, catalog, violations, missing, resolveBoundary);
            }
        }
    }
}
function validateSlotItems(compositeProductId, slotKey, items, placement, boundary, catalog, violations, missing) {
    // Single source of placement coordinates — same layout the bounds service and
    // the public `SlotApi.getItemLayout` use. Layout with the RESOLVED boundary:
    // the strategies' `fit` clamp reads `config.boundary` directly and treats an
    // unresolved expression as an open side, so validating raw-layout positions
    // would flag items that fit clamps into place.
    const layoutPlacement = 'boundary' in placement && placement.boundary ? { ...placement, boundary } : placement;
    const { layouts, missingBounds } = layoutSlotItems(items, layoutPlacement, catalog);
    for (const m of missingBounds)
        missing.push(m);
    const boundaryAABB = boundaryToAABB(boundary);
    for (const layout of layouts) {
        if (!containsAABB(boundaryAABB, layout.worldBbox)) {
            violations.push({
                compositeProductId,
                slotKey,
                itemId: layout.id,
                boundary: boundaryAABB,
                itemBbox: layout.worldBbox,
            });
        }
    }
}
/**
 * Extract the `boundary` field from any placement strategy that declares one.
 * Returns `null` for strategies without a boundary (linear / grid / and any
 * stack/free without an explicit boundary).
 */
function readBoundary(placement) {
    if ('boundary' in placement && placement.boundary)
        return placement.boundary;
    return null;
}

function resolvePartComponentTransforms(parts, getComponentModel) {
    const identityRoot = (partKey) => ({
        partKey,
        layout: { type: 'root', transform: Mat4.identity() },
    });
    const resolverParts = parts.map((part) => {
        const layout = part.layout;
        if (!layout)
            return identityRoot(part.partKey);
        if (layout.type === 'rootPartLayout') {
            const model = getComponentModel(part.componentKey);
            const transform = model ? layout.modelsTransforms[model.key] : undefined;
            return transform
                ? { partKey: part.partKey, layout: { type: 'root', transform: Mat4.fromTransform(transform) } }
                : identityRoot(part.partKey);
        }
        // childPartLayout — resolve both named joint points; degrade when absent.
        const parent = part.parentPartKey ? parts.find((it) => it.partKey === part.parentPartKey) : undefined;
        const ownModel = getComponentModel(part.componentKey);
        const parentModel = parent ? getComponentModel(parent.componentKey) : null;
        const ownJoint = ownModel?.jointPoints.find((jp) => jp.key === layout.mainJointPointKey)?.jointPoint;
        const parentJoint = parentModel?.jointPoints.find((jp) => jp.key === layout.parentJointPointKey)?.jointPoint;
        if (!part.parentPartKey || !ownJoint || !parentJoint) {
            return identityRoot(part.partKey);
        }
        return {
            partKey: part.partKey,
            layout: { type: 'child', parentPartKey: part.parentPartKey, parentJoint, ownJoint },
        };
    });
    let byPart;
    try {
        byPart = resolvePartLayout(resolverParts);
    }
    catch {
        // Cycle / orphan / invalid joint in draft data — keep the identity behavior.
        return new Map();
    }
    // First part wins for a component with several parts (mirrors the render
    // path, where the component's model is keyed once per part container).
    const result = new Map();
    for (const part of parts) {
        const mat = byPart.get(part.partKey);
        if (mat && !result.has(part.componentKey)) {
            result.set(part.componentKey, mat);
        }
    }
    return result;
}

function variantKey(productId, variantId) {
    return `${productId}/${variantId}`;
}
function viewsOf(spec) {
    return spec?.views ?? [];
}
function createFetcherBoundsCatalog(fetcher, options = {}) {
    const variantBounds = new Map();
    // Per-variant model info (model key + joint points), keyed like
    // `variantBounds` — feeds part-transform resolution (root `modelsTransforms`
    // are indexed by model key; child parts attach via named joint points).
    const variantModels = new Map();
    // Per-variant attribute values (attrKey → plain value), keyed the same way as
    // `variantBounds`. Feeds the `product` binding context for slot-origin
    // expressions during bounds composition (see BoundsService).
    const variantAttributes = new Map();
    const components = new Map();
    const slots = new Map();
    const parts = new Map();
    const warmedComposites = new Set();
    const params = (productId) => ({ ...options, productId });
    async function warmComposite(productId) {
        if (warmedComposites.has(productId))
            return;
        warmedComposites.add(productId); // mark before await — dedupe re-entrant loads
        // Best-effort: bounds are an auxiliary concern, so a fetcher that doesn't
        // serve view3d/variants must never break the caller (e.g. addItem). On
        // failure leave the composite unmapped → downstream surfaces missingBounds.
        try {
            const [variants, view3d, schema] = await Promise.all([
                fetcher.getCompositeProductVariants(params(productId)),
                fetcher.getCompositeView3dSpecification(params(productId)),
                fetcher.getCompositeProductSchema(params(productId)),
            ]);
            const boundsByModel3dId = new Map();
            const modelsByModel3dId = new Map();
            for (const view of viewsOf(view3d)) {
                if (view.model3dBounds)
                    boundsByModel3dId.set(view.id, view.model3dBounds);
                modelsByModel3dId.set(view.id, view);
            }
            const componentList = [];
            for (const [key, lists] of Object.entries(variants.components)) {
                let componentProductId;
                for (const list of lists) {
                    if (!list)
                        continue;
                    if (componentProductId === undefined)
                        componentProductId = list.product.productId;
                    for (const variant of list.variants) {
                        if (variant.attributes) {
                            variantAttributes.set(variantKey(variant.productId, variant.variantId), variant.attributes);
                        }
                        if (variant.model3dId == null)
                            continue;
                        const bounds = boundsByModel3dId.get(variant.model3dId);
                        if (bounds)
                            variantBounds.set(variantKey(variant.productId, variant.variantId), bounds);
                        const model = modelsByModel3dId.get(variant.model3dId);
                        if (model) {
                            variantModels.set(variantKey(variant.productId, variant.variantId), {
                                key: model.key,
                                jointPoints: model.model3dJointPoints ?? [],
                            });
                        }
                    }
                }
                if (componentProductId !== undefined)
                    componentList.push({ key, productId: componentProductId });
            }
            components.set(productId, componentList);
            // Slot-accepted basics carry `model3dId` per variant too (same shape as
            // components); join them into `variantBounds` the same way. Their bounds
            // live in the composite's view3d spec, already collected above.
            for (const lists of Object.values(variants.slots ?? {})) {
                for (const list of lists) {
                    if (!list)
                        continue;
                    for (const variant of list.variants) {
                        if (variant.attributes) {
                            variantAttributes.set(variantKey(variant.productId, variant.variantId), variant.attributes);
                        }
                        if (variant.model3dId == null)
                            continue;
                        const bounds = boundsByModel3dId.get(variant.model3dId);
                        if (bounds)
                            variantBounds.set(variantKey(variant.productId, variant.variantId), bounds);
                    }
                }
            }
            slots.set(productId, 
            // Carry `space` through: bounds composition lifts each slot frame by its
            // resolved origin (and, for `kind:'part'`, the part transform) into the
            // composite-local frame (ADR-0061 / ADR-0062).
            // Bag slots are non-visual (no space, no positioned items) and contribute
            // nothing to bounds, so they are excluded here.
            (schema.slots ?? [])
                .filter((slot) => slot.placement.kind !== 'bag')
                .map((slot) => ({ key: slot.key, placement: slot.placement, space: getSlotSpace(slot.placement) })));
            // Parts come from the view3d spec, not the product schema: the schema
            // endpoint strips `layout` (root modelsTransforms / joint-point keys),
            // which part-transform resolution needs. The schema's stripped parts
            // remain a fallback for partKey → componentKey mapping.
            const view3dParts = view3d?.parts;
            parts.set(productId, view3dParts ?? schema.parts ?? []);
        }
        catch {
            warmedComposites.delete(productId); // allow a later retry
        }
    }
    async function warmState(state) {
        // Only composites need their own warm pass — `warmComposite` already maps
        // bounds for every basic its slots accept (via `variants.slots`), so placed
        // basics need no separate warm. Recurse into nested composite presets.
        for (const items of Object.values(state.slots ?? {})) {
            for (const item of items) {
                if (item.product.kind === 'compositePreset') {
                    await warmComposite(item.product.productId);
                    if (item.product.configState)
                        await warmState(item.product.configState);
                }
            }
        }
    }
    return {
        async warmFor(rootProductId, state) {
            await warmComposite(rootProductId);
            await warmState(state);
        },
        getBasicVariantBounds(productId, variantId) {
            return variantBounds.get(variantKey(productId, variantId)) ?? null;
        },
        getBasicVariantAttributes(productId, variantId) {
            return variantAttributes.get(variantKey(productId, variantId)) ?? null;
        },
        getCompositeComponents(productId) {
            return components.get(productId) ?? null;
        },
        resolveComponentTransforms(productId, state) {
            const componentList = components.get(productId);
            if (!componentList)
                return null;
            const partList = parts.get(productId) ?? [];
            if (!partList.length)
                return new Map();
            return resolvePartComponentTransforms(partList, (componentKey) => {
                const ref = state.components?.[componentKey];
                if (!ref)
                    return null;
                return variantModels.get(variantKey(ref.productId, ref.id)) ?? null;
            });
        },
        getCompositeSlots(productId) {
            return slots.get(productId) ?? null;
        },
        getCompositeParts(productId) {
            return parts.get(productId) ?? null;
        },
    };
}

// Side-effect: register the built-in provider on module load. Mirrors how
// placement strategies wire themselves up.
registerBoundsProvider(staticModelBoundsProvider);

/**
 * Variant prices arrive on the wire still namespaced by price type
 * (`{ USD, PT2/USD }`, ADR-0079 §3.2). `loadAcceptedBasicProducts` hands
 * variants straight to hosts, so it is a public boundary and must project the
 * same way `getPrice`/`getAllVariants` do — a `PT#/` key must never escape a
 * public api. For a catalog without additional types this is the identity.
 */
function projectVariantPrice(variant) {
    const price = variant.meta?.price;
    if (!price)
        return variant;
    return { ...variant, meta: { ...variant.meta, price: projectPrice(price, DEFAULT_PRICE_TYPE_ORDINAL) } };
}
/**
 * Platform-agnostic UUID v4 generator. Prefers Web Crypto's `randomUUID`
 * (available in modern browsers and Node 19+); falls back to a manual
 * RFC 4122 v4 implementation backed by `crypto.getRandomValues` (browsers
 * + Node 14+) or `Math.random` as a last resort.
 *
 * Replaces the previous `import { randomUUID } from 'crypto'` which was
 * Node-only and broke in Vite browser bundles (admin-ui).
 */
function randomUUID() {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi?.randomUUID) {
        return cryptoApi.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (cryptoApi?.getRandomValues) {
        cryptoApi.getRandomValues(bytes);
    }
    else {
        for (let i = 0; i < 16; i++)
            bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
/**
 * Discriminate the placed-api union by a method only present on composite.
 * `serialize` is part of `ICompositeProductApi` but not `IBasicProductApi`,
 * so checking it lets us narrow without dragging in a `kind` field.
 */
function isCompositeProductApi(api) {
    return typeof api.serialize === 'function';
}
/**
 * Discriminate the placed-api union the other way. `getCurrentVariantId` is
 * on `IBasicProductApi` but not `ICompositeProductApi`.
 */
function isBasicProductApi(api) {
    return typeof api.getCurrentVariantId === 'function';
}
const ZERO_ROTATION = { x: 0, y: 0, z: 0 };
function rotationsEqual(a, b) {
    return a.x === b.x && a.y === b.y && a.z === b.z;
}
/**
 * `[defaultRotation, ...rotations]` deduplicated — the allowed menu / search
 * order. Shared by stack (ADR-0060) and list (ADR-0063) placements, which both
 * carry the `{ defaultRotation?, rotations? }` pair.
 */
function rotationMenu(placement) {
    const out = [];
    for (const r of [placement.defaultRotation ?? ZERO_ROTATION, ...(placement.rotations ?? [])]) {
        if (!out.some((o) => rotationsEqual(o, r)))
            out.push(r);
    }
    return out;
}
/** Rebuild a stack placement-info with `rotation` set, omitting it when identity (keeps it clean). */
function stackInfoWithRotation(info, rotation) {
    const base = {
        kind: 'stack',
        index: info.index,
        ...(info.align && { align: info.align }),
        ...(info.offset && { offset: info.offset }),
    };
    return rotation && !rotationsEqual(rotation, ZERO_ROTATION) ? { ...base, rotation } : base;
}
/** List twin of {@link stackInfoWithRotation} (ADR-0063). */
function listInfoWithRotation(info, rotation) {
    const base = {
        kind: 'list',
        index: info.index,
        ...(info.align && { align: info.align }),
        ...(info.offset && { offset: info.offset }),
    };
    return rotation && !rotationsEqual(rotation, ZERO_ROTATION) ? { ...base, rotation } : base;
}
/**
 * Resolves an add-item request against the slot's accepted-items catalog and
 * returns the runtime placed-product reference plus, for compositePreset, the
 * presetId to pass to the placed-api factory. Cross-kind matching rules:
 *   - `compositePreset` request → matches accepted `compositePreset` with the
 *     same `(productId, presetId)` pair; presetId alone is enough (UUID is
 *     globally unique) — productId is checked for fast-path consistency
 *   - `basicProduct` request → matches accepted `basicProduct` + productId;
 *     `variantId` resolved from `accepted.defaultVariantId` (throws if absent)
 *   - `basicVariant` request → matches accepted `basicProduct` + productId;
 *     caller's `variantId` is used unconditionally (not constrained by default)
 *
 * `placedRef` is the runtime ref stored on the placed item; it intentionally
 * omits `presetId` (placed identity is `productId + state`). `presetId` (only
 * for compositePreset) is returned separately so addItem can fire the preset
 * fetch via the factory.
 *
 * Returns `null` if no accepted entry matches.
 */
function resolveRequest(request, acceptedItems, slotName) {
    if (request.kind === 'compositePreset') {
        const match = acceptedItems.find((a) => a.kind === 'compositePreset' && a.productId === request.productId && a.presetId === request.presetId);
        if (!match)
            return null;
        return {
            placedRef: { kind: 'compositePreset', productId: request.productId },
            presetId: request.presetId,
        };
    }
    // Both basicProduct and basicVariant requests match against basicProduct accepted entries.
    const match = acceptedItems.find((a) => a.kind === 'basicProduct' && a.productId === request.productId);
    if (!match || match.kind !== 'basicProduct')
        return null;
    if (request.kind === 'basicVariant') {
        return {
            placedRef: { kind: 'basicVariant', productId: request.productId, initialVariantId: request.variantId },
        };
    }
    // basicProduct request — resolve via defaultVariantId
    if (match.defaultVariantId === undefined) {
        throw new Error(`Slot "${slotName}": basicProduct request for productId ${request.productId} requires ` +
            `the accepted entry to declare a defaultVariantId, or use a basicVariant request to ` +
            `pick a specific variant.`);
    }
    return {
        placedRef: { kind: 'basicVariant', productId: request.productId, initialVariantId: match.defaultVariantId },
    };
}
/**
 * True if a placed item's product reference is still covered by `acceptedItems`.
 * Placement strips the originating presetId (placed identity is `productId +
 * state`), so a placed compositePreset matches any accepted compositePreset with
 * the same productId; a placed basicVariant matches any accepted basicProduct
 * with the same productId.
 */
function isPlacedItemAccepted(product, acceptedItems) {
    if (product.kind === 'compositePreset') {
        return acceptedItems.some((a) => a.kind === 'compositePreset' && a.productId === product.productId);
    }
    return acceptedItems.some((a) => a.kind === 'basicProduct' && a.productId === product.productId);
}
class SlotApi {
    slot;
    items;
    // Holds the placed api (composite or basic) keyed by placed-item id. The
    // union covers both placement kinds; callers narrow via `getItems()[].product.kind`
    // or by checking which methods are present.
    placedApis = new Map();
    /**
     * Provenance (ADR-0066): which preset each placed compositePreset item was
     * stamped from. The runtime placedRef intentionally omits presetId (placed
     * identity is productId + state); this side map lets serialize() emit the
     * non-load-bearing `fromPreset` tag for release-time cross-checks.
     */
    placedPresetIds = new Map();
    placedApiFactory;
    placedBasicApiFactory;
    presetLoader;
    basicProductLoader;
    // Accepted-basic variant lists (with `model3dId`) for this slot, indexed by
    // productId. Source of truth for `loadAcceptedBasicProducts` and the placed
    // basic api's per-variant model3d map. Empty when no `slotVariants` wired.
    slotVariantsByProductId = new Map();
    // Cache one in-flight or settled fetch per presetId. Keyed by presetId
    // because UUIDs are globally unique — productId is just a fast-path hint
    // and would duplicate cache entries if used as part of the key.
    presetCache = new Map();
    // Cache one in-flight or settled basic-product fetch per productId. Keyed
    // by productId because basicProduct accepted entries are uniquely identified
    // by productId within a slot (defaultVariantId is metadata, not identity).
    basicProductCache = new Map();
    // Bounds wiring (SDK-owned). `serializeParent` is set after the parent
    // composite api exists (it closes over it), so it lands via a setter.
    boundsCatalog;
    parentProductId;
    getCompositeState;
    // partKey → componentKey for the parent composite, so a `space: 'part'` slot
    // resolves its origin against the named part's own bbox.
    partToComponent;
    serializeParent;
    constructor(slot, options = {}) {
        this.slot = slot;
        this.items = [];
        this.placedApiFactory = options.placedApiFactory;
        this.placedBasicApiFactory = options.placedBasicApiFactory;
        this.presetLoader = options.presetLoader;
        this.basicProductLoader = options.basicProductLoader;
        for (const list of options.slotVariants ?? []) {
            if (list)
                this.slotVariantsByProductId.set(list.product.productId, list);
        }
        this.boundsCatalog = options.boundsCatalog;
        this.parentProductId = options.parentProductId;
        this.getCompositeState = options.getCompositeState;
        this.partToComponent = new Map((options.parts ?? []).map((p) => [p.partKey, p.componentKey]));
    }
    /**
     * The accepted items active for the parent composite's *current* state. For a
     * static `acceptedItems` list this is just that list. For a conditional list
     * (state-dependent groups) it evaluates the conditions against the live
     * composite state and returns the first matching group's items.
     *
     * Every accepted-items consumer (`getAcceptedItems`, the loaders, placement
     * resolution, boundary look-ahead) goes through here so they all see the same
     * state-resolved view. Throws if the slot is conditional but no
     * `getCompositeState` was wired (production builders always wire it; tests
     * using conditional slots must opt in).
     */
    resolveAcceptedItems() {
        const acceptedItems = this.slot.acceptedItems;
        if (!isConditionalAcceptedItems(acceptedItems)) {
            return acceptedItems;
        }
        if (!this.getCompositeState) {
            throw new Error(`Slot "${this.slot.name}" has conditional acceptedItems but was constructed without ` +
                `getCompositeState; cannot resolve the active list. (Production builders wire this automatically.)`);
        }
        return resolveAcceptedSlotItems(acceptedItems, this.getCompositeState());
    }
    /**
     * Wire the callback that snapshots the parent composite's state. Called by the
     * builder once the composite api exists (it can't be passed at construction
     * because the slot is built first). Enables `canAddWithinBounds` and the
     * post-add auto-warm.
     */
    setSerializeParent(serializeParent) {
        this.serializeParent = serializeParent;
    }
    getName() {
        return this.slot.name;
    }
    getCardinality() {
        return this.slot.cardinality;
    }
    getAcceptedItems() {
        return [...this.resolveAcceptedItems()];
    }
    getPlacement() {
        return this.slot.placement;
    }
    /** A slot is required when it must hold at least one item — derived from cardinality. */
    isRequired() {
        return this.slot.cardinality.min >= 1;
    }
    getDescription() {
        return this.slot.description;
    }
    /**
     * Build the discriminated `{ composite, api }` bundle for a stored item. The
     * discriminant is derived from `item.product.kind`; the api is looked up from
     * `placedApis`. The map stores the broad `ICompositeProductApi |
     * IBasicProductApi` union, but the runtime invariant guarantees the stored api
     * matches the placement kind (composite factory → compositePreset, basic
     * factory → basicVariant), so the cast is sound. `?? null` keeps `api` total
     * for the test/legacy no-factory path.
     */
    /**
     * Per-variant `variantId → model3dId` map for an accepted basic product,
     * read from `slotVariants`. Threaded into the placed basic api so its
     * `_model3dId` stays correct across attribute navigation. Empty when no
     * `slotVariants` was wired or the product isn't among the accepted basics.
     */
    model3dByVariantIdFor(productId) {
        const list = this.slotVariantsByProductId.get(productId);
        if (!list)
            return {};
        const map = {};
        for (const variant of list.variants)
            map[variant.variantId] = variant.model3dId;
        return map;
    }
    placedApiBundle(item) {
        const api = this.placedApis.get(item.id) ?? null;
        return item.product.kind === 'compositePreset'
            ? { composite: true, api: api }
            : { composite: false, api: api };
    }
    getItems() {
        // Enrich each stored placed item with its discriminated live api. Every item
        // reaches the slot through `addItem` (directly or replayed by
        // `CompositeProductApi.applySerializedState`), which populates `placedApis`,
        // so the lookup resolves for all of them.
        return this.items.map((item) => ({ ...item, ...this.placedApiBundle(item) }));
    }
    /**
     * Resolve every `compositePreset` accepted item to its full preset DTO.
     *
     * `acceptedItems` is intentionally identity-only (productId + presetId) — it
     * doesn't carry preset state. To filter accepted presets by content
     * (footprint, version, etc.) the caller needs the full DTO; this helper
     * batches the fetches in parallel and caches them per-instance so a second
     * call (or a re-render) doesn't re-hit the network.
     *
     * `basicProduct` entries are skipped — they have no preset to load. The
     * result preserves the order of accepted compositePreset entries.
     *
     * Cross-slot filtering (e.g. "only boxes whose depth ≤ tray.depth") lives in
     * the caller's code by design — see ADR-0055 § Non-goals on rules.
     *
     * Throws if no `presetLoader` was wired (production builders always wire
     * one; tests opt in via `SlotApiOptions.presetLoader`).
     */
    async loadAcceptedPresets() {
        if (!this.presetLoader) {
            throw new Error(`Slot "${this.slot.name}" was constructed without a presetLoader; ` +
                `cannot loadAcceptedPresets. (Production builders wire this automatically.)`);
        }
        const composites = this.resolveAcceptedItems().filter((a) => a.kind === 'compositePreset');
        const loader = this.presetLoader;
        return Promise.all(composites.map(async (accepted) => {
            const existing = this.presetCache.get(accepted.presetId);
            // Cache the *promise* so concurrent callers dedupe naturally; on
            // rejection drop the entry so a retry can try again.
            const pending = existing ?? loader(accepted.productId, accepted.presetId);
            if (!existing) {
                this.presetCache.set(accepted.presetId, pending);
                pending.catch(() => this.presetCache.delete(accepted.presetId));
            }
            return { accepted, preset: await pending };
        }));
    }
    /**
     * Resolve every `basicProduct` accepted item to its full product + variants
     * data. Symmetric to `loadAcceptedPresets()`: batches the fetches in
     * parallel, caches per-instance by productId so repeat calls (or a
     * re-render) don't re-hit the network, and evicts failed fetches so a retry
     * can try again. `compositePreset` entries are skipped. Preserves the order
     * of accepted basicProduct entries.
     *
     * Prefers the composite's slot-variant data (`slotVariants`, which carries
     * `model3dId`) when wired; falls back to `basicProductLoader` (`model3dId:
     * null`) for any accepted product not covered by it. Throws only if a
     * fallback is needed but no loader was wired (production builders always wire
     * one; tests opt in via `SlotApiOptions.basicProductLoader`).
     */
    async loadAcceptedBasicProducts() {
        const basics = this.resolveAcceptedItems().filter((a) => a.kind === 'basicProduct');
        return Promise.all(basics.map(async (accepted) => {
            const fromSlot = this.slotVariantsByProductId.get(accepted.productId);
            if (fromSlot) {
                return { accepted, product: fromSlot.product, variants: fromSlot.variants.map(projectVariantPrice) };
            }
            if (!this.basicProductLoader) {
                throw new Error(`Slot "${this.slot.name}" was constructed without a basicProductLoader; ` +
                    `cannot loadAcceptedBasicProducts. (Production builders wire this automatically.)`);
            }
            const loader = this.basicProductLoader;
            const existing = this.basicProductCache.get(accepted.productId);
            // Cache the *promise* so concurrent callers dedupe naturally; on
            // rejection drop the entry so a retry can try again.
            const pending = existing ?? loader(accepted.productId);
            if (!existing) {
                this.basicProductCache.set(accepted.productId, pending);
                pending.catch(() => this.basicProductCache.delete(accepted.productId));
            }
            const { product, variants } = await pending;
            return { accepted, product, variants: variants.map(projectVariantPrice) };
        }));
    }
    /**
     * Shared placement pipeline for all strategy subclasses. Resolves the request
     * against `acceptedItems`, enforces `cardinality.max`, delegates placement-info
     * computation to the slot's strategy (passing `capacity` and an optional
     * `target` so index-based strategies like `list` can validate/assign a cell),
     * builds the placed api, and records the item.
     *
     * Subclasses expose the strategy-appropriate public surface: `StackSlotApi`/
     * `FreeSlotApi` call this from `addItem` (no target); `ListSlotApi` calls it
     * from `addItemAt` (with `{ index }`).
     */
    async placeItem(request, options, target) {
        const acceptedItems = this.resolveAcceptedItems();
        const resolved = resolveRequest(request, acceptedItems, this.slot.name);
        if (!resolved) {
            throw new Error(`Slot "${this.slot.name}" does not accept item ${JSON.stringify(request)}. ` +
                `Allowed items: ${JSON.stringify(acceptedItems)}.`);
        }
        const { placedRef: product, presetId } = resolved;
        const max = this.slot.cardinality.max;
        if (max !== 'unbounded' && this.items.length >= max) {
            throw new Error(`Slot "${this.slot.name}" is full (cardinality.max = ${max}, current items: ${this.items.length}).`);
        }
        if (options?.initialState && request.kind !== 'compositePreset') {
            throw new Error(`Slot "${this.slot.name}": initialState is only supported for compositePreset placements.`);
        }
        const id = randomUUID();
        const strategy = getPlacementStrategy(this.slot.placement.kind);
        const placementInfo = strategy.onAdd({
            config: this.slot.placement,
            currentItems: this.items,
            capacity: max,
            target,
        });
        const placed = {
            id,
            slot: this.slot.key,
            product,
            ...(placementInfo ? { placement: placementInfo } : {}),
        };
        await this.instantiatePlacedApi(id, product, presetId, options);
        this.items.push(placed);
        // Auto-warm the SDK-owned catalog for the new state: the just-placed item
        // may introduce a product the catalog hasn't loaded yet. Cached by
        // productId, so re-adding a known product is essentially free. removeItem
        // and variant changes need no warm — `warmComposite` loads a composite's
        // whole variant space at once.
        if (this.boundsCatalog && this.parentProductId !== undefined && this.serializeParent) {
            await this.boundsCatalog.warmFor(this.parentProductId, this.serializeParent(0));
        }
        // ADR-0060: pick the rotation that fits the boundary (default first, then
        // alternatives). Runs after the warm so the look-ahead sees the new item.
        await this.autoResolveRotation(placed);
        return { ...placed, ...this.placedApiBundle(placed) };
    }
    /**
     * Instantiate a placed product's own configuring api (composite preset or
     * basic variant) and record it in the placed-api maps. Shared by the spatial
     * {@link placeItem} pipeline and {@link BagSlotApi.addItem}. Returns `null`
     * when the product kind needs no api or no factory is wired (test/legacy).
     */
    async instantiatePlacedApi(id, product, presetId, options) {
        let api = null;
        if (product.kind === 'compositePreset') {
            if (!this.placedApiFactory) {
                throw new Error(`Slot "${this.slot.name}" was constructed without a placedApiFactory; ` +
                    `cannot place compositePreset (productId ${product.productId}).`);
            }
            // presetId is guaranteed present for compositePreset by resolveRequest.
            // When a runtime initialState follows, the factory skips hydrating the
            // preset's own stored slot children (ADR-0066) — the snapshot wins.
            api = await this.placedApiFactory(product.productId, presetId, {
                skipPresetStateHydration: Boolean(options?.initialState),
            });
            if (options?.initialState) {
                await api.applySerializedState(options.initialState);
            }
            this.placedApis.set(id, api);
            this.placedPresetIds.set(id, presetId);
        }
        else if (product.kind === 'basicVariant' && this.placedBasicApiFactory) {
            // Build a placed basic api initialized at the placed variantId. Symmetric
            // to the composite-preset path: independent instance (own LogicEngine
            // and model). `product.initialVariantId` stays as the placement-time
            // identity; the api's model carries the current state on subsequent
            // setAttribute calls (read back via `getCurrentVariantId` at serialize
            // time). If no factory is wired (test/legacy), `api` stays `null` —
            // backward-compat with the Phase B1 contract.
            api = await this.placedBasicApiFactory(product.productId, product.initialVariantId, this.model3dByVariantIdFor(product.productId));
            this.placedApis.set(id, api);
        }
        return api;
    }
    /**
     * Snapshot the slot's current occupancy + placed composite preset state.
     * Used by CompositeProductApi.serialize to build the recursive composite
     * tree snapshot.
     */
    serialize(version) {
        return this.items.map((item) => {
            const placedApi = this.placedApis.get(item.id);
            const product = item.product.kind === 'basicVariant'
                ? {
                    kind: 'basicVariant',
                    productId: item.product.productId,
                    // Capture the api's current variant when a placed basic api is
                    // wired (production builders always wire one) so subsequent
                    // setAttributeValue navigation survives the round-trip. When the
                    // api isn't wired, fall back to the placedRef's frozen
                    // initialVariantId (Phase B1 behaviour).
                    initialVariantId: placedApi && isBasicProductApi(placedApi)
                        ? placedApi.getCurrentVariantId() ?? item.product.initialVariantId
                        : item.product.initialVariantId,
                }
                : {
                    kind: 'compositePreset',
                    productId: item.product.productId,
                    // Narrow: only composite placed apis carry slot/configState.
                    ...(placedApi && isCompositeProductApi(placedApi) ? { configState: placedApi.serialize(version) } : {}),
                    // Provenance only (ADR-0066): records which preset stamped this
                    // child. Not load-bearing — configState is the state of record.
                    ...(this.placedPresetIds.has(item.id)
                        ? { fromPreset: { productId: item.product.productId, presetId: this.placedPresetIds.get(item.id) } }
                        : {}),
                };
            return {
                id: item.id,
                slot: item.slot,
                product,
                ...(item.placement ? { placement: item.placement } : {}),
                ...(item.amount !== undefined ? { amount: item.amount } : {}),
            };
        });
    }
    removeItem(itemId) {
        const index = this.items.findIndex((it) => it.id === itemId);
        if (index === -1) {
            throw new Error(`Slot "${this.slot.name}" has no placed item with id "${itemId}".`);
        }
        const [removed] = this.items.splice(index, 1);
        this.placedApis.delete(itemId);
        this.placedPresetIds.delete(itemId);
        const strategy = getPlacementStrategy(this.slot.placement.kind);
        const updates = strategy.onRemove({
            config: this.slot.placement,
            removed,
            remaining: this.items,
        });
        if (updates.size > 0) {
            for (const item of this.items) {
                const next = updates.get(item.id);
                if (next) {
                    item.placement = next;
                }
            }
        }
    }
    getItemApi(itemId) {
        const item = this.items.find((it) => it.id === itemId);
        if (!item) {
            throw new Error(`Slot "${this.slot.name}" has no placed item with id "${itemId}".`);
        }
        return this.placedApiBundle(item);
    }
    getKey() {
        return this.slot.key;
    }
    /**
     * The coordinate frame the slot lives in (default `{ kind: 'global' }`). For a
     * `part` space the viewer parents the slot's node under that part's node so it
     * inherits the part's transform; the SDK (binding resolution, validation) is
     * space-agnostic. See {@link ISlotSpace}.
     */
    getSpace() {
        return getSlotSpace(this.slot.placement) ?? { kind: 'global' };
    }
    /**
     * The slot's frame origin within its {@link ISlotSpace}, resolved against
     * current state (axis expressions evaluated; missing axes → 0). For a `part`
     * space the origin anchors to the named part's own bbox. A render-time offset
     * only — validation is origin-independent. Returns `(0,0,0)` when no origin is
     * declared or no state is wired.
     */
    getOrigin() {
        return resolveSpaceOrigin(getSlotSpace(this.slot.placement), this.buildBindingContext(), (partKey) => this.partToComponent.get(partKey));
    }
    /**
     * Editor-facing (ADR-0071): overwrite parts of the slot definition in
     * memory so the Layout page can live-preview unsaved edits without an SDK
     * reload. The placement `kind` cannot change — the strategy class is fixed
     * at construction — so a kind-changing placement patch is dropped (the
     * caller falls back to save-and-reload). Placed items are untouched.
     */
    patchDefinition(patch) {
        const { placement, ...rest } = patch;
        Object.assign(this.slot, rest);
        if (placement && placement.kind === this.slot.placement.kind) {
            this.slot.placement = placement;
        }
    }
    /**
     * The placement boundary with bound expressions resolved against current
     * state; unresolvable bindings pass through unchanged (same degradation as
     * the validation resolver). `undefined` when the strategy has no boundary.
     * Editor-facing (ADR-0071 slot visualization on the Layout page).
     */
    getResolvedBoundary() {
        const placement = this.slot.placement;
        const boundary = placement && 'boundary' in placement ? placement.boundary : undefined;
        if (!boundary)
            return undefined;
        try {
            return resolveBoundary(boundary, this.buildBindingContext());
        }
        catch {
            return boundary;
        }
    }
    /**
     * Build the evaluation context for slot-geometry bindings: component attribute
     * values (`product`, from the live composite model) and each component's
     * current bounding box (`bbox`, from the SDK bounds catalog). Both default to
     * empty when their wiring is absent (test/legacy) so static geometry still
     * resolves.
     */
    buildBindingContext() {
        const product = this.getCompositeState ? this.getCompositeState() : {};
        const bbox = {};
        if (this.boundsCatalog && this.serializeParent) {
            const components = this.serializeParent(0).components;
            for (const [key, comp] of Object.entries(components)) {
                if (!comp)
                    continue;
                const meta = this.boundsCatalog.getBasicVariantBounds(comp.productId, comp.id);
                if (!meta?.whole)
                    continue;
                const { min, max } = meta.whole;
                bbox[key] = {
                    min,
                    max,
                    center: { x: (min.x + max.x) / 2, y: (min.y + max.y) / 2, z: (min.z + max.z) / 2 },
                    size: { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z },
                };
            }
        }
        return { product, bbox };
    }
    /**
     * Boundary resolver passed to the slot-boundary validator: resolves bound
     * expressions for the owning (parent) composite against live state, and leaves
     * other composites' boundaries untouched (only the parent composite's state is
     * available here — nested composites use their static boundaries).
     */
    boundaryResolver = (boundary, compositeProductId) => {
        if (compositeProductId !== this.parentProductId)
            return boundary;
        try {
            return resolveBoundary(boundary, this.buildBindingContext());
        }
        catch {
            // A binding that can't resolve (e.g. a component bbox not yet warmed)
            // degrades to the unresolved boundary, which `boundaryToAABB` treats as
            // open — never a false violation, and never throws in the reactive path.
            return boundary;
        }
    };
    getInvalidItems() {
        const accepted = this.resolveAcceptedItems();
        const overflow = this.boundaryInvalidItemIds();
        return this.getItems().filter((item) => !isPlacedItemAccepted(item.product, accepted) || overflow.has(item.id));
    }
    /**
     * Ids of items in this slot whose placed bbox falls outside the slot's
     * *resolved* boundary in the current state. Empty when bounds wiring is absent
     * (the boundary check is skipped). Runs synchronously against the already-warm
     * catalog — a tray-attribute change introduces no new product.
     */
    boundaryInvalidItemIds() {
        if (!this.boundsCatalog || this.parentProductId === undefined || !this.serializeParent) {
            return new Set();
        }
        const { violations } = validateSlotBoundaries({ kind: 'compositePreset', productId: this.parentProductId, state: this.serializeParent(0) }, this.boundsCatalog, this.boundaryResolver);
        return new Set(violations.filter((v) => v.slotKey === this.slot.key).map((v) => v.itemId));
    }
    /** True when the SDK-owned bounds wiring is present (production builders wire it). */
    hasBoundsWiring() {
        return !!this.boundsCatalog && this.parentProductId !== undefined && !!this.serializeParent;
    }
    /** Whether the placed item currently fits its slot boundary (true if bounds aren't wired). */
    itemFitsBoundary(itemId) {
        return !this.boundaryInvalidItemIds().has(itemId);
    }
    /**
     * ADR-0060 auto-rotation. When the (stack) slot declares alternative
     * `rotations` and a `boundary`, pick the first menu angle (default first,
     * then the alternatives in order) whose placed item fits the boundary, and
     * store it on the item. If none fit, the preferred (default) rotation is kept
     * and the residual overflow is surfaced reactively by the boundary validator
     * (consistent with ADR-0059 fit). No-op without bounds wiring or alternatives.
     *
     * The product is unchanged across candidates, so the catalog stays warm and
     * the per-candidate containment check runs synchronously.
     */
    async autoResolveRotation(placed) {
        const placement = this.slot.placement;
        const info = placed.placement;
        const log = (msg, ...rest) => console.debug(`[clp-sdk][stack-auto-rotate][slot:${this.slot.key}][item:${placed.id}] ${msg}`, ...rest);
        if (placement.kind !== 'stack' || info?.kind !== 'stack') {
            log('skip: not a stack placement', { placementKind: placement.kind, infoKind: info?.kind });
            return;
        }
        if ((placement.rotations?.length ?? 0) === 0 || !placement.boundary) {
            log('skip: no rotation alternatives or no boundary', {
                rotations: placement.rotations?.length ?? 0,
                hasBoundary: !!placement.boundary,
            });
            return;
        }
        if (!this.hasBoundsWiring()) {
            log('skip: bounds wiring absent');
            return;
        }
        const preferred = info.rotation;
        const menu = rotationMenu(placement);
        log('resolving rotation', { preferred, candidates: menu, boundary: placement.boundary });
        for (const rotation of menu) {
            placed.placement = stackInfoWithRotation(info, rotation);
            // Fit against the WHOLE slot, not just this item. With a `fit` placement,
            // a rotation can leave this item inside its own footprint while the
            // whole-block clamp (ADR-0059) pushes the overflow onto a leading item —
            // `itemFitsBoundary(placed.id)` would then report a false FIT and store a
            // rotation `canAddWithinBounds` rejects. Keep the two searches in sync.
            const fits = this.boundaryInvalidItemIds().size === 0;
            log(`try rotation ${JSON.stringify(rotation)} -> ${fits ? 'FITS' : 'overflow'}`);
            if (fits) {
                log('picked rotation (first fitting angle wins)', { rotation });
                return; // first fitting angle wins
            }
        }
        // None fit — restore the preferred rotation (reactive overflow).
        log('no candidate fits — restoring preferred rotation (reactive overflow)', { preferred });
        placed.placement = stackInfoWithRotation(info, preferred);
    }
    getItemLayout() {
        const catalog = this.requireBoundsWiring().catalog;
        // Layout with the RESOLVED boundary: the strategies' `fit` clamp reads
        // `config.boundary` directly and treats an unresolved binding expression
        // as an open side — raw config would silently disable fit for
        // expression-bound axes.
        const placement = this.slot.placement;
        const layoutPlacement = 'boundary' in placement && placement.boundary
            ? { ...placement, boundary: this.getResolvedBoundary() }
            : placement;
        // Version is geometry-irrelevant here — it only tags the serialized snapshot.
        const { layouts } = layoutSlotItems(this.serialize(0), layoutPlacement, catalog);
        const result = new Map();
        for (const layout of layouts) {
            result.set(layout.id, { transform: layout.transform, worldBbox: layout.worldBbox });
        }
        return result;
    }
    async canAddWithinBounds(request, options) {
        const { catalog, parentProductId, serializeParent } = this.requireBoundsWiring();
        const log = (msg, ...rest) => console.debug(`[clp-sdk][stack-auto-rotate][slot:${this.slot.key}][look-ahead] ${msg}`, ...rest);
        if (options?.initialState && request.kind !== 'compositePreset') {
            return {
                ok: false,
                reason: `Slot "${this.slot.name}": initialState is only supported for compositePreset placements.`,
            };
        }
        const max = this.slot.cardinality.max;
        if (max !== 'unbounded' && this.items.length >= max) {
            return { ok: false, reason: `Slot "${this.slot.name}" is full (cardinality.max = ${max}).` };
        }
        const resolved = resolveRequest(request, this.resolveAcceptedItems(), this.slot.name);
        if (!resolved) {
            return { ok: false, reason: `Slot "${this.slot.name}" does not accept item ${JSON.stringify(request)}.` };
        }
        const parentState = serializeParent(0);
        const prospective = await this.buildProspectiveItem(resolved, parentState.version, options);
        const futureWith = (item) => ({
            ...parentState,
            slots: {
                ...(parentState.slots ?? {}),
                [this.slot.key]: [...(parentState.slots?.[this.slot.key] ?? []), item],
            },
        });
        // Warm once — the product set is identical across candidate rotations.
        await catalog.warmFor(parentProductId, futureWith(prospective));
        // Mirror ADR-0060 auto-rotation: the add succeeds if the item fits at *any*
        // allowed rotation (default first, then alternatives). Non-stack or
        // rotation-less stacks fall back to the single prospective orientation, so
        // this is a no-op for them.
        const placement = this.slot.placement;
        const stackInfo = prospective.placement?.kind === 'stack' ? prospective.placement : undefined;
        const candidates = placement.kind === 'stack' && stackInfo ? rotationMenu(placement) : [undefined];
        log('testing prospective add', {
            itemId: prospective.id,
            isStack: !!stackInfo,
            candidates,
        });
        for (const rotation of candidates) {
            const item = rotation && stackInfo ? { ...prospective, placement: stackInfoWithRotation(stackInfo, rotation) } : prospective;
            const { violations } = validateSlotBoundaries({ kind: 'compositePreset', productId: parentProductId, state: futureWith(item) }, catalog, this.boundaryResolver);
            // Fit against ANY item in this slot, not just the prospective one. With a
            // `fit` stack placement, an over-capacity add doesn't push the new tail
            // item past the boundary — the whole block's uniform clamp (ADR-0059,
            // `mainAxisBlockDelta`) shifts the overflow onto a *leading* item instead.
            // Keying the check on the prospective item's id alone (`v.itemId === item.id`)
            // therefore misses that violation and lets unlimited boxes be added.
            const fits = !violations.some((v) => v.slotKey === this.slot.key);
            log(`try rotation ${JSON.stringify(rotation)} -> ${fits ? 'FITS' : 'overflow'}`);
            if (fits) {
                log('can add: first fitting angle wins', { rotation });
                return { ok: true };
            }
        }
        log('cannot add: no candidate fits the slot boundary');
        return { ok: false, reason: `Adding this item would exceed the "${this.slot.name}" slot boundary.` };
    }
    /**
     * Assert the SDK-owned bounds wiring is present (production builders wire it;
     * standalone/test slots may not). Returns the catalog + parent context for the
     * boundary-aware methods.
     */
    requireBoundsWiring() {
        if (!this.boundsCatalog || this.parentProductId === undefined || !this.serializeParent) {
            throw new Error(`Slot "${this.slot.name}" was constructed without bounds wiring; ` +
                `getItemLayout / canAddWithinBounds need a bounds catalog. (Production builders wire this automatically.)`);
        }
        return {
            catalog: this.boundsCatalog,
            parentProductId: this.parentProductId,
            serializeParent: this.serializeParent,
        };
    }
    /**
     * Build the serialized placed item a prospective `addItem` would create —
     * without mutating the slot. A compositePreset item gets its default state by
     * spinning up a throwaway placed api and serializing it (the same path
     * `addItem` uses), so look-ahead measures exactly what would be placed. When
     * the prospective add carries an `initialState` snapshot (e.g. "add a copy of
     * the last box"), it's replayed onto the throwaway api before serializing —
     * mirroring {@link instantiatePlacedApi} — so the look-ahead measures the copy's
     * geometry, not the preset default.
     */
    async buildProspectiveItem(resolved, version, options) {
        const { placedRef, presetId } = resolved;
        const placement = getPlacementStrategy(this.slot.placement.kind).onAdd({
            config: this.slot.placement,
            currentItems: this.items,
            capacity: this.slot.cardinality.max,
        });
        const base = { id: randomUUID(), slot: this.slot.key, ...(placement ? { placement } : {}) };
        if (placedRef.kind === 'compositePreset') {
            if (!this.placedApiFactory) {
                throw new Error(`Slot "${this.slot.name}" was constructed without a placedApiFactory; ` +
                    `cannot evaluate boundary for compositePreset (productId ${placedRef.productId}).`);
            }
            const api = await this.placedApiFactory(placedRef.productId, presetId, {
                skipPresetStateHydration: Boolean(options?.initialState),
            });
            if (options?.initialState) {
                await api.applySerializedState(options.initialState);
            }
            return {
                ...base,
                product: { kind: 'compositePreset', productId: placedRef.productId, configState: api.serialize(version) },
            };
        }
        return {
            ...base,
            product: { kind: 'basicVariant', productId: placedRef.productId, initialVariantId: placedRef.initialVariantId },
        };
    }
}
/**
 * Stack-strategy slot. Items append to the tail; `addItem` returns the placed
 * item carrying `{ kind: 'stack', index }`. See `IStackSlotApi`.
 */
class StackSlotApi extends SlotApi {
    getPlacement() {
        return this.slot.placement;
    }
    addItem(request, options) {
        return this.placeItem(request, options);
    }
    /** The allowed rotation menu (ADR-0060): `[defaultRotation, ...rotations]`, deduplicated. */
    getRotationMenu() {
        return rotationMenu(this.getPlacement());
    }
    /**
     * Manually re-rotate a placed item to one of the allowed-menu angles
     * (ADR-0060). Applied only when it still fits the boundary; subsequent items
     * reposition by translation on the next layout. Throws when the item is
     * absent, the angle is not in the menu, or it would push the item outside the
     * boundary.
     */
    async rotateItem(itemId, rotation) {
        const item = this.items.find((i) => i.id === itemId);
        const info = item?.placement;
        if (!item || info?.kind !== 'stack') {
            throw new Error(`Slot "${this.slot.name}": no stack item "${itemId}" to rotate.`);
        }
        const placement = this.getPlacement();
        if (!rotationMenu(placement).some((r) => rotationsEqual(r, rotation))) {
            throw new Error(`Slot "${this.slot.name}": rotation ${JSON.stringify(rotation)} is not in the allowed menu.`);
        }
        const previous = info.rotation;
        item.placement = stackInfoWithRotation(info, rotation);
        if (placement.boundary && this.hasBoundsWiring() && !this.itemFitsBoundary(itemId)) {
            item.placement = stackInfoWithRotation(info, previous);
            throw new Error(`Slot "${this.slot.name}": rotating item "${itemId}" would exceed the slot boundary.`);
        }
    }
}
/**
 * Free-strategy slot. Generic append with no positioning (stub) — the placed
 * item carries no `placement` field until the strategy is implemented.
 */
class FreeSlotApi extends SlotApi {
    getPlacement() {
        return this.slot.placement;
    }
    addItem(request, options) {
        return this.placeItem(request, options);
    }
}
/**
 * List-strategy slot — a 1-D row of fixed cells addressed by `index`. Placement
 * is always explicit via `addItemAt`; `addItem` (no index) is rejected. Cells
 * may have gaps. See `IListSlotApi` and the `list` placement strategy.
 */
class ListSlotApi extends SlotApi {
    getPlacement() {
        return this.slot.placement;
    }
    /** Place `request` at the given cell `index`. Throws if the cell is occupied,
     *  the index is out of bounds, or the request isn't accepted. */
    addItemAt(index, request, options) {
        return this.placeItem(request, options, { index });
    }
    /** True if an item could be placed at `index` (in bounds and not occupied). */
    canAddAt(index) {
        const strategy = getPlacementStrategy(this.slot.placement.kind);
        return strategy.canAdd({
            config: this.slot.placement,
            currentItems: this.items,
            capacity: this.slot.cardinality.max,
            target: { index },
        }).ok;
    }
    /** Remove the item occupying `index`. Throws if the cell is empty. */
    removeAt(index) {
        const item = this.items.find((it) => it.placement?.kind === 'list' && it.placement.index === index);
        if (!item) {
            throw new Error(`List slot "${this.slot.name}" has no item at index ${index}.`);
        }
        this.removeItem(item.id);
    }
    /** The item occupying `index`, or `null` if the cell is empty. */
    getItemAt(index) {
        return this.getItems().find((it) => it.placement?.kind === 'list' && it.placement.index === index) ?? null;
    }
    /** Occupied cells with their items, sorted by index. */
    getOccupied() {
        return this.getItems()
            .filter((it) => it.placement?.kind === 'list')
            .map((item) => ({ index: item.placement.index, item }))
            .sort((a, b) => a.index - b.index);
    }
    /** Move the item at `from` to the empty cell `to`. Throws if `from` is empty
     *  or `to` is occupied/out of bounds. */
    moveItem(from, to) {
        if (from === to)
            return;
        const item = this.items.find((it) => it.placement?.kind === 'list' && it.placement.index === from);
        if (!item) {
            throw new Error(`List slot "${this.slot.name}" has no item at index ${from}.`);
        }
        if (!this.canAddAt(to)) {
            throw new Error(`List slot "${this.slot.name}": cannot move item to index ${to} (occupied or out of bounds).`);
        }
        // Preserve per-item align/offset/rotation (ADR-0063) — only the cell changes.
        item.placement = { ...item.placement, index: to };
    }
    /** The allowed rotation menu (ADR-0063): `[defaultRotation, ...rotations]`, deduplicated. */
    getRotationMenu() {
        return rotationMenu(this.getPlacement());
    }
    /**
     * Manually re-rotate a placed item to one of the allowed-menu angles
     * (ADR-0063). Unlike stack there is no boundary and no ripple — positions are
     * index-driven and the pivot is the bbox centre, so this is a pure per-item
     * update. Throws when the item is absent or the angle is not in the menu.
     * Async for surface parity with `StackSlotApi.rotateItem`.
     */
    async rotateItem(itemId, rotation) {
        const item = this.items.find((i) => i.id === itemId);
        const info = item?.placement;
        if (!item || info?.kind !== 'list') {
            throw new Error(`Slot "${this.slot.name}": no list item "${itemId}" to rotate.`);
        }
        if (!rotationMenu(this.getPlacement()).some((r) => rotationsEqual(r, rotation))) {
            throw new Error(`Slot "${this.slot.name}": rotation ${JSON.stringify(rotation)} is not in the allowed menu.`);
        }
        item.placement = listInfoWithRotation(info, rotation);
    }
}
/**
 * Bag-strategy slot — a non-visual collection (ADR — bag slots). Unlike the
 * spatial strategies, items carry no position/transform: each entry is one
 * accepted product plus an `amount` (how many identical copies), and the slot's
 * cardinality bounds the **total quantity** (sum of amounts). The placed entry's
 * configuring api is shared across all `amount` copies — configuring the entry
 * applies to every copy. Adds bypass the placement-strategy positional pipeline
 * (there is nothing to position) but reuse accepted-item resolution and placed-
 * api instantiation.
 */
class BagSlotApi extends SlotApi {
    getPlacement() {
        return this.slot.placement;
    }
    /** Sum of every entry's `amount` (undefined ⇒ 1) — the value `cardinality.max` bounds. */
    getTotalAmount() {
        return this.items.reduce((sum, item) => sum + (item.amount ?? 1), 0);
    }
    /**
     * Add `amount` (default 1) identical copies of one accepted product as a
     * single entry. Positionless; enforces total-quantity cardinality.
     */
    async addItem(request, options = {}) {
        const amount = options.amount ?? 1;
        if (!Number.isInteger(amount) || amount < 1) {
            throw new Error(`Slot "${this.slot.name}": amount must be a positive integer (got ${amount}).`);
        }
        const acceptedItems = this.resolveAcceptedItems();
        const resolved = resolveRequest(request, acceptedItems, this.slot.name);
        if (!resolved) {
            throw new Error(`Slot "${this.slot.name}" does not accept item ${JSON.stringify(request)}. ` +
                `Allowed items: ${JSON.stringify(acceptedItems)}.`);
        }
        const { placedRef: product, presetId } = resolved;
        const max = this.slot.cardinality.max;
        if (max !== 'unbounded' && this.getTotalAmount() + amount > max) {
            throw new Error(`Slot "${this.slot.name}" is full (cardinality.max = ${max}, current total: ${this.getTotalAmount()}, ` +
                `requested: ${amount}).`);
        }
        if (options.initialState && request.kind !== 'compositePreset') {
            throw new Error(`Slot "${this.slot.name}": initialState is only supported for compositePreset placements.`);
        }
        const id = randomUUID();
        const placed = { id, slot: this.slot.key, product, amount };
        await this.instantiatePlacedApi(id, product, presetId, { initialState: options.initialState });
        this.items.push(placed);
        return { ...placed, ...this.placedApiBundle(placed) };
    }
    /** Change the quantity of an installed entry; re-checks total-quantity cardinality. */
    setAmount(itemId, amount) {
        if (!Number.isInteger(amount) || amount < 1) {
            throw new Error(`Slot "${this.slot.name}": amount must be a positive integer (got ${amount}).`);
        }
        const item = this.items.find((it) => it.id === itemId);
        if (!item) {
            throw new Error(`Slot "${this.slot.name}" has no placed item with id "${itemId}".`);
        }
        const max = this.slot.cardinality.max;
        if (max !== 'unbounded') {
            const others = this.getTotalAmount() - (item.amount ?? 1);
            if (others + amount > max) {
                throw new Error(`Slot "${this.slot.name}": setting amount to ${amount} exceeds cardinality.max = ${max} ` +
                    `(other entries total ${others}).`);
            }
        }
        item.amount = amount;
    }
}
/**
 * Build the concrete SlotApi subclass for a slot's placement strategy. The
 * single place that maps `placement.kind` → class; all construction (production
 * builder, deserialization) goes through here so the right strategy surface is
 * exposed.
 */
function createSlotApi(slot, options = {}) {
    switch (slot.placement.kind) {
        case 'stack':
            return new StackSlotApi(slot, options);
        case 'list':
            return new ListSlotApi(slot, options);
        case 'free':
            return new FreeSlotApi(slot, options);
        case 'bag':
            return new BagSlotApi(slot, options);
        default: {
            const exhaustive = slot.placement;
            throw new Error(`Unknown placement kind: ${JSON.stringify(exhaustive)}`);
        }
    }
}
/** Narrow an `ISlotApi` to `IStackSlotApi` (stack placement). */
function isStackSlotApi(slot) {
    return slot.getPlacement().kind === 'stack';
}
/** Narrow an `ISlotApi` to `IListSlotApi` (list placement). */
function isListSlotApi(slot) {
    return slot.getPlacement().kind === 'list';
}
/** Narrow an `ISlotApi` to `IFreeSlotApi` (free placement). */
function isFreeSlotApi(slot) {
    return slot.getPlacement().kind === 'free';
}
/** Narrow an `ISlotApi` to `IBagSlotApi` (bag placement). */
function isBagSlotApi(slot) {
    return slot.getPlacement().kind === 'bag';
}

/**
 * Narrow an attribute's text data to the values of one component product.
 * Falls back to the flat `values` union when the payload predates
 * `valuesByProduct` (baked JSON assets) or the product has no entry.
 */
function scopeAttributeTextData(attributeData, productKey) {
    const values = attributeData.valuesByProduct?.[productKey];
    return values ? { ...attributeData, values } : attributeData;
}
class CompositeProductApi {
    options;
    fetcher;
    defaultValidationOptions;
    modelRef;
    compositeProduct;
    attributes;
    productComponentsApi;
    productComponentsApiByComponent;
    bomDetails;
    awaitInjection;
    productData;
    itemOrder;
    rawVariants;
    slotApis;
    _configurationMigrated;
    isInCatalog;
    projectId;
    draft;
    compositeProductVariants;
    dataVariantsLoaded = false;
    rules;
    _logicEngine = null;
    _cpMeta = {};
    _cpMetaChangeCallbacks = [];
    // Model change subscriptions
    _modelChangeCallbacks = [];
    _componentChangeCallbacks = new Map();
    _attributeChangeCallbacks = new Map();
    _slotItemsInvalidatedCallbacks = [];
    // Last-emitted invalid-item signature per slot — lets `notifySlotItemsInvalidated`
    // fire only when a slot's invalid set actually changes, not on every model change.
    _lastInvalidSlotItemIds = new Map();
    // Lazily-fetched, cached view3d spec (see getView3dSpecification).
    _view3dSpecification;
    _presetValidation;
    // Lazily-created, reused bounds catalog for the debug overlay (computeBoundsDebug).
    // Built over the SDK's memoizing fetcher; warmFor is cached per productId, so
    // recreating it would be cheap anyway — we cache it to avoid the churn.
    boundsDebugCatalog;
    constructor(options, fetcher, defaultValidationOptions, modelRef, compositeProduct, attributes, productComponentsApi, productComponentsApiByComponent, bomDetails, awaitInjection, productData, itemOrder, rawVariants, initialMeta = {}, presetValidation, slotApis = [], _configurationMigrated = false) {
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
        this.modelRef = modelRef;
        this.compositeProduct = compositeProduct;
        this.attributes = attributes;
        this.productComponentsApi = productComponentsApi;
        this.productComponentsApiByComponent = productComponentsApiByComponent;
        this.bomDetails = bomDetails;
        this.awaitInjection = awaitInjection;
        this.productData = productData;
        this.itemOrder = itemOrder;
        this.rawVariants = rawVariants;
        this.slotApis = slotApis;
        this._configurationMigrated = _configurationMigrated;
        // Component apis are built from variants alone and never see the schema, so
        // the composite hands them the catalog meta their price types come from.
        for (const componentApi of productComponentsApi) {
            componentApi.setCatalogMeta(compositeProduct.catalogMeta);
        }
        this.isInCatalog = 'catalogId' in options && options.catalogId !== undefined;
        this.projectId = options.projectId !== undefined ? String(options.projectId) : '';
        this.draft = options.version === 'draft';
        this.awaitInjection.then(({ logicEngine, compositeProductVariants, rules }) => {
            this._logicEngine = logicEngine;
            this.compositeProductVariants = compositeProductVariants;
            this.rules = rules;
            this.dataVariantsLoaded = true;
        });
        this._cpMeta = initialMeta;
        this._presetValidation = presetValidation;
    }
    async awaitLogicEngine() {
        await this.awaitInjection;
    }
    getComponentVariant(componentKey) {
        return this.modelRef.model[componentKey];
    }
    get logicEngine() {
        if (!this._logicEngine) {
            throw new Error('Logic engine should be injected before using');
        }
        return this._logicEngine;
    }
    get componentsKey() {
        return Object.keys(this.modelRef.model);
    }
    loadAllAttributeValues(componentKey, attributeKey) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).loadAllAttributeValues(attributeId);
    }
    getProductId() {
        return this.compositeProduct.productId;
    }
    getName() {
        return this.compositeProduct.productName;
    }
    getProductKey() {
        return this.compositeProduct.productKey;
    }
    getProduct() {
        return this.compositeProduct;
    }
    /**
     * The composite's view3d specification (3D models, parts, materials, camera).
     * Fetched lazily on first call and cached — most composites never need it, and
     * a composite placed in a slot exposes its own spec here so the viewer can
     * render it recursively (no fetcher needed in the viewer).
     */
    getView3dSpecification() {
        if (!this._view3dSpecification) {
            this._view3dSpecification = this.fetcher.getCompositeView3dSpecification(this.options);
        }
        return this._view3dSpecification;
    }
    getComponentSchema(componentKey) {
        return this.compositeProduct.productType.components.find((it) => it.key === componentKey);
    }
    getAvailableComponentsKeys() {
        return this.productComponentsApi.filter((it) => it.isAvailable()).map((it) => it.getKey());
    }
    getPriceTypes() {
        return buildPriceTypeViews(this.compositeProduct.catalogMeta);
    }
    async getPrice(options) {
        const ordinal = resolvePriceTypeOrdinal(this.getPriceTypes(), options?.priceType);
        return projectPrice(await this.getPriceAllTypes(), ordinal);
    }
    /**
     * Seeds a running total with every `(price type × currency)` key rather than
     * with the currencies alone: a type that nothing in this composite carries a
     * price for must still project to `{ USD: 0, EUR: 0 }`, not to `{}` — a
     * configurator has to render zero, not "no price".
     */
    zeroPriceAllTypes() {
        const { currencies } = this.compositeProduct.catalogMeta;
        const keys = [];
        for (const priceType of this.getPriceTypes()) {
            for (const currency of currencies) {
                keys.push(priceKey(priceType.ordinal, currency));
            }
        }
        return zeroPrice(keys);
    }
    async getPriceAllTypes() {
        const prices = await Promise.all(this.productComponentsApi.map((api) => api.getPriceAllTypes()));
        const getPartsAmount = computePartsAmount(this.compositeProduct.parts, this.getAvailableComponentsKeys());
        let totalInCents = this.zeroPriceAllTypes();
        for (let i = 0; i < prices.length; i++) {
            const componentApi = this.productComponentsApi[i];
            if (!componentApi.isIncludedInPdf()) {
                continue;
            }
            const price = prices[i];
            const componentKey = componentApi.getKey();
            const partsAmount = getPartsAmount(componentKey);
            const priceInCents = toCents(price);
            const componentPrice = mulPriceByScalar(priceInCents, partsAmount);
            totalInCents = addPrice(totalInCents, componentPrice);
        }
        const bomList = new BomList(this.bomDetails);
        this.addBomItemsToBomList(bomList);
        totalInCents = addPrice(totalInCents, toCents(bomList.getTotalPrice()));
        totalInCents = addPrice(totalInCents, await this.getSlotsPriceInCents());
        return fromCents(totalInCents);
    }
    async getMetaIds() {
        const ids = await Promise.all(this.productComponentsApi.map((api) => api.getMetaId()));
        const bomList = new BomList(this.bomDetails);
        this.addPartsMetaIds(ids, bomList);
        this.addBomItemsToBomList(bomList);
        await this.addSlotMetaIds(bomList);
        const result = [];
        for (const [id, amount] of bomList.getBomRecord().entries()) {
            result.push({
                [this.compositeProduct.catalogMeta.metaId]: id,
                amount: amount,
            });
        }
        return result;
    }
    addBomItemsToBomList(bomList) {
        const bomCalculator = new BomCalculator(this.compositeProduct.bom);
        bomCalculator.execute({
            product: this.getRawValue(),
            meta: this._cpMeta,
        }, bomList);
    }
    addPartsMetaIds(ids, bomList) {
        const getPartsAmount = computePartsAmount(this.compositeProduct.parts, this.getAvailableComponentsKeys());
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const componentKey = this.productComponentsApi[i].getKey();
            if (isSkippingMetaId(id)) {
                continue;
            }
            bomList.addExternalBomItem(id, getPartsAmount(componentKey));
        }
    }
    /**
     * Sum the price of every item placed in this composite's slots (in cents).
     * A placed composite's `getPriceAllTypes()` recurses into its own slots, so
     * nested slots-within-slots are covered. Bag items carry an `amount` (copies
     * share one api) and are multiplied accordingly; spatial items default to 1.
     *
     * Items contribute their full namespaced map: projecting per item would force
     * the selected price type down through every nesting level, so it happens once
     * instead, at `getPrice`.
     */
    async getSlotsPriceInCents() {
        const pricePromises = [];
        for (const slot of this.slotApis) {
            for (const item of slot.getItems()) {
                if (!item.api) {
                    continue;
                }
                const amount = item.amount ?? 1;
                pricePromises.push(item.api.getPriceAllTypes().then((price) => ({ price, amount })));
            }
        }
        const results = await Promise.all(pricePromises);
        let totalInCents = this.zeroPriceAllTypes();
        for (const { price, amount } of results) {
            totalInCents = addPrice(totalInCents, mulPriceByScalar(toCents(price), amount));
        }
        return totalInCents;
    }
    /**
     * Fold the metaIds of every item placed in this composite's slots into the
     * shared `BomList` (dedup + sum by metaId value). Basic items contribute a
     * single `getMetaId()`; composite items contribute their `getMetaIds()`,
     * which already recurses into their own slots. Bag `amount` scales quantities.
     * Skipping/empty metaIds are filtered by `BomList` internally.
     */
    async addSlotMetaIds(bomList) {
        for (const slot of this.slotApis) {
            for (const item of slot.getItems()) {
                const placementAmount = item.amount ?? 1;
                if (item.composite) {
                    if (!item.api) {
                        continue;
                    }
                    const metaIds = await item.api.getMetaIds();
                    for (const entry of metaIds) {
                        const { amount, ...rest } = entry;
                        const id = Object.values(rest)[0];
                        if (typeof id !== 'string') {
                            continue;
                        }
                        bomList.addExternalBomItem(id, amount * placementAmount);
                    }
                }
                else {
                    if (!item.api) {
                        continue;
                    }
                    const id = await item.api.getMetaId();
                    bomList.addExternalBomItem(id, placementAmount);
                }
            }
        }
    }
    getProductComponents() {
        return this.productComponentsApi;
    }
    getProductComponent(componentKey) {
        return this.productComponentsApi.find((api) => api.getKey() === componentKey);
    }
    getSlots() {
        return this.slotApis;
    }
    getSlot(key) {
        return this.slotApis.find((api) => api.getKey() === key);
    }
    // Effective logic order this api was built with (stored, overridden or
    // default) — set by the builder right after construction (ADR-0070).
    logicOrderSnapshot = [];
    /** Internal: called by CompositeProductApiBuilder. */
    setLogicOrderSnapshot(order) {
        this.logicOrderSnapshot = order;
    }
    getLogicOrder() {
        return this.logicOrderSnapshot;
    }
    // View-only model3d override (ADR-0070). Never persisted, never part of the
    // logic-engine schema; merged into getVariant() copies only when enabled.
    viewModel3dKeyEnabled = false;
    viewModel3dOverrides = new Map();
    /** Enable the `_viewModel3dKey` system attribute (ClpSdk.useViewOptions). */
    enableViewModel3dKey() {
        this.viewModel3dKeyEnabled = true;
    }
    isViewModel3dKeyEnabled() {
        return this.viewModel3dKeyEnabled;
    }
    setViewModel3dKey(componentKey, model3dKey) {
        if (!this.viewModel3dKeyEnabled) {
            throw new Error('_viewModel3dKey is disabled. Enable it via ClpSdk.useViewOptions({ enableViewModel3dKey: true })');
        }
        if (model3dKey === null) {
            this.viewModel3dOverrides.delete(componentKey);
        }
        else {
            this.viewModel3dOverrides.set(componentKey, model3dKey);
        }
    }
    getViewModel3dKey(componentKey) {
        return this.viewModel3dOverrides.get(componentKey) ?? null;
    }
    getComponentModel3dIds(componentKey) {
        const ids = new Set();
        for (const list of this.rawVariants.components[componentKey] ?? []) {
            if (!list)
                continue;
            for (const variant of list.variants) {
                if (variant.model3dId != null) {
                    ids.add(variant.model3dId);
                }
            }
        }
        return Array.from(ids);
    }
    /**
     * Attribute entries sorted by the composite's logic order. The engine's
     * setValue filters candidate variants by every attribute that PRECEDES the
     * one being set (relaxing those after it), so any multi-attribute replay
     * must follow that order: each prefix then matches the target variant
     * itself and every step stays satisfiable. An arbitrary (JSON-key) order
     * can request an impossible intermediate combination — e.g. the new width
     * with the old depth — for which the engine finds no variant.
     */
    attributeEntriesInLogicOrder(componentKey, attributes) {
        const rank = new Map();
        this.getLogicOrder().forEach((item, index) => {
            if (item.type === 'attribute' && item.componentKey === componentKey) {
                rank.set(item.attributeKey, index);
            }
        });
        return Object.entries(attributes ?? {}).sort(([a], [b]) => (rank.get(a) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b) ?? Number.MAX_SAFE_INTEGER));
    }
    async selectComponentModel3d(componentKey, model3dId) {
        const lists = (this.rawVariants.components[componentKey] ?? []).filter((list) => list !== null);
        const productIdAttribute = this.modelRef.model[componentKey]?.[VariantProperties.productId];
        const currentProductId = productIdAttribute && hasAttributeValue(productIdAttribute) ? productIdAttribute.value : undefined;
        // Prefer a variant of the currently selected product to minimize churn
        const ordered = [...lists].sort((a, b) => {
            const aCurrent = String(a.product.productId) === String(currentProductId) ? 0 : 1;
            const bCurrent = String(b.product.productId) === String(currentProductId) ? 0 : 1;
            return aCurrent - bCurrent;
        });
        for (const list of ordered) {
            const target = list.variants.find((variant) => variant.model3dId === model3dId);
            if (!target)
                continue;
            if (String(list.product.productId) !== String(currentProductId)) {
                await this.setAttributeValue(componentKey, VariantProperties.productId, {
                    value: String(list.product.productId),
                });
            }
            for (const [attributeKey, value] of this.attributeEntriesInLogicOrder(componentKey, target.attributes)) {
                if (value === CUSTOM_ATTRIBUTE_VALUE)
                    continue;
                await this.setAttributeValue(componentKey, attributeKey, { value });
            }
            return true;
        }
        return false;
    }
    getVariant() {
        if (!this.viewModel3dKeyEnabled || this.viewModel3dOverrides.size === 0) {
            return this.modelRef.model;
        }
        const merged = { ...this.modelRef.model };
        for (const [componentKey, model3dKey] of this.viewModel3dOverrides) {
            const componentVariant = merged[componentKey];
            if (!componentVariant) {
                continue;
            }
            merged[componentKey] = {
                ...componentVariant,
                [VariantProperties.viewModel3dKey]: { value: model3dKey },
            };
        }
        return merged;
    }
    getProductData() {
        return this.productData.product;
    }
    getComponentProductData(componentKey) {
        return this.getComponentApi(componentKey).getProductData();
    }
    getComponentAllProducts(componentKey) {
        const componentData = this.productData.components[componentKey];
        return componentData.products;
    }
    getComponentTextData(componentKey) {
        const componentData = this.productData.components[componentKey];
        return componentData.texts;
    }
    getComponentAttributesData(componentKey, productKey) {
        const attributes = this.productData.components[componentKey].attributes;
        if (!productKey) {
            return attributes;
        }
        const scoped = {};
        for (const [attributeKey, attributeData] of Object.entries(attributes)) {
            scoped[attributeKey] = scopeAttributeTextData(attributeData, productKey);
        }
        return scoped;
    }
    getComponentAttributeData(componentKey, attributeKey, productKey) {
        const attributeData = this.productData.components[componentKey].attributes[attributeKey];
        return productKey ? scopeAttributeTextData(attributeData, productKey) : attributeData;
    }
    getComponentProductType(componentKey) {
        const data = this.productData.components[componentKey];
        if (!data?.productTypeKey)
            return undefined;
        return { key: data.productTypeKey, isComposite: !!data.composite };
    }
    getComponentsData() {
        const result = {};
        for (const componentKey of this.componentsKey) {
            result[componentKey] = this.getComponentProductData(componentKey);
        }
        return result;
    }
    getComponentApi(componentKey) {
        const api = this.productComponentsApiByComponent.get(componentKey);
        if (!api) {
            throw new Error(`Invalid component key "${componentKey}"`);
        }
        return api;
    }
    getValue() {
        return pureObject(this.modelRef.model);
    }
    getRawValue() {
        const result = {};
        for (const [key, value] of Object.entries(this.modelRef.model)) {
            const v = new ConfiguratorPV(value);
            if (v.isAvailable()) {
                result[key] = pureObject(value);
            }
            else {
                result[key] = null;
            }
        }
        return result;
    }
    getAttributeValue(componentKey, attributeKey) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).getAttributeValue(attributeId);
    }
    isAvailable(componentKey) {
        return this.getComponentApi(componentKey).isAvailable();
    }
    isEnable(componentKey) {
        return this.getComponentApi(componentKey).isEnabled();
    }
    loadAvailability(componentKey) {
        return this.getComponentApi(componentKey).loadAvailability();
    }
    loadCompatibleAttributeValues(componentKey, attributeKey) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).loadCompatibleAttributeValues(attributeId);
    }
    async loadAllCompatibleAttributeValues() {
        const result = {};
        for (const component of this.productComponentsApi) {
            result[component.getKey()] = await component.loadAllCompatibleAttributeValues();
        }
        return result;
    }
    setAttributeValue(componentKey, attributeKey, attributeValue) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).setAttributeValue(attributeId, attributeValue);
    }
    setAvailability(componentKey, available) {
        return this.getComponentApi(componentKey).setAvailability(available);
    }
    getAttributes() {
        return this.attributes.filter((it) => !it.attributeKey.startsWith('_'));
    }
    getItemOrders() {
        return this.itemOrder;
    }
    async createLink() {
        if (!this.projectId) {
            throw new Error('projectId is required for createLink. Ensure projectId is provided in SDK initialization options.');
        }
        const serializedModel = this.serialize(1);
        const linkParams = {
            projectId: Number(this.projectId),
            productId: this.getProductId(),
            catalogId: 'catalogId' in this.options ? Number(this.options.catalogId) : undefined,
            model: serializedModel,
        };
        return await this.fetcher.getShortLink(linkParams, this.draft);
    }
    serialize(version) {
        const model = this.modelRef.model;
        const serializedComponents = {};
        const custom = {};
        for (const [componentKey, variant] of Object.entries(model)) {
            this.serializeVariant(variant, componentKey, serializedComponents, custom);
        }
        const slots = this.serializeSlots(version);
        return {
            version,
            custom: custom,
            components: serializedComponents,
            meta: this._cpMeta,
            ...(slots ? { slots } : {}),
        };
    }
    /**
     * Build the SDK-computed bounds tree for the CURRENT configuration, ready to
     * hand to the viewer's BoundsDebugPlugin (ADR-0068). Diagnostic-only; the
     * underlying builder never throws. The bounds catalog is lazily created over
     * the SDK's memoizing fetcher and reused across calls (warmFor is cached per
     * productId), so callers can invoke this after every scene rebuild cheaply.
     */
    async computeBoundsDebug(version = 0) {
        this.boundsDebugCatalog ??= createFetcherBoundsCatalog(this.fetcher, this.options);
        const productId = this.getProductId();
        const state = this.serialize(version);
        await this.boundsDebugCatalog.warmFor(productId, state);
        return computeBoundsDebug({ productId, state }, this.boundsDebugCatalog);
    }
    findRawVariantById(componentKey, variantId) {
        const productLists = this.rawVariants.components[componentKey];
        if (!productLists)
            return undefined;
        for (const productEntry of productLists) {
            if (!productEntry)
                continue;
            const variant = productEntry.variants.find((v) => Number(v.variantId) === Number(variantId));
            if (variant)
                return variant;
        }
        return undefined;
    }
    serializeSlots(version) {
        if (!this.slotApis.length)
            return undefined;
        const result = {};
        for (const slot of this.slotApis) {
            result[slot.getKey()] = slot.serialize(version);
        }
        return result;
    }
    /**
     * Apply a serialized snapshot to this composite. Restores custom attribute
     * overrides via setAttributeValue replay, then rehydrates slot children.
     *
     * NB: Phase C-min restoration playback. For variant-only selection (where
     * setAttributeValue with a variant attribute drives variantId selection),
     * the `custom` portion of the snapshot already carries the resulting
     * value — replaying it re-triggers the same LE path. Components-section
     * variantId restoration without corresponding custom entries is not yet
     * exercised; will revisit if a real use-case surfaces.
     */
    async applySerializedState(state) {
        // Wait for LE to be ready before replaying any setAttributeValue.
        await this.awaitLogicEngine();
        // 1. Restore variant selection per component by replaying the chosen
        //    variant's attribute values. This drives the LE to re-select that
        //    same variant.
        for (const [componentKey, comp] of Object.entries(state.components ?? {})) {
            if (!comp)
                continue;
            const variant = this.findRawVariantById(componentKey, comp.id);
            if (!variant)
                continue;
            // Logic-order replay — see attributeEntriesInLogicOrder: an unordered
            // replay can hit an impossible intermediate combination and silently
            // lose the restored state (the Layout page's product context resetting
            // after a slot save).
            for (const [attributeKey, value] of this.attributeEntriesInLogicOrder(componentKey, variant.attributes)) {
                await this.setAttributeValue(componentKey, attributeKey, { value });
            }
        }
        // 2. Restore custom (non-variant-axis) overrides. Applied AFTER variant
        //    selection so any custom field overrides variant defaults.
        for (const [componentKey, attrs] of Object.entries(state.custom ?? {})) {
            for (const [attributeKey, value] of Object.entries(attrs)) {
                await this.setAttributeValue(componentKey, attributeKey, { custom: true, value });
            }
        }
        // Restore meta passthrough.
        if (state.meta) {
            this._cpMeta = { ...state.meta };
        }
        // Restore slot children. Each placed compositePreset recursively applies
        // its own configState via SlotApi.addItem(..., { initialState }).
        if (state.slots) {
            for (const [slotKey, placedItems] of Object.entries(state.slots)) {
                const slot = this.getSlot(slotKey);
                if (!slot)
                    continue;
                for (const item of placedItems) {
                    // Build the add-item request from the serialized ref.
                    let request;
                    let options;
                    if (item.product.kind === 'basicVariant') {
                        // Serialized basicVariant carries `initialVariantId` — captured at
                        // serialize time by SlotApi (current variant from api when wired,
                        // otherwise the original placement-time identity). We feed it back
                        // as a basicVariant request whose `variantId` arg seeds the new
                        // placedRef's `initialVariantId`.
                        request = {
                            kind: 'basicVariant',
                            productId: item.product.productId,
                            variantId: item.product.initialVariantId,
                        };
                    }
                    else {
                        // The placed ref intentionally doesn't carry a load-bearing
                        // `presetId` (placed identity is `productId + state`; presets are
                        // starting points). To re-fetch via the placedApiFactory we still
                        // need a presetId — prefer the snapshot's `fromPreset` provenance
                        // when that preset is still accepted (keeps provenance stable
                        // across round-trips), otherwise any matching acceptedItem works
                        // because `initialState` (if present in the snapshot) overrides
                        // the preset's state; the preset round-trip is essentially a
                        // no-op for restored items.
                        const acceptedItems = slot.getAcceptedItems();
                        const fromPresetId = item.product.fromPreset?.presetId;
                        const accepted = (fromPresetId
                            ? acceptedItems.find((a) => a.kind === 'compositePreset' &&
                                a.productId === item.product.productId &&
                                a.presetId === fromPresetId)
                            : undefined) ??
                            acceptedItems.find((a) => a.kind === 'compositePreset' && a.productId === item.product.productId);
                        if (!accepted || accepted.kind !== 'compositePreset') {
                            throw new Error(`Cannot restore placed compositePreset for productId ${item.product.productId} ` +
                                `in slot "${slotKey}": no matching acceptedItem to source presetId from.`);
                        }
                        request = { kind: 'compositePreset', productId: item.product.productId, presetId: accepted.presetId };
                        options = item.product.configState ? { initialState: item.product.configState } : undefined;
                    }
                    // Dispatch on the slot's placement strategy: list slots restore the
                    // saved cell index via addItemAt; stack/free append via addItem.
                    if (isListSlotApi(slot)) {
                        if (item.placement?.kind !== 'list') {
                            throw new Error(`Cannot restore item in list slot "${slotKey}": serialized item is missing its list placement index.`);
                        }
                        await slot.addItemAt(item.placement.index, request, options);
                    }
                    else if (isBagSlotApi(slot)) {
                        // Bag entries carry no placement; the saved `amount` (⇒ 1 if absent)
                        // rides back through the add so the multiset survives the round-trip.
                        await slot.addItem(request, { ...options, amount: item.amount });
                    }
                    else if (isStackSlotApi(slot) || isFreeSlotApi(slot)) {
                        await slot.addItem(request, options);
                    }
                    else {
                        throw new Error(`Cannot restore items into slot "${slotKey}": unsupported placement strategy.`);
                    }
                }
            }
        }
    }
    serializeVariant(variant, componentKey, serializedComponents, custom) {
        const v = new ConfiguratorPV(variant);
        const available = v.isAvailable();
        if (!available) {
            serializedComponents[componentKey] = null;
            return;
        }
        const productId = v.productId;
        const id = v.variantId;
        for (const [attributeKey, attributeValue] of Object.entries(variant)) {
            if ('custom' in attributeValue && hasAttributeValue(attributeValue)) {
                if (!custom[componentKey]) {
                    custom[componentKey] = {};
                }
                custom[componentKey][attributeKey] = attributeValue.value;
            }
        }
        serializedComponents[componentKey] = {
            id: Number(id),
            productId: Number(productId),
        };
    }
    #getInvalidRules(logicEngine, validationOptions) {
        const errors = logicEngine.validate(this.modelRef.model, validationOptions || this.defaultValidationOptions);
        const errorsSet = new Set(errors.invalidRules);
        const invalidRules = [];
        for (const rule of this.rules) {
            if (errorsSet.has(rule.name)) {
                invalidRules.push(rule);
            }
        }
        return invalidRules;
    }
    getInvalidRules(validationOptions) {
        return this.#getInvalidRules(this.logicEngine, validationOptions);
    }
    validate(validationOptions) {
        return this.logicEngine.validate(this.modelRef.model, validationOptions || this.defaultValidationOptions);
    }
    async getFirstCompatibleVariantIgnoringRules() {
        const { logicEngine } = await this.awaitInjection;
        return logicEngine.getFirstCompatibleVariantIgnoringRules();
    }
    getComponentPartsAmount(componentKey) {
        const getPartsAmount = computePartsAmount(this.compositeProduct.parts, this.getAvailableComponentsKeys());
        return getPartsAmount(componentKey);
    }
    isComponentInPdf(componentKey) {
        const cmp = this.productComponentsApi.find((it) => it.getKey() === componentKey);
        if (!cmp) {
            throw new Error(`Cannot find component with key '${componentKey}'`);
        }
        return cmp.isIncludedInPdf();
    }
    getCompositeProductMeta() {
        return this._cpMeta;
    }
    setCompositeProductMeta(meta) {
        const oldMeta = this._cpMeta;
        this._cpMeta = meta;
        if (oldMeta !== meta) {
            this._cpMetaChangeCallbacks.forEach((callback) => callback(meta));
        }
    }
    onCompositeProductMetaChange(callback) {
        this._cpMetaChangeCallbacks.push(callback);
        return () => {
            const index = this._cpMetaChangeCallbacks.indexOf(callback);
            if (index > -1) {
                this._cpMetaChangeCallbacks.splice(index, 1);
            }
        };
    }
    // Alias methods for interface compatibility
    getMeta() {
        return this.getCompositeProductMeta();
    }
    setMeta(meta) {
        this.setCompositeProductMeta(meta);
    }
    onMetaChange(callback) {
        return this.onCompositeProductMetaChange(callback);
    }
    /**
     * Subscribe to any model change
     * @param callback Function to call when the model changes
     * @returns Unsubscribe function
     */
    onModelChange(callback) {
        this._modelChangeCallbacks.push(callback);
        return () => {
            const index = this._modelChangeCallbacks.indexOf(callback);
            if (index > -1) {
                this._modelChangeCallbacks.splice(index, 1);
            }
        };
    }
    /**
     * Subscribe to changes of a specific component
     * @param componentKey The component key to watch
     * @param callback Function to call when the component changes
     * @returns Unsubscribe function
     */
    onComponentChange(componentKey, callback) {
        if (!this._componentChangeCallbacks.has(componentKey)) {
            this._componentChangeCallbacks.set(componentKey, []);
        }
        const callbacks = this._componentChangeCallbacks.get(componentKey);
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        };
    }
    /**
     * Subscribe to changes of a specific attribute within a component
     * @param componentKey The component key
     * @param attributeKey The attribute key to watch
     * @param callback Function to call when the attribute changes
     * @returns Unsubscribe function
     */
    onAttributeChange(componentKey, attributeKey, callback) {
        const key = `${componentKey}:${attributeKey}`;
        if (!this._attributeChangeCallbacks.has(key)) {
            this._attributeChangeCallbacks.set(key, []);
        }
        const callbacks = this._attributeChangeCallbacks.get(key);
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        };
    }
    /**
     * Subscribe to slot-item invalidation. Fires when a model change shifts a
     * slot's conditional `acceptedItems` so its set of invalid placed items
     * changes (see {@link ISlotItemsInvalidatedEvent}). Returns an unsubscribe fn.
     */
    onSlotItemsInvalidated(callback) {
        this._slotItemsInvalidatedCallbacks.push(callback);
        return () => {
            const index = this._slotItemsInvalidatedCallbacks.indexOf(callback);
            if (index > -1) {
                this._slotItemsInvalidatedCallbacks.splice(index, 1);
            }
        };
    }
    /**
     * Recompute each slot's invalid placed items and emit
     * {@link ISlotItemsInvalidatedEvent} for slots whose invalid set changed since
     * the last emission. Called after a model change settles. Static slots never
     * produce invalid items, so they cost a cheap empty check.
     */
    notifySlotItemsInvalidated() {
        if (this._slotItemsInvalidatedCallbacks.length === 0)
            return;
        for (const slot of this.slotApis) {
            const invalidItems = slot.getInvalidItems();
            const signature = invalidItems
                .map((item) => item.id)
                .sort()
                .join(',');
            const previous = this._lastInvalidSlotItemIds.get(slot.getKey()) ?? '';
            if (signature === previous)
                continue;
            this._lastInvalidSlotItemIds.set(slot.getKey(), signature);
            const event = { slotKey: slot.getKey(), invalidItems };
            this._slotItemsInvalidatedCallbacks.forEach((callback) => callback(event));
        }
    }
    getPresetValidation() {
        return this._presetValidation;
    }
    /**
     * ADR-0066 degrade-on-read: true when the configuration was opened from a
     * link whose references no longer resolved exactly and the server healed
     * them in memory — the UI should tell the user the configuration may have
     * changed. The stored link itself is never rewritten.
     */
    isConfigurationMigrated() {
        return this._configurationMigrated;
    }
    /**
     * Returns all product variants across all components in a flat, simplified format.
     * Transforms the raw composite variants data into an array of IVariantInfo objects.
     */
    getAllVariants() {
        const result = [];
        // Iterate over all components
        for (const componentVariantLists of Object.values(this.rawVariants.components)) {
            // Each component can have multiple product variant lists (or null entries)
            for (const variantList of componentVariantLists) {
                if (!variantList) {
                    continue;
                }
                // Each variant list contains a product and its variants
                for (const variant of variantList.variants) {
                    result.push({
                        productKey: variant.productKey,
                        meta: {
                            id: variant.meta.id,
                            // `variant.meta.price` is the raw wire map, which carries every price type
                            // namespaced. Project it like `getPrice` does so no `PT#/` key escapes a
                            // public api; for a catalog without additional types this is the identity.
                            price: projectPrice(variant.meta.price, DEFAULT_PRICE_TYPE_ORDINAL),
                        },
                        model3dId: variant.model3dId,
                        attributes: variant.attributes,
                    });
                }
            }
        }
        return result;
    }
    /**
     * Internal method to notify all subscribers about model changes
     * Compares previous and new models to identify all changes
     * @param previousModel The model before changes
     * @param newModel The model after changes
     */
    notifyModelChange(previousModel, newModel) {
        let isChangeDetected = false;
        // Find all changed components and attributes
        const allComponentKeys = new Set([...Object.keys(previousModel), ...Object.keys(newModel)]);
        for (const componentKey of allComponentKeys) {
            const previousVariant = previousModel[componentKey];
            const newVariant = newModel[componentKey];
            // Check if component changed
            const componentChanged = !this.areVariantsEqual(previousVariant, newVariant);
            if (componentChanged) {
                isChangeDetected = true;
                // Notify component-specific listeners
                const componentCallbacks = this._componentChangeCallbacks.get(componentKey);
                if (componentCallbacks) {
                    componentCallbacks.forEach((callback) => callback(componentKey, newVariant));
                }
                // Find changed attributes within this component
                if (previousVariant && newVariant) {
                    const allAttributeKeys = new Set([...Object.keys(previousVariant), ...Object.keys(newVariant)]);
                    for (const attributeKey of allAttributeKeys) {
                        const previousValue = previousVariant[attributeKey];
                        const newValue = newVariant[attributeKey];
                        if (!this.areAttributeValuesEqual(previousValue, newValue)) {
                            // Notify attribute-specific listeners
                            const key = `${componentKey}:${attributeKey}`;
                            const attributeCallbacks = this._attributeChangeCallbacks.get(key);
                            if (attributeCallbacks) {
                                attributeCallbacks.forEach((callback) => callback(componentKey, attributeKey, newValue));
                            }
                        }
                    }
                }
            }
        }
        if (isChangeDetected) {
            this._modelChangeCallbacks.forEach((callback) => callback(newModel));
            this.notifySlotItemsInvalidated();
        }
    }
    /**
     * Compare two variants for equality
     */
    areVariantsEqual(v1, v2) {
        if (v1 === v2) {
            return true;
        }
        if (!v1 || !v2) {
            return false;
        }
        const keys1 = Object.keys(v1);
        const keys2 = Object.keys(v2);
        if (keys1.length !== keys2.length)
            return false;
        for (const key of keys1) {
            if (!this.areAttributeValuesEqual(v1[key], v2[key])) {
                return false;
            }
        }
        return true;
    }
    /**
     * Compare two attribute values for equality
     */
    areAttributeValuesEqual(v1, v2) {
        if (v1 === v2) {
            return true;
        }
        if (!v1 || !v2) {
            return false;
        }
        // Deep comparison of attribute values
        return JSON.stringify(v1) === JSON.stringify(v2);
    }
}

const INVALID_SAVED_CONFIGURATION = 'INVALID_SAVED_CONFIGURATION';
const INVALID_CONFIGURATION = 'INVALID_CONFIGURATION';
function isCatalogOptions(options) {
    return 'catalogId' in options;
}
/**
 * CompositeProductApiBuilder is used to build the CompositeProductApi.
 * It is used to load initial data(composite product, initial composite product variant, bom details, ui config), transform it to format required by CompositeProductApi and prepare the logic engine.
 *
 * Example:
 * ```ts
 * const fetcher = new Fetcher('https://api.clp.com');
 * const options: IConfiguratorCatalogProductApiOptions = {
 *   catalogId: 123,
 *   productId: 456,
 *   version: 'draft',
 *   configurationId: '123',
 * };
 *
 * const defaultValidationOptions: IValidationOptionsLE = {
 *   ignoreAllRules: false,
 * };
 * const builder = new CompositeProductApiBuilder(options, fetcher, defaultValidationOptions);
 * const compositeApi = await builder.load();
 *
 * ```
 *
 */
class CompositeProductApiBuilder {
    options;
    fetcher;
    defaultValidationOptions;
    placedCompositeApiFactory;
    placedBasicApiFactory;
    isInCatalog;
    compositeProduct;
    initialCompositeProductVariant;
    productComponentsApi = [];
    productComponentsApiByComponent = new Map();
    awaitLogicEngineReady;
    modelRef = { model: {} };
    compositeProductVariants;
    logicEngine;
    builder = new LogicEngineBuilder();
    attributesUsedInRules = new Set();
    attributes = [];
    logicOrder = [];
    logicOrderOverride;
    variants = {};
    #productData = null;
    bomDetails = [];
    UIConfig = null;
    itemOrder = [];
    presetValidation;
    get productData() {
        if (!this.#productData) {
            throw new Error('Product data is not loaded');
        }
        return this.#productData;
    }
    constructor(options, fetcher, defaultValidationOptions, 
    /**
     * Factory used by SlotApi to build a fresh CompositeProductApi for each
     * placed composite preset. Provided by CatalogApiBuilder, which delegates
     * to its own loadCompositeProduct (so the factory naturally recurses).
     * Optional only because of test/legacy paths that construct a builder
     * directly; placement of compositePreset will fail without it.
     */
    placedCompositeApiFactory, 
    /**
     * Factory used by SlotApi to build a fresh BasicProductApi for each
     * placed basicVariant. Provided by CatalogApiBuilder, which delegates to
     * BasicProductApiBuilder with the placed `(productId, variantId)`. Optional
     * only because of test/legacy paths; without it, basicVariant placements
     * still succeed but `IPlacedSlotItem.api` is `null`.
     */
    placedBasicApiFactory) {
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
        this.placedCompositeApiFactory = placedCompositeApiFactory;
        this.placedBasicApiFactory = placedBasicApiFactory;
        this.isInCatalog = 'catalogId' in options;
    }
    async load(options) {
        this.logicOrderOverride = options?.logicOrder;
        // The configurator opens a preset via `configurationId` in the api
        // options — same entity, different transport. Route it through the preset
        // fetch so the stored snapshot (slots included) hydrates; an explicit
        // link or initialVariant still wins, matching the server's precedence.
        const presetId = options?.presetId ??
            (!options?.initialVariant && !this.options.link ? this.options.configurationId : undefined);
        const initialVariant = options?.initialVariant;
        // When a presetId is given, replace the legacy initial-variant fetch with
        // the preset fetch — the preset's `variant` becomes the initial state.
        // Fired inside the same Promise.all so the extra round-trip costs nothing
        // beyond the slowest sibling fetch (typically the schema or variant
        // matrix, both heavier than a single preset record).
        const initialPromise = presetId
            ? this.fetcher.getCompositeProductPreset(this.options, presetId).then((preset) => ({
                ...preset.variant,
                presetValidation: preset.validationStatus
                    ? { validationStatus: preset.validationStatus, errors: preset.errors ?? [] }
                    : undefined,
                presetState: preset.state,
            }))
            : this.fetcher.getInitialCompositeProductVariant(this.options, initialVariant);
        [
            this.compositeProduct,
            this.initialCompositeProductVariant,
            this.bomDetails,
            this.UIConfig,
            this.compositeProductVariants,
        ] = await Promise.all([
            this.fetcher.getCompositeProductSchema(this.options),
            initialPromise,
            isCatalogOptions(this.options) ? this.fetcher.getBomDetails(this.options) : Promise.resolve([]),
            this.fetcher.getCompositeUiSpecification(this.options),
            // TODO: this additinally increases the time to load the product. Consider to load it later
            this.fetcher.getCompositeProductVariants(this.options),
        ]);
        this.presetValidation = this.initialCompositeProductVariant.presetValidation;
        this.#productData = await this.fetcher.getCompositeData(this.options);
        if (!this.initialCompositeProductVariant.components) {
            if (this.options.link) {
                throw new Error(INVALID_SAVED_CONFIGURATION);
            }
            else {
                throw new Error(INVALID_CONFIGURATION, {
                    cause: this.initialCompositeProductVariant,
                });
            }
        }
        this.parse();
        const awaitLogicEngineReady = this.prepareEngine();
        this.awaitLogicEngineReady = awaitLogicEngineReady;
        this.initModel();
        for (const componentApi of this.productComponentsApi) {
            const promise = awaitLogicEngineReady.then((logicEngine) => {
                const variants = this.variants[componentApi.getKey()];
                return {
                    logicEngine,
                    variants,
                };
            });
            componentApi.injectAwait(promise);
        }
        const slotApis = this.buildSlotApis();
        const compositeApi = new CompositeProductApi(this.options, this.fetcher, this.defaultValidationOptions, this.modelRef, this.compositeProduct, this.attributes, this.productComponentsApi, this.productComponentsApiByComponent, this.bomDetails, awaitLogicEngineReady.then((logicEngine) => ({
            logicEngine,
            compositeProductVariants: this.variants,
            rules: this.compositeProduct.rules,
        })), this.productData, this.itemOrder, this.compositeProductVariants, this.initialCompositeProductVariant.meta ?? {}, this.presetValidation, slotApis, this.initialCompositeProductVariant.configurationMigrated ?? false);
        compositeApi.setLogicOrderSnapshot([...this.logicOrder]);
        // Set up model change notifiers for all ProductApi instances
        for (const componentApi of this.productComponentsApi) {
            const productApi = componentApi.api;
            productApi.setModelChangeNotifier((previousModel, newModel) => {
                compositeApi.notifyModelChange(previousModel, newModel);
            });
        }
        // Bounds wiring: give each slot a way to snapshot the parent state (needed
        // for boundary look-ahead and the post-add auto-warm), then warm the catalog
        // once at load. We always warm (not only when slot items exist) so that
        // bbox-bound slot geometry — `getOrigin()` and bbox-bound boundaries — can
        // resolve the composite's own component bounding boxes before any item is
        // placed. `warmFor` is cached by productId, so the first addItem is free.
        const serializeParent = (version) => compositeApi.serialize(version);
        for (const slot of slotApis) {
            slot.setSerializeParent(serializeParent);
        }
        if (this.boundsCatalog) {
            await this.boundsCatalog.warmFor(this.compositeProduct.productId, compositeApi.serialize(0));
        }
        // ADR-0066 (gaps 2/3): a preset is a full snapshot, not just a variant
        // key. The `variant` seed above covers component selection; the stored
        // snapshot's custom overrides and slot children are replayed here — this
        // is what makes a preset-with-slots (including a composite child that
        // itself pre-places grandchildren, via the recursive placedApiFactory)
        // load correctly. Skipped when the caller applies its own runtime
        // `initialState` right after (SlotApi restore path) — that snapshot then
        // wins wholesale and children must not be placed twice.
        const presetState = this.initialCompositeProductVariant.presetState;
        if (presetState && !options?.skipPresetStateHydration) {
            const { custom, slots, meta } = presetState;
            const hasCustom = custom && Object.keys(custom).length > 0;
            const hasSlotItems = slots && Object.values(slots).some((items) => items.length > 0);
            const hasMeta = meta && Object.keys(meta).length > 0;
            if (hasCustom || hasSlotItems || hasMeta) {
                await compositeApi.applySerializedState({
                    version: presetState.version,
                    components: {},
                    custom: custom ?? {},
                    ...(meta ? { meta } : {}),
                    ...(slots ? { slots } : {}),
                });
            }
        }
        return compositeApi;
    }
    parse() {
        this.parseAttributes();
        this.parseProductComponentsApi();
        this.parseUIConfig();
    }
    parseAttributes() {
        const logicOrder = this.logicOrderOverride ?? this.compositeProduct.logicOrder;
        if (logicOrder) {
            this.parseLogicOrder(logicOrder);
        }
        else {
            this.logicOrder = getDefaultCompositeLogicOrder(this.compositeProduct.productType);
        }
        this.attributes = this.logicOrder.map((attribute) => {
            const attributeKey = attribute.type === 'attribute'
                ? attribute.attributeKey
                : attribute.type === 'availability'
                    ? VariantProperties.available
                    : VariantProperties.productId;
            const componentKey = attribute.componentKey;
            const attributeId = createAttributeId(componentKey, attributeKey);
            return {
                componentKey,
                attributeKey,
                attributeId,
            };
        });
    }
    parseProductComponentsApi() {
        this.productComponentsApi = [];
        this.productComponentsApiByComponent.clear();
        const visitedComponentKeys = new Set();
        const components = this.logicOrder.filter((it) => it.type === 'availability');
        for (const component of components) {
            const componentKey = component.componentKey;
            if (!visitedComponentKeys.has(componentKey)) {
                visitedComponentKeys.add(componentKey);
                // this.productComponentsApi.push(new ProductComponentApiBuilder(componentKey, this));
                const type = this.getProductType(componentKey);
                const id = this.initialCompositeProductVariant.components[componentKey]?.variant.variantId;
                const api = new BasicProductApi(id, componentKey, type.name, this.getConfigurableAttributes(componentKey), this.modelRef, componentKey, this.defaultValidationOptions);
                api.injectLogicEngine(this.awaitLogicEngineReady);
                const componentSchema = this.compositeProduct.productType.components?.find((it) => it.key === componentKey);
                if (!componentSchema) {
                    throw new Error(`Cannot find component schema for component key "${componentKey}"`);
                }
                const productComponentImpl = new CompositeProductComponentApi(api, this.productData.components[componentKey], componentSchema);
                this.productComponentsApiByComponent.set(componentKey, productComponentImpl);
                this.productComponentsApi.push(productComponentImpl);
            }
        }
    }
    createSchema() {
        // const attributes = this.createAttributes();
        this.builder.setSchema({
            attributes: this.attributes.map((attribute) => ({
                attributeKey: attribute.attributeKey,
                componentKey: attribute.componentKey,
            })),
        });
    }
    async prepareEngine() {
        this.variants = this.createProductVariants();
        this.createRules();
        this.createSchema();
        this.initModel();
        return this.builder.build();
    }
    createProductVariants() {
        const variants = ConfiguratorToDomainProductMapper.toCfgCompositeVariants(this.compositeProductVariants);
        for (const [componentKey, componentVariants] of Object.entries(variants)) {
            this.builder.addProductVariants(componentKey, componentVariants);
        }
        return variants;
    }
    getProductType(componentKey) {
        const productComponent = this.compositeProduct.productType.components.find((component) => component.key === componentKey);
        if (!productComponent) {
            throw new Error(`Cannot find product type. Invalid component key "${componentKey}"`);
        }
        return productComponent.type;
    }
    createRules() {
        this.attributesUsedInRules.clear();
        for (const rule of this.compositeProduct.rules) {
            const componentKeys = Object.keys(rule.deps);
            for (const [componentKey, attributes] of Object.entries(rule.deps)) {
                for (const attributeKey of attributes) {
                    const id = this.createComponentAttributeId(attributeKey, componentKey);
                    this.attributesUsedInRules.add(id);
                }
            }
            this.builder.addStringRule({
                name: rule.name,
                deps: { componentKeys: componentKeys },
                fn: rule.fn,
            });
        }
    }
    getConfigurableAttributes(componentKey) {
        // TODO: Optimize
        return this.attributes
            .filter((attribute) => attribute.componentKey === componentKey)
            .map((attribute) => ({
            id: attribute.attributeId,
            key: attribute.attributeKey,
        }));
    }
    createComponentAttributeId(attributeKey, componentKey) {
        return attributeKey + '_$$_' + componentKey;
    }
    initModel() {
        this.modelRef.model = ConfiguratorToDomainProductMapper.toCfgCompositeProduct(this.initialCompositeProductVariant, this.initialCompositeProductVariant?.custom);
    }
    getComponentApi(componentKey) {
        const api = this.productComponentsApiByComponent.get(componentKey);
        if (!api) {
            throw new Error(`Invalid component key "${componentKey}"`);
        }
        return api;
    }
    parseLogicOrder(logicOrder) {
        this.logicOrder = logicOrder;
        validateCompositeLogicOrder(this.logicOrder, this.compositeProduct.productType);
    }
    // SDK-owned bounds catalog, shared by this composite's slots and kept warm as
    // items are added. Created lazily in `buildSlotApis` (only when there are slots).
    boundsCatalog;
    buildSlotApis() {
        const slots = this.compositeProduct.slots ?? [];
        // Wrap the fetcher with the parent composite's scoping options (catalog/
        // version) so `SlotApi.loadAcceptedPresets()` doesn't need to know about
        // FetcherApi directly. Override `productId` per-call — that's the
        // accepted preset's product, not this composite's.
        const presetLoader = (placedProductId, placedPresetId) => this.fetcher.getCompositeProductPreset({ ...this.options, productId: placedProductId }, placedPresetId);
        // Symmetric loader for basicProduct accepted items: fetch the product
        // schema + variants in parallel for the placed productId and map the
        // domain DTOs to the public `IComponentProduct` / `IComponentProductVariant`
        // shapes consumed by `SlotApi.loadAcceptedBasicProducts()`. `model3dId`
        // and `meta` aren't carried on `IProduct`/`IProductVariant`, so we fill
        // defaults — callers that need rich media should still go through
        // `loadBasicProduct`.
        const basicProductLoader = async (placedProductId) => {
            const placedOptions = { ...this.options, productId: placedProductId };
            const [product, variants] = await Promise.all([
                this.fetcher.getProductSchema(placedOptions),
                this.fetcher.getProductVariants(placedOptions),
            ]);
            return {
                product: {
                    productId: product.productId,
                    productKey: product.productKey,
                    name: product.productName,
                    meta: {},
                },
                variants: variants.map((v) => ({
                    attributes: v.attributes,
                    meta: v.meta,
                    variantId: v.variantId,
                    productId: v.productId,
                    productKey: v.productKey,
                    model3dId: null,
                })),
            };
        };
        this.boundsCatalog = slots.length ? createFetcherBoundsCatalog(this.fetcher, this.options) : undefined;
        return slots.map((slot) => createSlotApi(slot, {
            placedApiFactory: this.placedCompositeApiFactory,
            placedBasicApiFactory: this.placedBasicApiFactory,
            presetLoader,
            basicProductLoader,
            // Accepted-basic variants (with `model3dId`) for this slot, from the
            // composite's `getCompositeProductVariants().slots`. Source of truth for
            // the slot's basic placements — supersedes the `model3dId: null` that
            // `basicProductLoader` fills.
            slotVariants: this.compositeProductVariants.slots?.[slot.key],
            boundsCatalog: this.boundsCatalog,
            parentProductId: this.compositeProduct.productId,
            // Parts (partKey → componentKey) so a `space: 'part'` slot resolves its
            // origin against the named part's own bbox.
            parts: this.compositeProduct.parts,
            // Live snapshot of the parent composite's state — read on demand so a
            // slot's conditional acceptedItems re-resolve as the tray (or any
            // component) changes. `modelRef` is the shared mutable ref updated by
            // the component apis, so reading `.model` at call time is always current.
            getCompositeState: () => this.modelRef.model,
        }));
    }
    parseUIConfig() {
        this.itemOrder = [];
        const root = this.UIConfig?.layout?.root;
        if (!root) {
            return;
        }
        const addItemToOrder = (item) => {
            const visible = Boolean(item.enabled && item.notAvailable !== true);
            if (item.kind === 'component') {
                this.itemOrder.push({
                    attributeKey: item.source.componentKey,
                    visible,
                    kind: 'component',
                    componentKey: item.source.componentKey,
                });
            }
            else {
                this.itemOrder.push({
                    attributeKey: item.source.attributeKey,
                    visible,
                    kind: item.kind,
                    componentKey: item.source.componentKey,
                });
            }
        };
        const traverse = (item) => {
            if (item.kind === 'component' || item.kind === 'attribute') {
                addItemToOrder(item);
            }
            if (item.kind === 'container') {
                item.children.forEach(traverse);
            }
        };
        traverse(root);
    }
}

class CatalogApiBuilder {
    catalogId;
    options;
    fetcher;
    defaultValidationOptions;
    viewOptions;
    constructor(catalogId, options, fetcher, defaultValidationOptions, viewOptions) {
        this.catalogId = catalogId;
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
        this.viewOptions = viewOptions;
    }
    buildOptions(productId) {
        const base = {
            productId,
            projectId: this.options.projectId,
            version: this.options.version,
            configurationId: this.options.configurationId,
            link: this.options.link,
        };
        if (this.catalogId != null) {
            return { ...base, catalogId: this.catalogId };
        }
        return base;
    }
    async loadBasicProduct(productId) {
        const apiBuilder = new BasicProductApiBuilder(this.buildOptions(productId), this.fetcher, this.defaultValidationOptions);
        const api = await apiBuilder.load();
        return api;
    }
    async loadCompositeProduct(productId, options) {
        // Factory used by SlotApi to instantiate per-placed-item composite APIs.
        // Recurses into loadCompositeProduct so nested slots inside a placed
        // composite get their own factory too. `presetId` is required for the
        // factory path — that's what SlotApi.addItem passes for compositePreset
        // placements (parallel preset fetch by `(productId, presetId)`).
        const placedCompositeApiFactory = (placedProductId, placedPresetId, factoryOpts) => this.loadCompositeProduct(placedProductId, {
            presetId: placedPresetId,
            skipPresetStateHydration: factoryOpts?.skipPresetStateHydration,
        });
        // Factory used by SlotApi to instantiate per-placed-item basic APIs. Each
        // placed basicVariant gets its own BasicProductApi (independent LogicEngine
        // and model) initialized at the placed variantId via BasicProductApiBuilder.
        // Symmetric to the composite factory; doesn't share state with the parent
        // composite.
        const placedBasicApiFactory = (placedProductId, placedVariantId, model3dByVariantId) => {
            const builder = new BasicProductApiBuilder(this.buildOptions(placedProductId), this.fetcher, this.defaultValidationOptions);
            return builder.load({ initialVariantId: placedVariantId, model3dByVariantId });
        };
        const apiBuilder = new CompositeProductApiBuilder(this.buildOptions(productId), this.fetcher, this.defaultValidationOptions, placedCompositeApiFactory, placedBasicApiFactory);
        const api = await apiBuilder.load(options);
        if (this.viewOptions?.enableViewModel3dKey) {
            api.enableViewModel3dKey();
        }
        return api;
    }
}

/** The implicit default cart used in `single` mode (and as the `cartKey` default). */
const DEFAULT_CART_KEY = '';

const EMPTY_STATE = { carts: {} };
function cloneState(state) {
    // Cart items hold plain serialized data (ISerializedComposite + primitives),
    // so a structural clone is safe and keeps callers from mutating stored state.
    return { carts: JSON.parse(JSON.stringify(state.carts)) };
}
/** In-memory strategy — cart lives until the page is reloaded. */
class InMemoryCartStorage {
    state = EMPTY_STATE;
    load() {
        return cloneState(this.state);
    }
    save(state) {
        this.state = cloneState(state);
    }
}
/**
 * `localStorage` strategy — survives reloads within the same browser.
 * Namespaced by `storageKey` so multiple carts (e.g. per catalog) don't collide.
 * Falls back to empty state when `localStorage` is unavailable or corrupt.
 */
class LocalStorageCartStorage {
    storageKey;
    constructor(storageKey) {
        this.storageKey = storageKey;
    }
    get store() {
        return typeof localStorage !== 'undefined' ? localStorage : undefined;
    }
    load() {
        const raw = this.store?.getItem(this.storageKey);
        if (!raw) {
            return cloneState(EMPTY_STATE);
        }
        try {
            const parsed = JSON.parse(raw);
            return parsed?.carts ? parsed : cloneState(EMPTY_STATE);
        }
        catch {
            return cloneState(EMPTY_STATE);
        }
    }
    save(state) {
        this.store?.setItem(this.storageKey, JSON.stringify(state));
    }
}
/**
 * Build a storage for the given persistence strategy. `localStorage` requires a
 * namespacing `storageKey`. `persistant` is not yet supported (throws).
 */
function createCartStorage(persistence, storageKey) {
    switch (persistence) {
        case 'inMemory':
            return new InMemoryCartStorage();
        case 'localStorage':
            if (!storageKey) {
                throw new Error("createCartStorage: 'localStorage' persistence requires a storageKey");
            }
            return new LocalStorageCartStorage(storageKey);
        case 'persistant':
            throw new Error("createCartStorage: 'persistant' (DB + SSO) persistence is not implemented yet (ADR-0074 Phase 2)");
        default: {
            const exhaustive = persistence;
            throw new Error(`createCartStorage: unknown persistence '${exhaustive}'`);
        }
    }
}

let idCounter = 0;
function generateId(prefix) {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : null;
    return uuid ?? `${prefix}-${(idCounter += 1)}`;
}
function isThenable(value) {
    return typeof value?.then === 'function';
}
class Cart {
    config;
    storage;
    state;
    constructor(config, storage) {
        this.config = config;
        this.storage = storage;
        const loaded = this.storage.load();
        if (isThenable(loaded)) {
            // The CartApi surface is synchronous; async storage (e.g. a future DB
            // strategy) needs a different, async construction path.
            throw new Error('Cart: asynchronous CartStorage is not supported by the synchronous CartApi');
        }
        this.state = loaded;
        if (this.config.mode === 'single') {
            this.ensureCart(DEFAULT_CART_KEY, []);
        }
    }
    getCartInfo() {
        const { mode, persistence } = this.config;
        return mode === 'single' ? { mode, persistence } : { mode, persistence, fields: this.config.fields };
    }
    getAcceptedCartProducts() {
        return [...this.config.acceptedProducts];
    }
    getAllCartKeys() {
        return Object.keys(this.state.carts);
    }
    createCart(fields) {
        if (this.config.mode !== 'multi') {
            throw new Error("Cart.createCart is only available in 'multi' mode");
        }
        const key = generateId('cart');
        this.ensureCart(key, fields);
        this.persist();
        return key;
    }
    addToCart(state, amount, cartKey = DEFAULT_CART_KEY) {
        this.assertAmount(amount);
        const record = this.resolveCart(cartKey);
        const item = { key: generateId('item'), state, amount };
        record.items.push(item);
        this.persist();
        return { ...item };
    }
    getCartItems(cartKey = DEFAULT_CART_KEY) {
        return this.resolveCart(cartKey).items.map((item) => ({ ...item }));
    }
    changeCartItemAmount(cartKey, itemKey, amount) {
        this.assertAmount(amount);
        const item = this.resolveCart(cartKey).items.find((it) => it.key === itemKey);
        if (!item) {
            throw new Error(`Cart: item '${itemKey}' not found in cart '${cartKey}'`);
        }
        item.amount = amount;
        this.persist();
    }
    removeCartItem(cartKey, itemKey) {
        const record = this.resolveCart(cartKey);
        const next = record.items.filter((it) => it.key !== itemKey);
        if (next.length === record.items.length) {
            throw new Error(`Cart: item '${itemKey}' not found in cart '${cartKey}'`);
        }
        record.items = next;
        this.persist();
    }
    /** In single mode always the default cart; in multi mode the cart must exist. */
    resolveCart(cartKey) {
        if (this.config.mode === 'single') {
            return this.ensureCart(DEFAULT_CART_KEY, []);
        }
        const record = this.state.carts[cartKey];
        if (!record) {
            throw new Error(`Cart: cart '${cartKey}' does not exist — create it with createCart() first`);
        }
        return record;
    }
    ensureCart(cartKey, fields) {
        const existing = this.state.carts[cartKey];
        if (existing) {
            return existing;
        }
        const record = { fields, items: [] };
        this.state.carts[cartKey] = record;
        return record;
    }
    assertAmount(amount) {
        if (!Number.isInteger(amount) || amount < 1) {
            throw new Error(`Cart: amount must be a positive integer, got ${amount}`);
        }
    }
    persist() {
        this.storage.save(this.state);
    }
}
/**
 * Build a {@link CartApi} from config. Picks a storage from `config.persistence`
 * unless one is supplied; `localStorage` requires `storageKey` for namespacing.
 */
function createCartApi(config, options) {
    const storage = options?.storage ?? createCartStorage(config.persistence, options?.storageKey);
    return new Cart(config, storage);
}

/**
 * Walk a composite's slots and build the neutral render DTO the viewer consumes
 * ({@link ISlotRenderSpec}). Keeps the SDK and the viewer decoupled — the viewer
 * never imports the SDK, and this never imports the viewer.
 *
 * - A placed **composite** item recurses, sourcing its own view3d via the placed
 *   api's `getView3dSpecification()` and its variant via `getVariant()` (the raw
 *   variant, with `_available`/`_model3dId` intact — the viewer dispatches on those).
 * - A placed **basic** item becomes a `model3d` ref (its `_model3dId`).
 * - Items whose placement strategy yields no transform (e.g. the `free`
 *   positioning stub, which returns `null`) are skipped. Both `stack` and
 *   `list` cells produce transforms and are rendered.
 */
async function buildSlotRenderSpecs(api) {
    const specs = [];
    for (const slot of api.getSlots()) {
        // Bag slots are non-visual collections — no space, no positioned items.
        // Skip them so the viewer never receives a positionless render spec.
        if (slot.getPlacement().kind === 'bag')
            continue;
        let layout;
        try {
            layout = slot.getItemLayout();
        }
        catch {
            // Slot built without bounds wiring — nothing to position.
            continue;
        }
        const items = [];
        for (const item of slot.getItems()) {
            const placement = layout.get(item.id);
            if (!placement)
                continue;
            if (item.composite && item.api) {
                const compApi = item.api;
                items.push({
                    id: item.id,
                    transform: placement.transform,
                    geometry: {
                        kind: 'composite',
                        composite: {
                            view3d: await compApi.getView3dSpecification(),
                            variant: compApi.getVariant(),
                            slots: await buildSlotRenderSpecs(compApi),
                        },
                    },
                });
            }
            else if (!item.composite && item.api) {
                const model3dId = readModel3dId(item.api);
                if (model3dId === undefined)
                    continue;
                items.push({ id: item.id, transform: placement.transform, geometry: { kind: 'model3d', model3dId } });
            }
        }
        specs.push({ key: slot.getKey(), space: slot.getSpace(), origin: slot.getOrigin(), items });
    }
    return specs;
}
function readModel3dId(api) {
    const raw = api.getVariantRaw()['_model3dId'];
    const value = raw ? getAttributeValue(raw) : undefined;
    return typeof value === 'number' ? value : undefined;
}

/**
 * Find a placed slot item by its id, searching `root`'s slots recursively
 * through placed composites (same traversal as `buildSlotRenderSpecs`). Ids
 * are globally unique, so the first match is the only one. Placed composites
 * without a wired api cannot be descended into and are skipped.
 */
function findSlotItemById(root, itemId) {
    return findIn(root, itemId, []);
}
function findIn(composite, itemId, prefix) {
    for (const slot of composite.getSlots()) {
        for (const item of slot.getItems()) {
            const path = [...prefix, { itemId: item.id, slotKey: slot.getKey() }];
            if (item.id === itemId) {
                return { item, api: item.api, slot, parentApi: composite, path };
            }
            if (item.composite && item.api) {
                const found = findIn(item.api, itemId, path);
                if (found) {
                    return found;
                }
            }
        }
    }
    return null;
}

/**
 * SDK implementation of the kernel's `LocalizationClient` port (ADR-0075
 * §clp-sdk): transport and caching only — translation semantics live in the
 * kernel, endpoint selection in the `Fetcher`.
 *
 * Caching semantics:
 * - LIVE reads (draft / no catalog) are never cached: CSV imports and
 *   custom-key edits change them mid-session.
 * - ACTIVE reads are deduplicated while in flight.
 * - Settled ACTIVE results are reusable only under a caller-supplied
 *   immutable `snapshotId` (obtained from a prior response) — the identity
 *   participates in the cache key, so a catalog re-release can never serve a
 *   prior snapshot's entry: a response whose snapshotId differs from the
 *   requested one is returned fresh and NOT stored under the stale key.
 */
class HttpLocalizationClient {
    fetcher;
    inFlight = new Map();
    settledBySnapshot = new Map();
    constructor(fetcher) {
        this.fetcher = fetcher;
    }
    getTranslations(request) {
        return this.fetcher.getProductTranslations(toFetcherParams(request), request.lang);
    }
    async getLayeredTranslations(request) {
        const options = {
            lang: request.lang,
            // Normalize once, up front: the same canonical form feeds the wire AND
            // every cache key below (decision 5).
            include: request.include ? normalizeFamilies(request.include) : undefined,
            layers: request.layers ? normalizeLayers(request.layers) : undefined,
        };
        const params = toFetcherParams(request);
        const live = request.draft === true || request.catalogId == null;
        const projectionKey = JSON.stringify([
            request.catalogId ?? null,
            request.projectId ?? null,
            request.productId,
            options.lang ?? null,
            options.include ?? null,
            options.layers ?? null,
        ]);
        if (live) {
            const dto = await this.fetcher.getProductTranslationsV2(params, options);
            return toLayered(dto);
        }
        if (request.snapshotId) {
            const hit = this.settledBySnapshot.get(`${request.snapshotId}|${projectionKey}`);
            if (hit)
                return hit;
        }
        const pending = this.inFlight.get(projectionKey);
        if (pending)
            return pending;
        const run = this.fetcher
            .getProductTranslationsV2(params, options)
            .then((dto) => {
            const result = toLayered(dto);
            // Store under the identity the platform actually served. When the
            // caller expected an older snapshot (re-release happened), the stale
            // key stays unpopulated by construction.
            if (result.snapshotId) {
                this.settledBySnapshot.set(`${result.snapshotId}|${projectionKey}`, result);
            }
            return result;
        })
            .finally(() => {
            this.inFlight.delete(projectionKey);
        });
        this.inFlight.set(projectionKey, run);
        return run;
    }
}
/** The documented construction path for consumers and `ClpSdk`. */
function createLocalizationClient(fetcher) {
    return new HttpLocalizationClient(fetcher);
}
function toFetcherParams(request) {
    return {
        productId: request.productId,
        projectId: request.projectId,
        catalogId: request.catalogId,
        version: request.draft ? 'draft' : 'active',
    };
}
function toLayered(dto) {
    const result = {
        language: dto.language,
        defaultLanguage: dto.defaultLanguage,
        families: [...dto.families],
        layers: dto.layers,
    };
    if (dto.snapshotId !== undefined)
        result.snapshotId = dto.snapshotId;
    return result;
}

/**
 * ClpSdk is the main class for the clp sdk.
 * It is used to build the catalog api builder.
 * This class should be used to initialize the sdk and build the api builders.
 *
 * Example:
 * ```ts
 * const sdk = new ClpSdk(new Fetcher('https://api.clp.com'));
 * const catalogApi = sdk.useCatalog(123).useApiOptions({ version: 'draft', projectId: 456 }).build();

 * const compositeApi = await catalogApi.loadCompositeProduct(12345);
 * ```
 */
class ClpSdk {
    // Session-scoped memoizing wrapper: dedupes identical read requests across the
    // composite-api builders, placed-item api builds, and the bounds catalog.
    fetcher;
    constructor(fetcher) {
        this.fetcher = createMemoizingFetcher(fetcher);
    }
    catalogId;
    options;
    defaultValidationOptions = {
        ignoreAllRules: false,
    };
    viewOptions = {
        enableViewModel3dKey: false,
    };
    localizationClient;
    /**
     * Lazy singleton `LocalizationClient` (ADR-0075) over this SDK instance's
     * fetcher. The memoizing wrapper is translation-aware: live reads pass
     * through it uncached, so the client's own caching rules stay authoritative.
     */
    getLocalizationClient() {
        this.localizationClient ??= createLocalizationClient(this.fetcher);
        return this.localizationClient;
    }
    /**
     * Set the catalog id to use. If undefined, catalog will be ignored, and product level api will be used.
     * @param catalogId
     */
    useCatalog(catalogId) {
        this.catalogId = catalogId;
        return this;
    }
    /**
     * Set the api options to use. projectId is required for SDK features like createLink.
     * @param options
     * @returns ClpSdk
     */
    useApiOptions(options) {
        this.options = options;
        return this;
    }
    /**
     * Set the default validation options to use.
     * @param defaultValidationOptions
     * @returns ClpSdk
     * */
    useDefaultValidationOptions(defaultValidationOptions) {
        this.defaultValidationOptions = defaultValidationOptions;
        return this;
    }
    /**
     * Set the view-layer options to use (ADR-0070).
     * @param viewOptions
     * @returns ClpSdk
     */
    useViewOptions(viewOptions) {
        this.viewOptions = viewOptions;
        return this;
    }
    /**
     * Build the catalog api builder.
     * @returns CatalogApiBuilder
     */
    build() {
        if (!this.options) {
            throw new Error('Options are required. Use useApiOptions() to set the options.');
        }
        return new CatalogApiBuilder(this.catalogId, this.options, this.fetcher, this.defaultValidationOptions, this.viewOptions);
    }
}

/**
 * HqrImageResolver resolves the appropriate high-quality render image URL
 * based on the current product configuration.
 */
class HqrImageResolver {
    config;
    constructor(config) {
        this.config = config;
    }
    /**
     * Find matching HQR image for current variant configuration.
     * Returns first matching entry's image, or default image, or null.
     *
     * @param variant The current composite variant configuration
     * @returns The resolved image URL or null if no match found
     */
    resolve(variant) {
        // Try to find entry with all conditions matching
        for (const entry of this.config) {
            if (entry.combination && entry.combination.length > 0) {
                if (this.allConditionsMatch(entry.combination, variant)) {
                    return entry.image;
                }
            }
        }
        // Return default image (entry without combination)
        const defaultEntry = this.config.find((e) => !e.combination || e.combination.length === 0);
        return defaultEntry?.image || null;
    }
    allConditionsMatch(conditions, variant) {
        return conditions.every((condition) => this.conditionMatches(condition, variant));
    }
    conditionMatches(condition, variant) {
        const componentVariant = variant[condition.componentKey];
        if (!componentVariant)
            return false;
        if (condition.type === 'attribute') {
            // Check if attribute value matches
            const attrValue = componentVariant[condition.attributeKey];
            if (!attrValue)
                return false;
            // Handle both direct value and value wrapper (ICfgAttributeValue)
            const actualValue = typeof attrValue === 'object' && 'value' in attrValue ? attrValue.value : attrValue;
            return actualValue === condition.value;
        }
        if (condition.type === 'product') {
            // Check if product ID matches
            const productIdAttr = componentVariant['_productId'];
            if (!productIdAttr)
                return false;
            const productId = typeof productIdAttr === 'object' && 'value' in productIdAttr ? productIdAttr.value : productIdAttr;
            return String(productId) === String(condition.productId);
        }
        return false;
    }
    /**
     * Check if the HQR configuration has any entries
     */
    isEnabled() {
        return this.config.length > 0;
    }
}

export { BagSlotApi, BasicProductApi, Cart, CatalogApiBuilder, ClpSdk, CompositeProductApi, CompositeProductApiBuilder, CompositeProductComponentApi, DEFAULT_CART_KEY, Fetcher, FreeSlotApi, HqrImageResolver, HttpLocalizationClient, InMemoryCartStorage, InvalidConfiguration, JsonFetcher, ListSlotApi, LocalStorageCartStorage, PriceTypeNotFoundError, STATIC_MODEL_NODE_KIND, SlotApi, StackSlotApi, assembleCompositeData, assembleCompositeVariants, assembleInitialVariant, buildSlotRenderSpecs, computeBounds, createCartApi, createCartStorage, createLocalFetcher, createLocalizationClient, createSlotApi, findSlotItemById, getBoundsProvider, getPlacementStrategy, isBagSlotApi, isFreeSlotApi, isListSlotApi, isStackSlotApi, layoutSlotItems, registerBoundsProvider, registerPlacementStrategy, validateSlotBoundaries };
//# sourceMappingURL=index.mjs.map
